/**
 * 用途：TikTok 广告创建向导（分步骤），由 CreateAdWizard 根据平台参数调用
 * 路由：无（子组件，通过 /campaigns/tiktok/create 间接访问）
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAppStore } from '../store/useAppStore';
import { useToastStore } from '../store/useToastStore';
import {
  accountsApi,
  type CreateAdFullRequest,
  type DraftPayloadRequest,
  type TikTokCreateAdFullRequest,
  type TikTokIdentityOption,
  type TikTokPixel,
  type TikTokAudience,
  type TikTokCatalog,
  type TikTokCatalogSet,
  type TikTokVideoItem,
  type TikTokSparkPost,
  type TikTokTargetingSearchItem,
  type ValidateAdFullRequest,
  type TikTokEditDetailResponse,
  type TikTokEditCampaignPayload,
  type TikTokEditAdSetPayload,
  type TikTokEditAdPayload,
} from '../api/accounts';
import { adTagsApi, type TagDefinition } from '../api/adTags';
import { confirmDialog } from '../store/useConfirmStore';
import {
  DateTimePicker,
  FormGroup,
  Hint,
  HorizontalStepBar,
  Label,
  SectionCard,
  UploadZone,
  WizardErrorBanner,
  type WizardStep,
} from './adWizardShared';
import {
  getCampaignSubtypeConfig,
  getDestinationConfig,
  getObjectiveConfig,
  getObjectiveSubtypeOptions,
  TIKTOK_OBJECTIVE_CONFIG,
  TIKTOK_OBJECTIVE_GROUPS,
  type Option,
} from './tiktokCampaignRules';
import { MultiSelect } from '../components/MultiSelect';

type AccountOption = { id: string; account_id: string; account_name?: string };
type EditMode = {
  entity: 'campaign' | 'adset' | 'ad';
  id: number;
};

type CreateUnderMode = {
  entity: 'campaign' | 'adset';
  parentId: number;
};

type Props = {
  brandId: string;
  siteId: string;
  onCancel: () => void;
  onCreated: () => void;
  onStepChange?: (step: WizardStep) => void;
  onObjectiveChange?: (objective: string) => void;
  onAdFormatChange?: (adFormat: string) => void;
  editMode?: EditMode;
  createUnder?: CreateUnderMode;
  initialAccountID?: string;
};

type TikTokDraftPayload = Record<string, unknown>;

// COUNTRIES is now loaded dynamically from TikTok API — see regionOptions state

const AGES: Option[] = [
  { value: 'AGE_UNLIMITED', label: '不限' },
  { value: 'AGE_13_17', label: '13-17 岁' },
  { value: 'AGE_18_24', label: '18-24 岁' },
  { value: 'AGE_25_34', label: '25-34 岁' },
  { value: 'AGE_35_44', label: '35-44 岁' },
  { value: 'AGE_45_54', label: '45-54 岁' },
  { value: 'AGE_55_100', label: '55 岁以上' },
];

const SALES_AGES: Option[] = [
  { value: 'AGE_UNLIMITED', label: '不限' },
  { value: 'AGE_18_24', label: '18-24 岁' },
  { value: 'AGE_25_34', label: '25-34 岁' },
  { value: 'AGE_35_44', label: '35-44 岁' },
  { value: 'AGE_45_54', label: '45-54 岁' },
  { value: 'AGE_55_100', label: '55 岁以上' },
];
const SALES_AGE_VALUES = SALES_AGES.filter((a) => a.value !== 'AGE_UNLIMITED').map((a) => a.value);

const OPTIMIZATION_EVENT_LABELS: Record<string, string> = {
  COMPLETE_PAYMENT: '完成支付',
  INITIATE_CHECKOUT: '发起结账',
  ADD_TO_CART: '加入购物车',
  VIEW_CONTENT: '浏览内容',
  LANDING_PAGE_VIEW: '落地页浏览',
  ON_WEB_ORDER: '网页下单',
  ON_WEB_CART: '网页加购',
  ON_WEB_DETAIL: '网页浏览详情',
  ON_WEB_REGISTER: '网页注册',
  SHOPPING: '购物',
  INITIATE_ORDER: '发起订单',
  TRAFFIC_LANDING_PAGE_VIEW: '落地页浏览',
  PAGE_VIEW: '页面浏览',
  SUBMIT_FORM: '提交表单',
  FORM: '表单提交',
  CLICK: '点击',
  CLICK_LANDING_PAGE: '落地页点击',
  ENGAGED_VIEW: '视频播放 (6s)',
  ENGAGED_VIEW_FIFTEEN: '视频播放 (15s)',
  REACH: '覆盖',
  SHOW: '展示',
};

const PIXEL_EVENTS: Option[] = [
  { value: 'LANDING_PAGE_VIEW', label: '落地页浏览' },
  { value: 'COMPLETE_PAYMENT', label: '完成支付' },
  { value: 'ADD_TO_CART', label: '加入购物车' },
  { value: 'FORM', label: '表单提交' },
  { value: 'PAGE_VIEW', label: '页面浏览' },
];

const LEAD_CONVERT_EVENT_OPTIONS: Option[] = [
  { value: 'ON_WEB_REGISTER', label: '完成注册' },
  { value: 'ON_WEB_DETAIL', label: '查看内容' },
];

const SALES_CONVERT_EVENT_OPTIONS: Option[] = [
  { value: 'ON_WEB_ORDER', label: '下单' },
  { value: 'SHOPPING', label: '付费' },
  { value: 'ON_WEB_CART', label: '加入购物车' },
  { value: 'INITIATE_ORDER', label: '发起结账' },
  { value: 'ON_WEB_DETAIL', label: '查看内容' },
];

const SALES_VALUE_EVENT_OPTIONS: Option[] = [
  { value: 'SHOPPING', label: '付费' },
];


const CTAS: Option[] = [
  { value: 'LEARN_MORE', label: '了解更多' },
  { value: 'SHOP_NOW', label: '立即购买' },
  { value: 'SIGN_UP', label: '立即注册' },
  { value: 'CONTACT_US', label: '联系我们' },
  { value: 'SUBSCRIBE', label: '订阅' },
  { value: 'DOWNLOAD', label: '下载' },
  { value: 'GET_QUOTE', label: '获取报价' },
  { value: 'APPLY_NOW', label: '立即申请' },
  { value: 'BOOK_NOW', label: '立即预订' },
  { value: 'ORDER_NOW', label: '立即下单' },
];

const CTA_MODES = [
  { value: 'DYNAMIC', label: '动态优选', hint: '自动向不同的用户显示不同的行动号召，以提高转化效率。' },
  { value: 'STANDARD', label: '标准', hint: '' },
] as const;

const LANGUAGE_OPTIONS: Option[] = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
];

const MIN_AGE_OPTIONS: Option[] = [
  { value: '', label: '不限' },
  { value: '18', label: '18+' },
  { value: '25', label: '25+' },
];

const REACH_TARGETED_FREQUENCY_OPTIONS = [
  { value: '7:2', label: '每 7 天播放广告 2 次' },
  { value: '7:3', label: '每 7 天播放广告 3 次' },
  { value: '7:4', label: '每 7 天播放广告 4 次' },
];

const REACH_FREQUENCY_CAP_PRESETS = [
  { value: '7:3', label: '每 7 天展示广告不超过 3 次' },
  { value: '1:1', label: '每天的广告展示数不超过 1 次' },
];

const VIDEO_VIEW_GOAL_OPTIONS = [
  {
    value: 'ENGAGED_VIEW',
    label: '观看 6 秒次数（专注观看）',
    hint: '以观看 6 秒次数或广告互动为目标进行优化（以先到者为准）。',
  },
  {
    value: 'ENGAGED_VIEW_FIFTEEN',
    label: '观看 15 秒次数（专注观看）',
    hint: '以观看 15 秒次数或广告互动为目标进行优化（以先到者为准）。',
  },
] as const;


const VIDEO_VIEW_BENEFITS = [
  '尽可能增加视频广告的播放次数。',
  '通过向更积极的用户展示广告来提升受众意向。',
];

const errStyle: React.CSSProperties = { color: 'var(--red)', fontSize: 12, marginTop: 4 };

function isPositive(v: string) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
}

function normalizeMoneyInput(value: string) {
  const cleaned = value.replace(/[^\d.]/g, '');
  if (!cleaned) return '';
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  const integerPart = cleaned.slice(0, firstDot);
  const decimalPart = cleaned.slice(firstDot + 1).replace(/\./g, '').slice(0, 2);
  if (cleaned.endsWith('.') && decimalPart.length === 0) {
    return `${integerPart}.`;
  }
  return `${integerPart}.${decimalPart}`;
}

function parseTikTokCreativeIDs(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/[\s,，;\n\r\t]+/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
}

function parseJSONStringList(text?: string): string[] {
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? dedupeStrings(parsed.map((item) => String(item ?? ''))) : [];
  } catch {
    return [];
  }
}

function isHttpUrl(v: string) {
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function dedupeStrings(values: string[]) {
  return Array.from(new Set(values.map((v) => v.trim()).filter(Boolean)));
}

function formatDraftTimestamp(input?: string | Date | null) {
  if (!input) return '--';
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '--';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function csvList(v: string) {
  return dedupeStrings(v.split(','));
}

function resolveTikTokBudgetStrategy(
  budgetStrategy: string | undefined,
  budgetMode: string | undefined,
  objectiveType: string | undefined,
  budget?: number,
) {
  const normalized = (budgetStrategy || '').trim();
  if (normalized) return normalized;
  const mode = (budgetMode || '').trim();
  const objective = (objectiveType || '').trim();
  const amount = Number(budget || 0);
  if ((objective === 'PRODUCT_SALES' || objective === 'WEB_CONVERSIONS') && amount <= 0) {
    return 'ADGROUP_BUDGET';
  }
  if (mode === 'BUDGET_MODE_INFINITE') {
    return objective === 'PRODUCT_SALES' || objective === 'WEB_CONVERSIONS' ? 'ADGROUP_BUDGET' : 'NONE';
  }
  return 'CAMPAIGN_BUDGET';
}

function resolveTikTokBudgetStrategyFromDetail(
  campaign: { budget_strategy?: string; budget_mode?: string; objective_type?: string; budget?: number } | undefined,
  adset?: { budget_mode?: string; budget?: number } | undefined,
) {
  const strategy = resolveTikTokBudgetStrategy(
    campaign?.budget_strategy,
    campaign?.budget_mode,
    campaign?.objective_type,
    campaign?.budget,
  );
  const objective = (campaign?.objective_type || '').trim();
  if ((objective === 'PRODUCT_SALES' || objective === 'WEB_CONVERSIONS') && !campaign?.budget_strategy) {
    const adsetMode = (adset?.budget_mode || '').trim();
    if (adsetMode === 'BUDGET_MODE_DAY' || adsetMode === 'BUDGET_MODE_TOTAL' || adsetMode === 'BUDGET_MODE_INFINITE') {
      return 'ADGROUP_BUDGET';
    }
  }
  return strategy;
}

function resolveTikTokAdGroupBudgetModeFromDetail(
  campaign: { budget_strategy?: string; budget_mode?: string; objective_type?: string; budget?: number } | undefined,
  adset?: { budget_mode?: string; budget?: number } | undefined,
) {
  const mode = (adset?.budget_mode || '').trim();
  const budgetValue = Number(adset?.budget || 0);
  const strategy = resolveTikTokBudgetStrategyFromDetail(campaign, adset);
  if (strategy === 'ADGROUP_BUDGET' && budgetValue <= 0 && (mode === '' || mode === 'BUDGET_MODE_DAY')) {
    return 'BUDGET_MODE_INFINITE';
  }
  if (mode) return mode;
  if (strategy === 'ADGROUP_BUDGET') {
    return 'BUDGET_MODE_INFINITE';
  }
  return 'BUDGET_MODE_DAY';
}

function Chips({
  options,
  selected,
  onChange,
}: {
  options: Option[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((x) => x !== value) : [...selected, value]);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((o) => {
        const active = selected.includes(o.value);
        return (
          <label
            key={o.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              padding: '6px 10px',
              borderRadius: 8,
              border: `1px solid ${active ? 'rgba(18,16,184,.45)' : 'var(--border)'}`,
              background: active ? 'var(--primary-bg)' : 'var(--surface2)',
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={active}
              onChange={() => toggle(o.value)}
              style={{ accentColor: 'var(--primary)' }}
            />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}

function SearchableMultiSelect({
  options,
  selected,
  onChange,
  placeholder = '搜索...',
}: {
  options: Option[];
  selected: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));
  const selectedLabels = selected.map((v) => options.find((o) => o.value === v)?.label || v);

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((x) => x !== value) : [...selected, value]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 8px',
          border: '1px solid var(--border)', borderRadius: 6, minHeight: 38, cursor: 'text',
          background: 'var(--surface2)',
        }}
      >
        {selectedLabels.map((label, i) => (
          <span key={selected[i]} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'var(--primary-bg)', border: '1px solid rgba(18,16,184,.3)',
            borderRadius: 4, padding: '2px 6px', fontSize: 12,
          }}>
            {label}
            <span style={{ cursor: 'pointer', fontWeight: 700 }} onClick={(e) => { e.stopPropagation(); toggle(selected[i]); }}>&times;</span>
          </span>
        ))}
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length ? '' : placeholder}
          style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, minWidth: 80, fontSize: 13 }}
        />
      </div>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, marginTop: 4,
          maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)',
          borderRadius: 6, background: 'var(--surface)', boxShadow: '0 4px 12px rgba(0,0,0,.12)',
        }}>
          {filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 13, color: 'var(--muted)' }}>无匹配结果</div>}
          {filtered.map((o) => {
            const active = selected.includes(o.value);
            return (
              <div
                key={o.value}
                onClick={() => toggle(o.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                  cursor: 'pointer', fontSize: 13,
                  background: active ? 'var(--primary-bg)' : undefined,
                }}
                onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLDivElement).style.background = 'var(--surface2)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = active ? 'var(--primary-bg)' : ''; }}
              >
                <input type="checkbox" checked={active} readOnly style={{ accentColor: 'var(--primary)' }} />
                {o.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Cards({
  name,
  value,
  onChange,
  options,
  disabled,
}: {
  name: string;
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <label
            key={o.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: disabled ? 'not-allowed' : 'pointer',
              padding: '10px 12px',
              borderRadius: 8,
              border: `1px solid ${active ? 'rgba(18,16,184,.45)' : 'var(--border)'}`,
              background: active ? 'var(--primary-bg)' : 'var(--surface2)',
              fontSize: 13,
              fontWeight: 600,
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <input
              type="radio"
              name={name}
              checked={active}
              readOnly={disabled}
              onChange={() => !disabled && onChange(o.value)}
              style={{ accentColor: 'var(--primary)', pointerEvents: disabled ? 'none' : undefined }}
            />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}

function TikTokMultiSelect({
  options,
  selected,
  onChange,
  placeholder = '请选择',
  searchable = false,
}: {
  options: Option[];
  selected: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
}) {
  return (
    <MultiSelect
      options={options}
      value={selected}
      onChange={onChange}
      placeholder={placeholder}
      searchable={searchable}
      clearable
      tagDisplay
    />
  );
}

function TikTokAttributionWindowPicker({
  clickValue,
  viewValue,
  onClickChange,
  onViewChange,
  disabled = false,
}: {
  clickValue: string;
  viewValue: string;
  onClickChange: (v: string) => void;
  onViewChange: (v: string) => void;
  disabled?: boolean;
}) {
  const isMobile = useIsMobile();
  const clickOptions: Option[] = [
    { value: 'ONE_DAY', label: '1 天（点击）' },
    { value: 'SEVEN_DAYS', label: '7 天（点击）' },
    { value: 'FOURTEEN_DAYS', label: '14 天（点击）' },
    { value: 'TWENTY_EIGHT_DAYS', label: '28 天（点击）' },
  ];
  const viewOptions: Option[] = [
    { value: 'UNSET', label: '关' },
    { value: 'ONE_DAY', label: '1 天（展示）' },
    { value: 'SEVEN_DAYS', label: '7 天（展示）' },
  ];

  const summary = `"${clickOptions.find((o) => o.value === clickValue)?.label ?? clickValue}" 或 "${
    viewOptions.find((o) => o.value === viewValue)?.label ?? viewValue
  }"`;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
          gap: 20,
          padding: 16,
          border: '1px solid var(--border)',
          borderRadius: 12,
          background: 'var(--surface)',
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>点击归因窗口期</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {clickOptions.map((item) => (
              <label key={item.value} className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="radio"
                  checked={clickValue === item.value}
                  onChange={() => onClickChange(item.value)}
                  disabled={disabled}
                  style={{ accentColor: 'var(--primary)' }}
                />
                {item.label}
              </label>
            ))}
          </div>
        </div>

        <div style={{ alignSelf: 'center', color: 'var(--text3)', fontSize: 14 }}>或</div>

        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>展示归因窗口期</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {viewOptions.map((item) => (
              <label key={item.value} className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="radio"
                  checked={viewValue === item.value}
                  onChange={() => onViewChange(item.value)}
                  disabled={disabled}
                  style={{ accentColor: 'var(--primary)' }}
                />
                {item.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="form-input" style={{ color: 'var(--text2)', background: 'var(--surface2)' }}>
        {summary}
      </div>
    </div>
  );
}

const TIKTOK_DEFAULT_COUNTRY = '6252001';

function TikTokOptionalAttributionSettings({
  enabled,
  onToggle,
  clickValue,
  viewValue,
  onClickChange,
  onViewChange,
  statisticType,
  onStatisticTypeChange,
  disabled = false,
}: {
  enabled: boolean;
  onToggle: (v: boolean) => void;
  clickValue: string;
  viewValue: string;
  onClickChange: (v: string) => void;
  onViewChange: (v: string) => void;
  statisticType: string;
  onStatisticTypeChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          padding: 16,
          borderRadius: 10,
          border: '1px solid var(--border)',
          background: 'var(--surface2)',
          display: 'grid',
          gap: 14,
        }}
      >
        <div>
          <Label>归因窗口期</Label>
          <MultiSelect
            single
            options={[{ value: 'ENABLED', label: '设置归因窗口期' }]}
            value={enabled ? ['ENABLED'] : []}
            onChange={(vals) => onToggle(vals[0] === 'ENABLED')}
            placeholder="-"
            disabled={disabled}
          />
        </div>

        {enabled && (
          <>
            <TikTokAttributionWindowPicker
              clickValue={clickValue}
              viewValue={viewValue}
              onClickChange={onClickChange}
              onViewChange={onViewChange}
              disabled={disabled}
            />

            <div>
              <Label>事件统计方式</Label>
              <Cards
                name="tt-optional-stat"
                value={statisticType}
                onChange={onStatisticTypeChange}
                disabled={disabled}
                options={[
                  { value: 'EVERYTIME', label: '每一次' },
                  { value: 'NONE', label: '仅一次' },
                ]}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TikTokInterestBehaviorPicker({
  items,
  lookupItems,
  loading,
  selected,
  onChange,
  onSearchChange,
}: {
  items: TikTokTargetingSearchItem[];
  lookupItems: TikTokTargetingSearchItem[];
  loading: boolean;
  selected: string[];
  onChange: (v: string[]) => void;
  onSearchChange: (v: string) => void;
}) {
  const [search, setSearch] = useState('');

  const targetFieldLabel = (value?: string) => {
    switch (value) {
      case 'interest_category_ids':
        return '兴趣';
      case 'video_interaction_ids':
        return '视频互动';
      case 'creator_interaction_ids':
        return '创作者互动';
      case 'hashtag_interaction_ids':
        return '话题互动';
      default:
        return '搜索结果';
    }
  };

  useEffect(() => {
    onSearchChange(search);
  }, [onSearchChange, search]);

  const normalized = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        parentID: item.parent_id?.trim() ?? '',
        level: Number(item.level ?? 0),
        actionScene: item.action_scene?.trim() ?? '',
        category: item.category?.trim() ?? 'INTEREST',
      })),
    [items],
  );

  const resultItems = useMemo(
    () => [...normalized].sort((a, b) => a.name.localeCompare(b.name, 'zh-CN')),
    [normalized],
  );
  const groupedResultItems = useMemo(() => {
    const order = [
      'interest_category_ids',
      'video_interaction_ids',
      'creator_interaction_ids',
      'hashtag_interaction_ids',
    ];
    const groups = new Map<string, typeof resultItems>();
    for (const item of resultItems) {
      const key = item.target_field || 'targeting/search';
      const current = groups.get(key) ?? [];
      current.push(item);
      groups.set(key, current);
    }
    return order
      .map((key) => ({
        key,
        label: targetFieldLabel(key),
        items: groups.get(key) ?? [],
      }))
      .filter((group) => group.items.length > 0);
  }, [resultItems]);
  const [activeGroupKey, setActiveGroupKey] = useState<string>('');
  useEffect(() => {
    if (groupedResultItems.length === 0) {
      setActiveGroupKey('');
      return;
    }
    setActiveGroupKey((current) =>
      groupedResultItems.some((group) => group.key === current) ? current : groupedResultItems[0]?.key ?? '',
    );
  }, [groupedResultItems]);
  const activeGroup = useMemo(
    () => groupedResultItems.find((group) => group.key === activeGroupKey) ?? groupedResultItems[0] ?? null,
    [activeGroupKey, groupedResultItems],
  );

  const selectedItems = useMemo(() => {
    const map = new Map(lookupItems.map((item) => [item.id, item.name]));
    return selected.map((id) => ({ id, label: map.get(id) ?? id }));
  }, [lookupItems, selected]);

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((x) => x !== value) : [...selected, value]);

  const summary =
    selectedItems.length === 0
      ? '请选择兴趣和行为'
      : selectedItems.length <= 3
        ? selectedItems.map((item) => item.label).join('、')
        : `已选择 ${selectedItems.length} 项`;

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 12,
          background: 'var(--surface)',
          boxShadow: '0 12px 30px rgba(0,0,0,.06)',
          padding: 12,
        }}
      >
        <div style={{ marginBottom: 10 }}>
          <input
            className="form-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索兴趣、视频互动、创作者互动或话题互动"
          />
        </div>

        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 10,
            height: 320,
            background: 'var(--surface2)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              height: '100%',
              display: 'grid',
              gridTemplateColumns: '180px minmax(0, 1fr)',
            }}
          >
            {loading ? (
              <div style={{ gridColumn: '1 / span 2', padding: 16, color: 'var(--text3)', fontSize: 13 }}>搜索中...</div>
            ) : groupedResultItems.length > 0 && activeGroup ? (
              <>
                <div
                  style={{
                    borderRight: '1px solid var(--border)',
                    background: 'var(--surface2)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    minHeight: 0,
                  }}
                >
                  {groupedResultItems.map((group) => {
                    const active = group.key === activeGroup.key;
                    return (
                      <button
                        key={group.key}
                        type="button"
                        onClick={() => setActiveGroupKey(group.key)}
                        style={{
                          width: '100%',
                          border: 'none',
                          borderBottom: '1px solid var(--border)',
                          background: active ? 'var(--surface)' : 'transparent',
                          color: active ? 'var(--primary)' : 'var(--text2)',
                          padding: '12px 14px',
                          fontSize: 13,
                          fontWeight: active ? 700 : 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'background .18s ease, color .18s ease',
                        }}
                      >
                        <span>{group.label}</span>
                        <span style={{ color: 'var(--text3)', fontSize: 12 }}>{group.items.length}</span>
                      </button>
                    );
                  })}
                </div>
                <div key={activeGroup.key} style={{ overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
                  <div
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      padding: '10px 14px',
                      background: 'var(--surface)',
                      borderBottom: '1px solid var(--border)',
                      fontSize: 12,
                      fontWeight: 700,
                      color: 'var(--text2)',
                    }}
                  >
                    {activeGroup.label}
                  </div>
                  {activeGroup.items.map((item) => (
                    <label
                      key={item.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '20px minmax(0, 1fr)',
                        gap: 10,
                        padding: '12px 14px',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: selected.includes(item.id) ? 'rgba(20,184,166,.08)' : 'transparent',
                        fontSize: 14,
                        transition: 'background .18s ease',
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(item.id)}
                        onChange={() => toggle(item.id)}
                        style={{ accentColor: 'var(--primary)' }}
                      />
                      <div style={{ display: 'grid', gap: 4 }}>
                        <span>{item.name}</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: 'rgba(20,184,166,.12)',
                              color: 'var(--primary)',
                              fontSize: 12,
                            }}
                          >
                            {activeGroup.label}
                          </span>
                          <span style={{ color: 'var(--text3)', fontSize: 12 }}>
                            {item.target_field || 'targeting/search'}
                          </span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ gridColumn: '1 / span 2', padding: 16, color: 'var(--text3)', fontSize: 13 }}>
                {search.trim() ? '没有匹配项' : '暂无默认推荐的兴趣与行为项'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="form-input"
        style={{
          minHeight: 42,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          paddingTop: 8,
          paddingBottom: 8,
        }}
      >
        {selectedItems.length === 0 ? (
          <span style={{ color: 'var(--text3)' }}>{summary}</span>
        ) : (
          selectedItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => toggle(item.id)}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 999,
                padding: '4px 10px',
                background: 'var(--surface2)',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              {item.label} ×
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function TikTokSparkPostPickerModal({
  open,
  posts,
  sourceMode,
  multiSelect,
  identities,
  selectedIdentityID,
  loadingPosts,
  loadingLibrary,
  loadingMorePosts,
  hasMorePosts,
  selectedItemIDs,
  selectedIdentityLabel,
  search,
  errorMessage,
  authCode,
  onSearchChange,
  onAuthCodeChange,
  onClose,
  onApplySelection,
  onIdentityChange,
  onLoadMore,
  onApplyAuthCode,
  }: {
    open: boolean;
    posts: TikTokSparkPost[];
    sourceMode: 'TIKTOK_POST' | 'LIBRARY' | 'CATALOG_CREATIVE';
    multiSelect: boolean;
    identities: TikTokIdentityOption[];
    selectedIdentityID: string;
  loadingPosts: boolean;
  loadingLibrary?: boolean;
  loadingMorePosts: boolean;
  hasMorePosts: boolean;
  selectedItemIDs: string[];
  selectedIdentityLabel: string;
  search: string;
  errorMessage: string;
  authCode: string;
  onSearchChange: (value: string) => void;
  onAuthCodeChange: (v: string) => void;
  onClose: () => void;
  onApplySelection: (items: TikTokSparkPost[]) => void;
  onIdentityChange: (identityID: string) => void;
  onLoadMore: () => void;
  onApplyAuthCode: () => void;
}) {
  const isMobile = useIsMobile();
  const listRef = useRef<HTMLDivElement>(null);
  const [playingPreviewID, setPlayingPreviewID] = useState('');
  const [pendingSelectedIDs, setPendingSelectedIDs] = useState<string[]>([]);
  const formatDuration = (value?: number) => {
    if (!value || value <= 0) return '';
    const total = Math.max(0, Math.round(value));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };
  const formatResolution = (width?: number, height?: number) => {
    if (!width || !height) return '';
    return `${width}x${height}`;
  };

  const normalizedPosts = useMemo(
    () =>
      posts.map((post) => ({
        ...post,
        accountLabel: post.display_name?.trim() || selectedIdentityLabel || '已授权 TikTok 账号',
        contentLabel: post.caption?.trim() || `帖子 ${post.item_id}`,
        durationLabel: formatDuration(post.duration),
        resolutionLabel: formatResolution(post.width, post.height),
        sourceLabel:
          post.source === 'creative_library'
            ? '创意素材库'
            : post.source === 'catalog_creative'
              ? '商品库创意素材'
              : 'TikTok 帖子',
      })),
    [posts, selectedIdentityLabel],
  );

  const filteredPosts = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return normalizedPosts.filter((post) => {
      const matchesSource =
        sourceMode === 'TIKTOK_POST'
          ? post.source !== 'creative_library' && post.source !== 'catalog_creative'
          : sourceMode === 'LIBRARY'
            ? post.source === 'creative_library'
            : post.source === 'catalog_creative';
      const matchesKeyword =
        !keyword
        || post.contentLabel.toLowerCase().includes(keyword)
        || post.item_id.toLowerCase().includes(keyword)
        || post.accountLabel.toLowerCase().includes(keyword);
      return matchesSource && matchesKeyword;
    });
  }, [normalizedPosts, search, sourceMode]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || !open) return;
    const onScroll = () => {
      if (!hasMorePosts || loadingPosts || loadingMorePosts) return;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120;
      if (nearBottom) onLoadMore();
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMorePosts, loadingMorePosts, loadingPosts, onLoadMore, open]);

  useEffect(() => {
    if (!open) setPlayingPreviewID('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setPendingSelectedIDs(selectedItemIDs);
  }, [open, selectedItemIDs]);

  if (!open) return null;

  const toggleSelection = (itemID: string) => {
    setPendingSelectedIDs((prev) => {
      if (!multiSelect) {
        return [itemID];
      }
      return prev.includes(itemID)
        ? prev.filter((id) => id !== itemID)
        : [...prev, itemID];
    });
  };

  const pendingSelectedItems = normalizedPosts.filter((post) => pendingSelectedIDs.includes(post.item_id));

  return (
    <div className="modal-overlay open" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1120px, calc(100vw - 48px))',
          maxHeight: 'calc(100vh - 56px)',
          background: 'var(--surface)',
          borderRadius: 18,
          boxShadow: '0 32px 80px rgba(0,0,0,.24)',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateRows: 'auto auto 1fr auto',
        }}
      >
        <div style={{ padding: '22px 24px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 6 }}>添加创意</div>
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                {sourceMode === 'TIKTOK_POST'
                  ? `展示 TikTok 帖子内容，并可通过授权码补充可投放帖子。${multiSelect ? '支持一次选择多个帖子。' : '当前模式仅支持单个帖子。'}`
                  : sourceMode === 'LIBRARY'
                    ? `展示创意素材库中的可用视频素材。${multiSelect ? '支持一次选择多个素材。' : '当前模式仅支持单个素材。'}`
                    : '展示当前商品库可用的商品库创意素材。'}
              </div>
            </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="关闭">
            ×
          </button>
        </div>

        <div style={{ padding: '16px 24px 0' }}>
          <div
            style={{
              border: '1px solid rgba(20,184,166,.24)',
              background: 'rgba(20,184,166,.06)',
              color: 'var(--text2)',
              borderRadius: 12,
              padding: '14px 16px',
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            {sourceMode === 'TIKTOK_POST'
              ? '当前列表展示 TikTok 帖子内容。设为“仅通过广告显示”的非红人内容，通常会从创意素材库侧显示。'
              : sourceMode === 'LIBRARY'
                ? '当前列表展示创意素材库中的视频素材，可直接作为非 Spark 视频素材投放。'
                : '当前列表展示基于已选商品库和商品范围生成的商品库创意素材。'}
          </div>
        </div>

        <div style={{ padding: 24, overflow: 'auto', display: 'grid', gap: 18 }}>
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <input
                className="form-input"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={sourceMode === 'CATALOG_CREATIVE' ? '按素材名称或素材 ID 搜索' : '按帖子标题或帖子 ID 搜索'}
                style={{ flex: '1 1 320px', minWidth: 260 }}
              />
              <MultiSelect
                single
                options={identities.map((item) => ({ value: item.identity_id, label: item.display_name || item.username || item.identity_id }))}
                value={selectedIdentityID ? [selectedIdentityID] : []}
                onChange={(vals) => onIdentityChange(vals[0] ?? '')}
                placeholder="TikTok 账号：请选择"
                style={{ width: 260 }}
              />
              <MultiSelect
                single
                options={[{ value: sourceMode === 'CATALOG_CREATIVE' ? 'CAROUSEL' : 'VIDEO', label: `样式：${sourceMode === 'CATALOG_CREATIVE' ? '轮播' : '视频'}` }]}
                value={[sourceMode === 'CATALOG_CREATIVE' ? 'CAROUSEL' : 'VIDEO']}
                onChange={() => {}}
                style={{ width: 180, color: 'var(--text2)', pointerEvents: 'none', opacity: 0.75 }}
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 320px',
                gap: 18,
                alignItems: 'start',
              }}
            >
              <div
                ref={listRef}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 16,
                  background: 'var(--surface2)',
                  padding: 16,
                  minHeight: 460,
                  maxHeight: 460,
                  overflowY: 'auto',
                }}
              >
                {loadingPosts || loadingLibrary ? (
                  <div style={{ minHeight: 428, display: 'grid', placeItems: 'center', color: 'var(--text3)', fontSize: 14 }}>
                    正在加载创意内容...
                  </div>
                ) : filteredPosts.length === 0 ? (
                  <div style={{ minHeight: 428, display: 'grid', placeItems: 'center', color: 'var(--text3)', fontSize: 14 }}>
                    {posts.length === 0
                      ? sourceMode === 'CATALOG_CREATIVE'
                        ? '当前条件下没有可选的商品库创意素材，请先确认已选择商品库并设置商品范围。'
                        : '当前账号下没有可选内容，请先确认该 TikTok Identity 已授权且有可投放内容。'
                      : '没有匹配的内容。'}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 14 }}>
                    {filteredPosts.map((post) => {
                      const active = pendingSelectedIDs.includes(post.item_id);
                      const isPlaying = playingPreviewID === post.item_id && !!post.preview_url;
                      return (
                        <button
                          key={post.item_id}
                          type="button"
                          onClick={() => toggleSelection(post.item_id)}
                          style={{
                            border: active ? '2px solid var(--primary)' : '1px solid var(--border)',
                            borderRadius: 14,
                            padding: 10,
                            background: 'var(--surface)',
                            cursor: 'pointer',
                            textAlign: 'left',
                            boxShadow: active ? '0 10px 24px rgba(20,184,166,.16)' : 'none',
                          }}
                        >
                          <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#E8EAF0', aspectRatio: '9 / 16', marginBottom: 10 }}>
                            {isPlaying ? (
                              <video
                                src={post.preview_url}
                                poster={post.cover_url}
                                controls
                                autoPlay
                                muted
                                playsInline
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : post.cover_url ? (
                              <img src={post.cover_url} alt={post.contentLabel} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: 'rgba(0,0,0,.45)', fontSize: 12 }}>
                                无封面
                              </div>
                            )}
                            {!isPlaying && post.preview_url ? (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPlayingPreviewID(post.item_id);
                                }}
                                style={{
                                  position: 'absolute',
                                  inset: 'auto auto 12px 12px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  borderRadius: 999,
                                  padding: '8px 12px',
                                  background: 'rgba(15,23,42,.78)',
                                  color: '#fff',
                                  fontSize: 12,
                                  cursor: 'pointer',
                                }}
                              >
                                <span style={{ fontSize: 14, lineHeight: 1 }}>▶</span>
                                点击播放
                              </span>
                            ) : null}
                            <div style={{ position: 'absolute', left: 8, right: 8, bottom: 8, display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                              {post.resolutionLabel ? (
                                <span style={{ background: 'rgba(15,23,42,.88)', color: '#fff', borderRadius: 999, padding: '4px 8px', fontSize: 11, lineHeight: 1 }}>
                                  {post.resolutionLabel}
                                </span>
                              ) : <span />}
                              {post.durationLabel ? (
                                <span style={{ background: 'rgba(15,23,42,.88)', color: '#fff', borderRadius: 999, padding: '4px 8px', fontSize: 11, lineHeight: 1 }}>
                                  {post.durationLabel}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div style={{ display: 'grid', gap: 6 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'start' }}>
                              <input
                                type="checkbox"
                                checked={active}
                                readOnly
                                style={{ accentColor: 'var(--primary)', marginTop: 2 }}
                              />
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', lineHeight: 1.45, minHeight: 38 }}>
                              {post.contentLabel}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text2)' }}>{post.accountLabel}</div>
                            <div style={{ fontSize: 11, color: 'var(--primary)' }}>{post.sourceLabel}</div>
                            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{post.source === 'catalog_creative' ? `素材 ID：${post.item_id}` : `帖子 ID：${post.item_id}`}</div>
                          </div>
                        </button>
                      );
                    })}
                    {sourceMode !== 'CATALOG_CREATIVE' && loadingMorePosts && (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '8px 0' }}>
                        正在加载更多帖子...
                      </div>
                    )}
                    {sourceMode !== 'CATALOG_CREATIVE' && !loadingMorePosts && hasMorePosts && (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text3)', fontSize: 12, padding: '4px 0 0' }}>
                        向下滚动以加载更多，默认按发布时间近到远
                      </div>
                    )}
                  </div>
                )}
              </div>

              {sourceMode === 'TIKTOK_POST' ? (
                <div
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 16,
                    background: 'var(--surface)',
                    padding: 16,
                    display: 'grid',
                    gap: 14,
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 700 }}>根据授权码添加</div>
                  <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>
                    粘贴创作者提供的授权码，系统会自动解析对应帖子并回填内容 ID。
                  </div>
                  <input
                    className="form-input"
                    value={authCode}
                    onChange={(e) => onAuthCodeChange(e.target.value)}
                    placeholder="请输入授权码"
                  />
                  <button type="button" className="btn btn-primary" disabled={!authCode.trim()} onClick={onApplyAuthCode}>
                    添加帖子
                  </button>
                  {errorMessage && (
                    <div style={{ fontSize: 12, color: 'var(--red)', lineHeight: 1.6 }}>
                      {errorMessage}
                    </div>
                  )}
                  <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
                    当前授权账号
                    <div style={{ marginTop: 6, padding: '10px 12px', borderRadius: 10, background: 'var(--surface2)', color: 'var(--text1)', fontWeight: 600 }}>
                      {selectedIdentityLabel || '未选择 TikTok 账号'}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.7 }}>
                    首屏、搜索和滚动翻页都使用同一个帖子接口，选择帖子后会自动回填到 Step3 的 TikTok 内容 ID。
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 16,
                    background: 'var(--surface)',
                    padding: 16,
                    display: 'grid',
                    gap: 14,
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 700 }}>
                    {sourceMode === 'LIBRARY' ? '素材库说明' : '商品库创意素材说明'}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.7 }}>
                    {sourceMode === 'LIBRARY'
                      ? '这里展示创意素材库中的视频内容，选中后会自动回填 Step3 的视频 ID。'
                      : '这里展示当前商品库和商品范围下可用的商品库创意素材，选中后会自动回填到 Step3。'}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
                    当前授权账号
                    <div style={{ marginTop: 6, padding: '10px 12px', borderRadius: 10, background: 'var(--surface2)', color: 'var(--text1)', fontWeight: 600 }}>
                      {selectedIdentityLabel || '未选择 TikTok 账号'}
                    </div>
                  </div>
                  {sourceMode === 'CATALOG_CREATIVE' && (
                    <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.7 }}>
                      商品库创意素材会使用当前页面中已选的商品库与商品范围，不需要再单独填写视频 ID 或 TikTok 内容 ID。
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px 22px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            {pendingSelectedItems.length > 0 ? `已选择 ${pendingSelectedItems.length} 个内容` : '尚未选择内容'}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {pendingSelectedItems.slice(0, 3).map((item) => (
              <span key={item.item_id} style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--surface2)', borderRadius: 999, padding: '4px 8px' }}>
                {item.contentLabel}
              </span>
            ))}
            {pendingSelectedItems.length > 3 ? (
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>+{pendingSelectedItems.length - 3}</span>
            ) : null}
            <button type="button" className="btn btn-outline" onClick={onClose}>
              关闭
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={pendingSelectedItems.length === 0}
              onClick={() => onApplySelection(pendingSelectedItems)}
            >
              确认选择
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CreateTikTokAdWizard({
  brandId,
  siteId,
  onCancel,
  onCreated,
  onStepChange,
  onObjectiveChange,
  onAdFormatChange,
  editMode,
  createUnder,
  initialAccountID,
}: Props) {
  const { brand, hasPermission, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const showToast = useToastStore((s) => s.show);
  const createUnderMinStep: WizardStep = createUnder
    ? (createUnder.entity === 'campaign' ? 2 : 3)
    : 1;
  const [step, setStep] = useState<WizardStep>(createUnder ? createUnderMinStep : 1);
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const errorRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const formCardRef = useRef<HTMLDivElement>(null);
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [selectedAccountID, setSelectedAccountID] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [identityOptions, setIdentityOptions] = useState<TikTokIdentityOption[]>([]);
  const [selectedIdentityID, setSelectedIdentityID] = useState('');
  const [loadingIdentities, setLoadingIdentities] = useState(false);

  // Fetched resource lists
  const [pixelList, setPixelList] = useState<TikTokPixel[]>([]);
  const [audienceList, setAudienceList] = useState<TikTokAudience[]>([]);
  const [regionOptions, setRegionOptions] = useState<Option[]>([]);
  const [regionCodeToLocationID, setRegionCodeToLocationID] = useState<Record<string, string>>({});
  const [catalogList, setCatalogList] = useState<TikTokCatalog[]>([]);
  const [catalogSetList, setCatalogSetList] = useState<TikTokCatalogSet[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogSetLoading, setCatalogSetLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [catalogSetError, setCatalogSetError] = useState('');
  const resolveTikTokSelectedAccountID = useCallback((accountValue?: string | null) => {
    const normalized = (accountValue || '').trim();
    if (!normalized) return '';
    const matchAccount = accounts.find((a) => a.account_id === normalized) || accounts.find((a) => a.id === normalized);
    return matchAccount?.id || normalized;
  }, [accounts]);
  const [sparkPostList, setSparkPostList] = useState<TikTokSparkPost[]>([]);
  const [libraryCreativeList, setLibraryCreativeList] = useState<TikTokSparkPost[]>([]);
  const [libraryCreativeLoading, setLibraryCreativeLoading] = useState(false);
  const [sparkPostLoading, setSparkPostLoading] = useState(false);
  const [sparkPostLoadingMore, setSparkPostLoadingMore] = useState(false);
  const [sparkPostCursor, setSparkPostCursor] = useState('');
  const [sparkPostHasMore, setSparkPostHasMore] = useState(false);
  const [sparkPostKeyword, setSparkPostKeyword] = useState('');
  const [interestSearchKeyword, setInterestSearchKeyword] = useState('');
  const [interestSearchResults, setInterestSearchResults] = useState<TikTokTargetingSearchItem[]>([]);
  const [knownTargetingItems, setKnownTargetingItems] = useState<TikTokTargetingSearchItem[]>([]);
  const [interestSearchLoading, setInterestSearchLoading] = useState(false);

  // New fields
  const [catalogEnabled, setCatalogEnabled] = useState(false);
  const [catalogID, setCatalogID] = useState('');
  const [productSelectionMode, setProductSelectionMode] = useState<'ALL_PRODUCTS' | 'PRODUCT_SET'>('ALL_PRODUCTS');
  const [catalogSetID, setCatalogSetID] = useState('');
  const [frequency, setFrequency] = useState('');
  const [frequencySchedule, setFrequencySchedule] = useState('7');
  const [reachFrequencyMode, setReachFrequencyMode] = useState<'TARGETED' | 'CAP'>('TARGETED');
  const [reachTargetedFrequency, setReachTargetedFrequency] = useState('7:3');
  const [reachCapMode, setReachCapMode] = useState<'PRESET' | 'CUSTOM'>('PRESET');
  const [reachCapPreset, setReachCapPreset] = useState('7:3');
  const [reachCapCustomCount, setReachCapCustomCount] = useState('3');
  const [reachCapCustomDays, setReachCapCustomDays] = useState('1');
  const [videoFrequencyMode, setVideoFrequencyMode] = useState<'NONE' | 'CUSTOM'>('NONE');
  const [videoFrequencyCount, setVideoFrequencyCount] = useState('3');
  const [videoFrequencyDays, setVideoFrequencyDays] = useState('1');
  const [clickAttributionWindow, setClickAttributionWindow] = useState('ONE_DAY');
  const [viewAttributionWindow, setViewAttributionWindow] = useState('ONE_DAY');
  const [leadAttributionEnabled, setLeadAttributionEnabled] = useState(false);
  const [statisticType, setStatisticType] = useState('EVERYTIME');
  const [sparkAuthCode, setSparkAuthCode] = useState('');
  const [showSparkPostModal, setShowSparkPostModal] = useState(false);
  const [sparkPostModalError, setSparkPostModalError] = useState('');

  const [campaignName, setCampaignName] = useState('');
  const [objectiveType, setObjectiveType] = useState('TRAFFIC');
  const [campaignSubtype, setCampaignSubtype] = useState('TRAFFIC_MANUAL');
  const [campaignBudget, setCampaignBudget] = useState('');
  const [useCampaignLifetime, setUseCampaignLifetime] = useState(false);
  // 非SALES: 'NONE' | 'CAMPAIGN_BUDGET' | 'CAMPAIGN_BUDGET_OPTIMIZE'
  // SALES:   'NONE' | 'CAMPAIGN_BUDGET' | 'ADGROUP_BUDGET'
  const [budgetStrategy, setBudgetStrategy] = useState<string>('NONE');
  const [campaignStatus, setCampaignStatus] = useState('DISABLE');

  // ── 动态广告标签 ──────────────────────────────────────────────────────────
  const [tagConfig, setTagConfig] = useState<TagDefinition[]>([]);
  const [campaignTags, setCampaignTags] = useState<Record<string, string>>({});
  const [adsetTags, setAdsetTags] = useState<Record<string, string>>({});
  const [adTags, setAdTags] = useState<Record<string, string>>({});
  const siteCode = brand === 'vooglam' ? 'v' : 'z';

  useEffect(() => {
    adTagsApi.getConfig(siteCode, 'tiktok')
      .then(res => setTagConfig(res?.data ?? []))
      .catch(() => setTagConfig([]));
  }, [siteCode]);

  const [adGroupName, setAdGroupName] = useState('');
  const [adGroupStatus, setAdGroupStatus] = useState('DISABLE');
  const [adGroupBudgetMode, setAdGroupBudgetMode] = useState('BUDGET_MODE_DAY');
  const [adGroupBudget, setAdGroupBudget] = useState('');
  const [scheduleType, setScheduleType] = useState('SCHEDULE_FROM_NOW');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [optimizationGoal, setOptimizationGoal] = useState('CLICK');
  const [bidType, setBidType] = useState('BID_TYPE_NO_BID');
  const [bidPrice, setBidPrice] = useState('');
  const [billingEvent, setBillingEvent] = useState('OCPM');
  const [pixelID, setPixelID] = useState('');
  const [optimizationEvent, setOptimizationEvent] = useState('LANDING_PAGE_VIEW');
  const [audienceExcludeText, setAudienceExcludeText] = useState('');
  const [audienceIDsText, setAudienceIDsText] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [ageGroups, setAgeGroups] = useState<string[]>([]);
  const [gender, setGender] = useState('GENDER_UNLIMITED');
  const [languages, setLanguages] = useState<string[]>([]);
  const [minimumAge, setMinimumAge] = useState('18');
  const [interestsBehaviorText, setInterestsBehaviorText] = useState('');
  const [runContinuously, setRunContinuously] = useState(true);
  const [targetRoas, setTargetRoas] = useState('');
  const [promotionType, setPromotionType] = useState('');
  const [appID, setAppID] = useState('');
  const [adName, setAdName] = useState('');
  const [adStatus, setAdStatus] = useState('DISABLE');
  const [adText, setAdText] = useState('');
  const [salesAdTexts, setSalesAdTexts] = useState<string[]>(['']);
  const [ctaMode, setCtaMode] = useState<'DYNAMIC' | 'STANDARD'>('DYNAMIC');
  const [callToAction, setCallToAction] = useState('LEARN_MORE');
  const [landingPageURL, setLandingPageURL] = useState('');
  const [videoID, setVideoID] = useState('');
  const [tikTokItemID, setTikTokItemID] = useState('');
  const [appliedSparkAuthCode, setAppliedSparkAuthCode] = useState('');
  const [sparkPostIdentityID, setSparkPostIdentityID] = useState('');
  const [catalogCreativeAssetTypes, setCatalogCreativeAssetTypes] = useState<string[]>(['VIDEO', 'IMAGE']);
  const [videoSource, setVideoSource] = useState<'TIKTOK_POST' | 'LIBRARY' | 'CATALOG_CREATIVE'>('LIBRARY');
  const [tikTokPostAdFormat, setTikTokPostAdFormat] = useState<'SPARK_ADS' | 'SINGLE_VIDEO'>('SINGLE_VIDEO');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadPct, setVideoUploadPct] = useState(0);
  const [musicID, setMusicID] = useState('');
  const [musicUploading, setMusicUploading] = useState(false);
  const [musicUploadPct, setMusicUploadPct] = useState(0);
  const sparkPostQueryKeyRef = useRef('');
  const initialDraftSnapshotRef = useRef<TikTokDraftPayload | null>(null);
  const savedDraftSnapshotRef = useRef<TikTokDraftPayload | null>(null);
  const draftLoadedRef = useRef(false);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTempDraftSignatureRef = useRef('');
  const [draftReady, setDraftReady] = useState(false);
  const [draftBootstrapVersion, setDraftBootstrapVersion] = useState(0);
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState<string | null>(null);
  const [hasTempDraft, setHasTempDraft] = useState(false);

  // ── Edit mode state ──
  const [editLoading, setEditLoading] = useState(!!editMode);
  const [createUnderLoading, setCreateUnderLoading] = useState(!!createUnder);
  const [editableFields, setEditableFields] = useState<string[]>([]);
  // editMode?.id is used for save calls; no separate entity IDs needed
  const isEditMode = !!editMode;
  const draftKey = useMemo(() => {
    if (isEditMode && editMode) return `tiktok:edit:${editMode.entity}:${editMode.id}`;
    if (createUnder) return `tiktok:create-under:${createUnder.entity}:${createUnder.parentId}`;
    return 'tiktok:create:full';
  }, [createUnder, editMode, isEditMode]);
  const canEdit = (field: string) => !isEditMode || editableFields.includes(field);
  const draftFeatureEnabled = !isEditMode || editableFields.length === 0 || (
    editMode?.entity === 'campaign'
      ? canEdit('campaign.objective_type')
      : editMode?.entity === 'adset'
        ? canEdit('adset.budget_mode')
        : canEdit('ad.landing_page_url')
  );
  const draftScene = isEditMode ? 'edit' : 'create';
  const draftEntityType = isEditMode ? editMode!.entity : (createUnder?.entity || 'campaign');
  const draftEntityID = isEditMode ? editMode!.id : undefined;
  const draftName = isEditMode ? `tiktok-${editMode!.entity}-${editMode!.id}` : draftKey;

  const editMaxStep: WizardStep = editMode
    ? (editMode.entity === 'campaign' ? 1 : editMode.entity === 'adset' ? 2 : 3)
    : 3;

  const objectiveConfig = useMemo(() => getObjectiveConfig(objectiveType), [objectiveType]);
  const subtypeConfig = useMemo(() => getCampaignSubtypeConfig(campaignSubtype), [campaignSubtype]);
  const subtypeOptions = useMemo(
    () => getObjectiveSubtypeOptions(objectiveType as 'REACH' | 'TRAFFIC' | 'VIDEO_VIEWS' | 'SALES' | 'LEAD_GENERATION'),
    [objectiveType],
  );
  const destinationConfig = useMemo(() => getDestinationConfig(promotionType), [promotionType]);
  const apiObjectiveType = objectiveConfig.apiObjectiveType ?? objectiveType;
  const apiPromotionType =
    objectiveType === 'LEAD_GENERATION'
      ? 'LEAD_GENERATION'
      : (destinationConfig?.apiPromotionType ?? promotionType);
  const apiPromotionTargetType =
    apiPromotionType === 'LEAD_GENERATION'
      ? 'EXTERNAL_WEBSITE'
      : undefined;
  const goals = objectiveConfig.optimizationGoals;
  const bids = objectiveConfig.bidStrategies[optimizationGoal] ?? [{ value: 'BID_TYPE_NO_BID', label: '自动 CPM' }];
  const isLeadGenObjective = objectiveType === 'LEAD_GENERATION';
  const isSalesObjective = objectiveType === 'SALES';
  const isSmartPlusMode = isSalesObjective;
  const isTrafficObjective = objectiveType === 'TRAFFIC';
  const needsLandingPageURL = true;
  const landingPageRequired = isSalesObjective;
  const isTrafficAppPath = isTrafficObjective && (promotionType === 'APP_ANDROID' || promotionType === 'APP_IOS');
  const isTrafficAdvancedGoal =
    isTrafficObjective && ['TRAFFIC_LANDING_PAGE_VIEW', 'ENGAGED_VIEW'].includes(optimizationGoal);
  const isLeadClickGoal = objectiveType === 'LEAD_GENERATION' && optimizationGoal === 'CLICK';
  const isLeadConvertGoal = objectiveType === 'LEAD_GENERATION' && optimizationGoal === 'CONVERT';
  const isSalesConvertGoal = isSalesObjective && optimizationGoal === 'CONVERT';
  const isSalesValueGoal = isSalesObjective && optimizationGoal === 'VALUE';
  const needsApp = isTrafficAppPath;
  const isReachObjective = objectiveType === 'REACH';
  const isVideoViewsObjective = objectiveType === 'VIDEO_VIEWS';
  const visibleModules = useMemo(() => {
    const modules = new Set(objectiveConfig.visibleModules);
    for (const item of destinationConfig?.visibleModules ?? []) modules.add(item);
    return Array.from(modules);
  }, [objectiveConfig, destinationConfig]);
  const showCampaignBudget = budgetStrategy === 'CAMPAIGN_BUDGET' || budgetStrategy === 'CAMPAIGN_BUDGET_OPTIMIZE';
  const showAdGroupBudget = budgetStrategy === 'ADGROUP_BUDGET';
  const campaignBudgetTypeLabel = useCampaignLifetime ? '总预算' : '日预算';
  const campaignBudgetSummary = `使用推广系列预算：${campaignBudgetTypeLabel} ${Number(campaignBudget || 0).toFixed(2)} USD`;
  const isTikTokPostCreative = videoSource === 'TIKTOK_POST';
  const adFormat = videoSource === 'TIKTOK_POST'
    ? tikTokPostAdFormat
    : videoSource === 'CATALOG_CREATIVE'
      ? 'CAROUSEL'
      : 'SINGLE_VIDEO';

  const [pixelEventOptions, setPixelEventOptions] = useState<Option[]>(PIXEL_EVENTS);
  const ageOptions = isSalesObjective ? SALES_AGES : AGES;
  const audienceIDs = useMemo(() => csvList(audienceIDsText), [audienceIDsText]);
  const payloadAgeGroups = isSalesObjective
    ? (ageGroups.length === 0 || ageGroups.includes('AGE_UNLIMITED') ? SALES_AGE_VALUES : ageGroups.filter((v) => v !== 'AGE_UNLIMITED'))
    : ageGroups.length === 0 || ageGroups.includes('AGE_UNLIMITED')
      ? []
      : ageGroups;
  const handleAgeGroupsChange = (values: string[]) => {
    const deduped = dedupeStrings(values);
    if (deduped.includes('AGE_UNLIMITED')) {
      setAgeGroups(['AGE_UNLIMITED']);
      return;
    }
    if (isSalesObjective) {
      setAgeGroups(deduped.filter((value) => SALES_AGE_VALUES.includes(value)));
      return;
    }
    setAgeGroups(deduped.filter((value) => value !== 'AGE_UNLIMITED'));
  };
  useEffect(() => {
    if (ageGroups.includes('AGE_13_17')) {
      setMinimumAge('13');
      return;
    }
    setMinimumAge('18');
  }, [ageGroups]);
  // When switching to SALES objective, clean up invalid age values (remove AGE_13_17)
  useEffect(() => {
    if (!isSalesObjective) return;
    const invalid = ageGroups.filter((v) => v !== 'AGE_UNLIMITED' && !SALES_AGE_VALUES.includes(v));
    if (invalid.length > 0) {
      setAgeGroups((prev) => prev.filter((v) => v === 'AGE_UNLIMITED' || SALES_AGE_VALUES.includes(v)));
    }
  }, [isSalesObjective]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectedInterestBehaviorIDs = useMemo(() => csvList(interestsBehaviorText), [interestsBehaviorText]);
  const selectedInterestCategoryIDs = useMemo(
    () =>
      selectedInterestBehaviorIDs
        .filter((id) => {
          const matched = knownTargetingItems.find((item) => item.id === id);
          return matched ? (matched.category ?? 'INTEREST') === 'INTEREST' : true;
        })
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0),
    [knownTargetingItems, selectedInterestBehaviorIDs],
  );
  const selectedVideoActionCategoryIDs = useMemo(
    () =>
      selectedInterestBehaviorIDs.filter((id) => {
        const matched = knownTargetingItems.find((item) => item.id === id);
        return matched?.sub_type === 'VIDEO_INTERACTION';
      }),
    [knownTargetingItems, selectedInterestBehaviorIDs],
  );
  const selectedInterestBehaviorItems = useMemo(
    () =>
      selectedInterestBehaviorIDs.map((id) => {
        const matched = knownTargetingItems.find((item) => item.id === id);
        return matched ?? { id, name: id };
      }),
    [knownTargetingItems, selectedInterestBehaviorIDs],
  );
  const resolvedBidType = useMemo(() => {
    if ((bidType === 'BID_TYPE_CUSTOM' || bidType === 'BID_TYPE_TARGET_COST') && !isPositive(bidPrice)) {
      return 'BID_TYPE_NO_BID';
    }
    return bidType;
  }, [bidPrice, bidType]);
  const selectedIdentity = useMemo(
    () => identityOptions.find((item) => item.identity_id === selectedIdentityID) ?? null,
    [identityOptions, selectedIdentityID],
  );
  const selectedVideoIDs = useMemo(() => parseTikTokCreativeIDs(videoID), [videoID]);
  const selectedTikTokItemIDs = useMemo(() => parseTikTokCreativeIDs(tikTokItemID), [tikTokItemID]);
  const selectedSparkPost = useMemo(
      () => sparkPostList.find((item) => item.item_id === selectedTikTokItemIDs[selectedTikTokItemIDs.length - 1]) ?? null,
      [sparkPostList, selectedTikTokItemIDs],
    );
  const selectedLibraryCreative = useMemo(
      () => libraryCreativeList.find((item) => item.item_id === selectedVideoIDs[selectedVideoIDs.length - 1]) ?? null,
      [libraryCreativeList, selectedVideoIDs],
    );
    const mergedCreativeList = useMemo(
      () => {
        const map = new Map<string, TikTokSparkPost>();
        sparkPostList.forEach((item) => map.set(`post:${item.item_id}`, item));
        libraryCreativeList.forEach((item) => map.set(`lib:${item.item_id}`, item));
        return Array.from(map.values());
      },
      [libraryCreativeList, sparkPostList],
    );
  const selectedSparkPosts = useMemo(
    () => selectedTikTokItemIDs.map((id) => sparkPostList.find((item) => item.item_id === id) || mergedCreativeList.find((item) => item.item_id === id)).filter(Boolean) as TikTokSparkPost[],
    [mergedCreativeList, selectedTikTokItemIDs, sparkPostList],
  );
  const removeTikTokItemID = (itemID: string) => {
    const nextIDs = selectedTikTokItemIDs.filter((id) => id !== itemID);
    setTikTokItemID(nextIDs.join(','));
    if (nextIDs.length === 0) {
      setAppliedSparkAuthCode('');
    }
    setFieldErrors((p) => ({ ...p, tikTokItemID: '' }));
  };
    const catalogBcID = useMemo(
    () => selectedIdentity?.identity_authorized_bc_id || identityOptions.find((item) => item.identity_authorized_bc_id)?.identity_authorized_bc_id || '',
    [identityOptions, selectedIdentity],
  );
  const loadTikTokPosts = async (cursor?: string, keyword?: string) => {
    if (!brandId || !advertiserIDForFetch || !selectedIdentity?.identity_id) return;
    const params = {
      brand_id: brandId,
      site_id: siteId || undefined,
      advertiser_id: advertiserIDForFetch,
      identity_id: selectedIdentity.identity_id,
      identity_type: selectedIdentity.identity_type || undefined,
      identity_authorized_bc_id: selectedIdentity.identity_authorized_bc_id || undefined,
      cursor: cursor || undefined,
      keyword: (keyword ?? sparkPostKeyword).trim() || undefined,
    };
    const res = await accountsApi.listTikTokPosts(params);
    const list = res.list ?? [];
    setSparkPostList((prev) => (cursor ? [...prev, ...list.filter((item) => !prev.some((p) => p.item_id === item.item_id))] : list));
    setSparkPostCursor(res.cursor ?? '');
    setSparkPostHasMore(Boolean(res.has_more && res.cursor));
    if (!cursor) {
      sparkPostQueryKeyRef.current = `${selectedIdentity.identity_id}::${((keyword ?? sparkPostKeyword).trim().toLowerCase())}`;
    }
  };
  const upsertSparkPost = (post: TikTokSparkPost) => {
    setSparkPostList((prev) => {
      const idx = prev.findIndex((item) => item.item_id === post.item_id);
      if (idx < 0) return [post, ...prev];
      const current = prev[idx];
      const next = [...prev];
      next[idx] = {
        ...current,
        ...post,
        video_id: post.video_id || current.video_id,
        caption: post.caption || current.caption,
        cover_url: post.cover_url || current.cover_url,
        preview_url: post.preview_url || current.preview_url,
        display_name: post.display_name || current.display_name,
        source: post.source || current.source,
      };
      return next;
    });
  };
  const upsertLibraryCreative = (creative: TikTokSparkPost) => {
    setLibraryCreativeList((prev) => {
      const idx = prev.findIndex((item) => item.item_id === creative.item_id);
      if (idx < 0) return [creative, ...prev];
      const current = prev[idx];
      const next = [...prev];
      next[idx] = {
        ...current,
        ...creative,
        video_id: creative.video_id || current.video_id,
        caption: creative.caption || current.caption,
        cover_url: creative.cover_url || current.cover_url,
        preview_url: creative.preview_url || current.preview_url,
        display_name: creative.display_name || current.display_name,
        source: creative.source || current.source,
      };
      return next;
    });
  };

  useEffect(() => {
    if (isEditMode || !!createUnder) return;
    setBudgetStrategy(isSalesObjective ? 'ADGROUP_BUDGET' : 'NONE');
  }, [createUnder, isEditMode, isSalesObjective]);

  useEffect(() => {
    if (!goals.find((g) => g.value === optimizationGoal)) setOptimizationGoal(goals[0].value);
  }, [goals, optimizationGoal]);

  useEffect(() => {
    if (accounts.length === 1 && !selectedAccountID) {
      setSelectedAccountID(accounts[0].id);
    }
  }, [accounts, selectedAccountID]);

  useEffect(() => {
    if (identityOptions.length === 1 && !selectedIdentityID) {
      setSelectedIdentityID(identityOptions[0].identity_id);
    }
  }, [identityOptions, selectedIdentityID]);

  useEffect(() => {
    if (pixelList.length === 1 && !pixelID) {
      applyPixelSelection(pixelList[0].pixel_id);
    }
  }, [pixelID, pixelList]);

  useEffect(() => {
    const firstSubtype = objectiveConfig.subtypes[0];
    if (!objectiveConfig.subtypes.includes(campaignSubtype as never)) {
      setCampaignSubtype(firstSubtype);
    }
  }, [objectiveConfig, campaignSubtype]);

  useEffect(() => {
    if (subtypeConfig?.destination) {
      setPromotionType(subtypeConfig.destination);
      return;
    }
    if (objectiveConfig.defaultDestination && !objectiveConfig.destinations.includes(promotionType as never)) {
      setPromotionType(objectiveConfig.defaultDestination);
    }
  }, [subtypeConfig, objectiveConfig, promotionType]);

  useEffect(() => {
    if (!showCampaignBudget) {
      setUseCampaignLifetime(false);
      setCampaignBudget('');
    }
  }, [showCampaignBudget]);

  useEffect(() => {
    // Set default promotionType based on objective
    const defaultDest = objectiveConfig.defaultDestination;
    if (defaultDest && !promotionType) {
      setPromotionType(defaultDest);
    }
  }, [objectiveConfig, promotionType]);

  useEffect(() => {
    if (!isTrafficAppPath) setAppID('');
  }, [isTrafficAppPath]);

  useEffect(() => {
    if (isLeadGenObjective) {
      setPromotionType('WEBSITE');
    }
    if (isVideoViewsObjective) {
      setPromotionType('WEBSITE');
    }
  }, [isLeadGenObjective, isVideoViewsObjective]);

  useEffect(() => {
    if (isTrafficAppPath) {
      setOptimizationGoal('CLICK');
    }
  }, [isTrafficAppPath]);

  useEffect(() => {
    if (!isTrafficObjective) return;
    if (optimizationGoal === 'TRAFFIC_LANDING_PAGE_VIEW' && optimizationEvent !== 'LANDING_PAGE_VIEW') {
      setOptimizationEvent('LANDING_PAGE_VIEW');
      return;
    }
    if (optimizationGoal === 'ENGAGED_VIEW' && optimizationEvent !== 'ENGAGED_VIEW') {
      setOptimizationEvent('ENGAGED_VIEW');
    }
  }, [isTrafficObjective, optimizationEvent, optimizationGoal]);

  useEffect(() => {
    if (isReachObjective) {
      setBillingEvent('CPM');
      setOptimizationGoal('REACH');
    } else if (isVideoViewsObjective) {
      setBillingEvent('CPV');
      if (!['ENGAGED_VIEW', 'ENGAGED_VIEW_FIFTEEN'].includes(optimizationGoal)) {
        setOptimizationGoal('ENGAGED_VIEW');
      }
    } else if (isTrafficObjective) {
      setBillingEvent(isTrafficAdvancedGoal ? 'OCPM' : 'CPC');
    } else if (isLeadGenObjective) {
      setBillingEvent(isLeadConvertGoal ? 'OCPM' : 'CPC');
    } else if (isSalesObjective) {
      setBillingEvent('OCPM');
    }
  }, [
    isLeadClickGoal,
    isLeadConvertGoal,
    isLeadGenObjective,
    isReachObjective,
    isSalesObjective,
    isTrafficAdvancedGoal,
    isTrafficObjective,
    isVideoViewsObjective,
    optimizationGoal,
  ]);

  useEffect(() => {
    if (isSalesValueGoal) {
      setBidType('BID_TYPE_TARGET_COST');
      setBidPrice('');
    }
  }, [isSalesValueGoal]);

  useEffect(() => {
    if (!isSalesValueGoal) setTargetRoas('');
    if (!isReachObjective && !isVideoViewsObjective) {
      setFrequency('');
      setFrequencySchedule('7');
    }
    if (!isSalesObjective) {
      setCatalogEnabled(false);
      setCatalogID('');
      setCatalogSetID('');
      setProductSelectionMode('ALL_PRODUCTS');
    }
  }, [isSalesValueGoal, isReachObjective, isVideoViewsObjective, isSalesObjective]);

  useEffect(() => {
    if (!isReachObjective) {
      setReachFrequencyMode('TARGETED');
      setReachTargetedFrequency('7:3');
      setReachCapMode('PRESET');
      setReachCapPreset('7:3');
      setReachCapCustomCount('3');
      setReachCapCustomDays('1');
    }
  }, [isReachObjective]);

  useEffect(() => {
    setVideoFrequencyMode('NONE');
    setVideoFrequencyCount('3');
    setVideoFrequencyDays('1');
  }, [isVideoViewsObjective]);

  useEffect(() => {
    if (!isLeadConvertGoal) {
      setLeadAttributionEnabled(false);
      setClickAttributionWindow('ONE_DAY');
      setViewAttributionWindow('ONE_DAY');
      setStatisticType('EVERYTIME');
    }
  }, [isLeadConvertGoal]);

  useEffect(() => {
    if (!bids.find((b) => b.value === bidType)) setBidType(bids[0].value);
  }, [bids, bidType]);

  useEffect(() => {
    if (error && errorRef.current) errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  useEffect(() => {
    onStepChange?.(step);
  }, [onStepChange, step]);

  useEffect(() => {
    onObjectiveChange?.(objectiveType);
  }, [objectiveType, onObjectiveChange]);

  useEffect(() => {
    onAdFormatChange?.(adFormat);
  }, [adFormat, onAdFormatChange]);

  useEffect(() => {
    return undefined;
  }, [videoFile]);

  useEffect(() => {
    if (!brandId) return;
    setLoadingAccounts(true);
    accountsApi
      .list({ brand_id: brandId, site_id: siteId || undefined, platform: 'tiktok', scope: 'campaign_write', page: 1, size: 100 })
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const opts = rows.map((r) => ({ id: r.id, account_id: r.account_id, account_name: r.account_name }));
        setAccounts(opts);
        setSelectedAccountID((v) => {
          const requested = (initialAccountID || '').trim();
          const initialMatch = requested
            ? opts.find((item) => item.account_id === requested || item.id === requested)
            : undefined;
          if (initialMatch) return initialMatch.id;
          return v || opts[0]?.id || '';
        });
      })
      .catch(() => {
        setAccounts([]);
        setSelectedAccountID('');
      })
      .finally(() => setLoadingAccounts(false));
  }, [brandId, initialAccountID, siteId]);

  useEffect(() => {
    const account = accounts.find((a) => a.id === selectedAccountID);
    const advertiserID = account?.account_id ?? selectedAccountID;
    if (!brandId || !advertiserID) {
      setIdentityOptions([]);
      setSelectedIdentityID('');
      return;
    }
    setLoadingIdentities(true);
    accountsApi
      .listTikTokIdentities(brandId, advertiserID, siteId || undefined)
      .then((res) => {
        const items = Array.isArray(res.items) ? res.items : [];
        setIdentityOptions(items);
        setSelectedIdentityID((current) =>
          items.some((item) => item.identity_id === current) ? current : items[0]?.identity_id ?? '',
        );
      })
      .catch(() => {
        setIdentityOptions([]);
        setSelectedIdentityID('');
      })
      .finally(() => setLoadingIdentities(false));
  }, [accounts, brandId, selectedAccountID, siteId]);

  // ── Edit mode: load existing data ──
  const editDataLoadedRef = useRef(false);
  useEffect(() => {
    if (!editMode || !brandId || accounts.length === 0 || editDataLoadedRef.current) return;
    editDataLoadedRef.current = true;
    setEditLoading(true);
    const loadEditData = async () => {
      let res: TikTokEditDetailResponse;
      if (editMode.entity === 'campaign') {
        res = await accountsApi.getTikTokCampaignEditDetail(brandId, editMode.id, siteId);
      } else if (editMode.entity === 'adset') {
        res = await accountsApi.getTikTokAdSetEditDetail(brandId, editMode.id, siteId);
      } else {
        res = await accountsApi.getTikTokAdEditDetail(brandId, editMode.id, siteId);
      }
      setEditableFields(res.editable_fields || []);

      // Campaign fields
      if (res.campaign) {
        const c = res.campaign;
        setCampaignName(c.name || '');
        setCampaignStatus(c.operation_status || 'DISABLE');

        // Map objective_type to internal objectiveType
        const objMap: Record<string, string> = {
          TRAFFIC: 'TRAFFIC', CONVERSIONS: 'SALES', REACH: 'REACH',
          VIDEO_VIEWS: 'VIDEO_VIEWS', LEAD_GENERATION: 'LEAD_GENERATION',
          APP_PROMOTION: 'TRAFFIC', PRODUCT_SALES: 'SALES', WEB_CONVERSIONS: 'SALES',
        };
        const mappedObj = objMap[c.objective_type] || c.objective_type || 'TRAFFIC';
        setObjectiveType(mappedObj);
        setBudgetStrategy(resolveTikTokBudgetStrategyFromDetail(c, res.adset));

        // Budget
        setUseCampaignLifetime(c.budget_mode === 'BUDGET_MODE_TOTAL');
        if (c.budget_mode === 'BUDGET_MODE_INFINITE') {
          setCampaignBudget('');
        } else {
          setCampaignBudget(c.budget && c.budget > 0 ? String(c.budget) : '');
        }
        setTargetRoas(c.roas_bid && c.roas_bid > 0 ? String(c.roas_bid) : '');

        // Select the right account
        const resolvedAccountID = resolveTikTokSelectedAccountID(c.account_id);
        if (resolvedAccountID) {
          setSelectedAccountID(resolvedAccountID);
        }

        // 标签回填
        if (c.tags && Object.keys(c.tags).length > 0) {
          setCampaignTags(c.tags as Record<string, string>);
        }
      }

      // AdSet fields
      if (res.adset) {
        const s = res.adset;
        setAdGroupName(s.name || '');
        setAdGroupStatus(s.adgroup_status || 'DISABLE');
        setAdGroupBudgetMode(resolveTikTokAdGroupBudgetModeFromDetail(res.campaign, s));
        if (s.budget && s.budget > 0) setAdGroupBudget(String(s.budget));
        setScheduleType(s.schedule_type || 'SCHEDULE_FROM_NOW');
        setRunContinuously(s.schedule_type !== 'SCHEDULE_START_END');
        setStartTime(s.start_time || '');
        setEndTime(s.end_time || '');
        setOptimizationGoal(s.optimization_goal || 'CLICK');
        setBidType(s.bid_type || 'BID_TYPE_NO_BID');
        if (s.bid_price && s.bid_price > 0) setBidPrice(String(s.bid_price));
        setBillingEvent(s.billing_event || 'OCPM');
        setPixelID(s.pixel_id || '');
        setOptimizationEvent(s.optimization_event || 'LANDING_PAGE_VIEW');
        setAudienceIDsText((s.audience_ids || []).join(','));
        setAudienceExcludeText((s.excluded_audience_ids || []).join(','));
        setCountries(s.location_ids || []);
        setAgeGroups(s.age_groups || []);
        setGender(s.gender || 'GENDER_UNLIMITED');
        setLanguages(s.languages || []);
        setInterestsBehaviorText([...(s.interest_category_ids || []), ...(s.video_action_category_ids || [])].join(','));
        if (Array.isArray(s.interest_behavior_items) && s.interest_behavior_items.length > 0) {
          setKnownTargetingItems((prev) => {
            const map = new Map(prev.map((item) => [item.id, item]));
            s.interest_behavior_items?.forEach((item) => map.set(item.id, item));
            return Array.from(map.values());
          });
        }
        setPromotionType(s.promotion_type || '');
        if (s.app_id) setAppID(s.app_id);

        // Pre-select identity after identities load
        if (s.identity_id) {
          setSelectedIdentityID(s.identity_id);
        }

        // 标签回填
        if (s.tags && Object.keys(s.tags).length > 0) {
          setAdsetTags(s.tags as Record<string, string>);
        }
      }

      // Ad fields
      if (res.ad) {
        const a = res.ad;
        setAdName(a.name || '');
        setAdStatus(a.ad_status || 'DISABLE');
        setAdText(a.ad_text || '');
        // For SALES objectives, ad text goes into salesAdTexts array
        const editObjType = res.campaign?.objective_type || '';
        const editIsSales = ['CONVERSIONS', 'PRODUCT_SALES', 'WEB_CONVERSIONS', 'SALES'].includes(editObjType);
        if (editIsSales && a.ad_text) {
          setSalesAdTexts([a.ad_text]);
        }
        setCallToAction(a.call_to_action || 'LEARN_MORE');
        setLandingPageURL(a.landing_page_url || '');
        const editTikTokItemIDs = parseJSONStringList(a.tiktok_item_ids_json);
        const editVideoIDs = parseJSONStringList(a.video_ids_json);
        const resolvedTikTokItemIDs = editTikTokItemIDs.length > 0 ? editTikTokItemIDs : (a.tiktok_item_id ? [a.tiktok_item_id] : []);
        const resolvedVideoIDs = editVideoIDs.length > 0 ? editVideoIDs : (a.video_id ? [a.video_id] : []);

        // Map ad_format to videoSource + tikTokPostAdFormat
        if (resolvedTikTokItemIDs.length > 0) {
          setVideoSource('TIKTOK_POST');
          setTikTokPostAdFormat('SPARK_ADS');
          setTikTokItemID(resolvedTikTokItemIDs.join(','));
          if (a.auth_code) setAppliedSparkAuthCode(a.auth_code);
          setSparkPostList((prev) => {
            const map = new Map(prev.map((item) => [item.item_id, item]));
            resolvedTikTokItemIDs.forEach((itemID, index) => {
              const current = map.get(itemID);
              map.set(itemID, {
                item_id: itemID,
                cover_url: current?.cover_url || (index === 0 ? a.post_cover_url || '' : ''),
                caption: current?.caption || (index === 0 ? a.post_caption || '' : `帖子 ${itemID}`),
                display_name: current?.display_name || a.display_name || '',
              });
            });
            return Array.from(map.values());
          });
        } else if (resolvedVideoIDs.length > 0) {
          setVideoSource('LIBRARY');
          setVideoID(resolvedVideoIDs.join(','));
          setLibraryCreativeList((prev) => {
            const map = new Map(prev.map((item) => [item.item_id, item]));
            resolvedVideoIDs.forEach((itemID, index) => {
              const current = map.get(itemID);
              map.set(itemID, {
                item_id: itemID,
                video_id: itemID,
                cover_url: current?.cover_url || (index === 0 ? a.video_cover_url || '' : ''),
                caption: current?.caption || (index === 0 ? a.name || `素材 ${itemID}` : `素材 ${itemID}`),
                display_name: current?.display_name || '创意素材库',
                source: 'creative_library',
              });
            });
            return Array.from(map.values());
          });
        } else if (a.ad_format === 'CAROUSEL') {
          setVideoSource('CATALOG_CREATIVE');
        }

        // 标签回填
        if (a.tags && Object.keys(a.tags).length > 0) {
          setAdTags(a.tags as Record<string, string>);
        }
      }

      // Set initial step to the entity being edited
      setStep(editMode.entity === 'campaign' ? 1 : editMode.entity === 'adset' ? 2 : 3);
    };
    loadEditData().catch((e) => setError(e instanceof Error ? e.message : '加载编辑数据失败')).finally(() => setEditLoading(false));
  }, [editMode, brandId, siteId, accounts, resolveTikTokSelectedAccountID]);

  // ── Load parent entity data for "create under" mode ──
  const createUnderLoadedRef = useRef(false);
  useEffect(() => {
    if (!createUnder || !brandId || accounts.length === 0 || createUnderLoadedRef.current) return;
    createUnderLoadedRef.current = true;
    setCreateUnderLoading(true);
    const loadParentData = async () => {
      if (createUnder.entity === 'campaign') {
        // Load campaign data to pre-fill account and objective
        const res = await accountsApi.getTikTokCampaignEditDetail(brandId, createUnder.parentId, siteId);
        if (res.campaign) {
          const c = res.campaign;
          setCampaignName(c.name || '');
          const objMap: Record<string, string> = {
            TRAFFIC: 'TRAFFIC', CONVERSIONS: 'SALES', REACH: 'REACH',
            VIDEO_VIEWS: 'VIDEO_VIEWS', LEAD_GENERATION: 'LEAD_GENERATION',
            APP_PROMOTION: 'TRAFFIC', PRODUCT_SALES: 'SALES', WEB_CONVERSIONS: 'SALES',
          };
          setObjectiveType(objMap[c.objective_type] || c.objective_type || 'TRAFFIC');
          setBudgetStrategy(resolveTikTokBudgetStrategyFromDetail(c, res.adset));
          if (c.budget && c.budget > 0) {
            setCampaignBudget(String(c.budget));
          }
          setUseCampaignLifetime(c.budget_mode === 'BUDGET_MODE_TOTAL');
          setTargetRoas(c.roas_bid && c.roas_bid > 0 ? String(c.roas_bid) : '');
          const resolvedAccountID = resolveTikTokSelectedAccountID(c.account_id);
          if (resolvedAccountID) setSelectedAccountID(resolvedAccountID);
        }
      } else {
        // Load adset data (includes campaign) to pre-fill account, objective, adgroup fields
        const res = await accountsApi.getTikTokAdSetEditDetail(brandId, createUnder.parentId, siteId);
        if (res.campaign) {
          const c = res.campaign;
          setCampaignName(c.name || '');
          const objMap: Record<string, string> = {
            TRAFFIC: 'TRAFFIC', CONVERSIONS: 'SALES', REACH: 'REACH',
            VIDEO_VIEWS: 'VIDEO_VIEWS', LEAD_GENERATION: 'LEAD_GENERATION',
            APP_PROMOTION: 'TRAFFIC', PRODUCT_SALES: 'SALES', WEB_CONVERSIONS: 'SALES',
          };
          setObjectiveType(objMap[c.objective_type] || c.objective_type || 'TRAFFIC');
          setBudgetStrategy(resolveTikTokBudgetStrategyFromDetail(c, res.adset));
          if (c.budget && c.budget > 0) {
            setCampaignBudget(String(c.budget));
          }
          setUseCampaignLifetime(c.budget_mode === 'BUDGET_MODE_TOTAL');
          setTargetRoas(c.roas_bid && c.roas_bid > 0 ? String(c.roas_bid) : '');
          const resolvedAccountID = resolveTikTokSelectedAccountID(c.account_id);
          if (resolvedAccountID) setSelectedAccountID(resolvedAccountID);
        }
        if (res.adset) {
          const s = res.adset;
          setAdGroupName(s.name || '');
          setAdGroupStatus(s.adgroup_status || 'DISABLE');
          setAdGroupBudgetMode(resolveTikTokAdGroupBudgetModeFromDetail(res.campaign, s));
          if (s.budget && s.budget > 0) setAdGroupBudget(String(s.budget));
          setScheduleType(s.schedule_type || 'SCHEDULE_FROM_NOW');
          setRunContinuously(s.schedule_type !== 'SCHEDULE_START_END');
          setStartTime(s.start_time || '');
          setEndTime(s.end_time || '');
          setOptimizationGoal(s.optimization_goal || 'CLICK');
          setBidType(s.bid_type || 'BID_TYPE_NO_BID');
          if (s.bid_price && s.bid_price > 0) setBidPrice(String(s.bid_price));
          setBillingEvent(s.billing_event || 'OCPM');
          setPixelID(s.pixel_id || '');
          setOptimizationEvent(s.optimization_event || 'LANDING_PAGE_VIEW');
          setAudienceIDsText((s.audience_ids || []).join(','));
          setAudienceExcludeText((s.excluded_audience_ids || []).join(','));
          setCountries(s.location_ids || []);
          setAgeGroups(s.age_groups || []);
          setGender(s.gender || 'GENDER_UNLIMITED');
          setLanguages(s.languages || []);
          setInterestsBehaviorText([...(s.interest_category_ids || []), ...(s.video_action_category_ids || [])].join(','));
          if (Array.isArray(s.interest_behavior_items) && s.interest_behavior_items.length > 0) {
            setKnownTargetingItems((prev) => {
              const map = new Map(prev.map((item) => [item.id, item]));
              s.interest_behavior_items?.forEach((item) => map.set(item.id, item));
              return Array.from(map.values());
            });
          }
          setPromotionType(s.promotion_type || '');
          if (s.app_id) setAppID(s.app_id);
          if (s.identity_id) setSelectedIdentityID(s.identity_id);
        }
      }
    };
    loadParentData()
      .catch((e) => setError(e instanceof Error ? e.message : '加载父级数据失败'))
      .finally(() => setCreateUnderLoading(false));
  }, [createUnder, brandId, siteId, accounts, resolveTikTokSelectedAccountID]);

  // Handle case where accounts loaded but empty in createUnder mode
  useEffect(() => {
    if (createUnder && !loadingAccounts && accounts.length === 0) {
      setCreateUnderLoading(false);
      setError('该品牌下暂无可用的 TikTok 广告账户，无法创建。');
    }
  }, [createUnder, loadingAccounts, accounts]);

  // Fetch TikTok resources when account is selected
  const advertiserIDForFetch = useMemo(() => {
    const account = accounts.find((a) => a.id === selectedAccountID);
    return account?.account_id ?? selectedAccountID;
  }, [accounts, selectedAccountID]);

    useEffect(() => {
      if (!brandId || !advertiserIDForFetch) return;
      const p = { brand_id: brandId, site_id: siteId || undefined, advertiser_id: advertiserIDForFetch };
      accountsApi.listTikTokPixels(p).then((r) => setPixelList(r.list ?? [])).catch(() => setPixelList([]));
      accountsApi.listTikTokAudiences(p).then((r) => setAudienceList(r.list ?? [])).catch(() => setAudienceList([]));
      setLibraryCreativeLoading(true);
      accountsApi.listTikTokVideos(p).then((r) => {
        const list = (r.list ?? []).map((item: TikTokVideoItem) => ({
          item_id: item.video_id,
          video_id: item.video_id,
          caption: item.display_name || `素材 ${item.video_id}`,
          preview_url: item.preview_url,
          cover_url: item.cover_url,
          display_name: item.source === 'library' ? '创意素材库' : 'TikTok 主页视频',
          source: item.source === 'library' ? 'creative_library' : item.source,
          duration: item.duration,
        }));
        setLibraryCreativeList((prev) => {
          // Merge: API results take priority, but preserve cover_url from synthetic entries (edit mode) when API entry lacks it
          const prevMap = new Map(prev.map((item) => [item.item_id, item]));
          const merged = list.map((item) => {
            const existing = prevMap.get(item.item_id);
            if (existing?.cover_url && !item.cover_url) {
              return { ...item, cover_url: existing.cover_url };
            }
            return item;
          });
          const apiSet = new Set(list.map((item) => item.item_id));
          const kept = prev.filter((item) => !apiSet.has(item.item_id));
          return [...merged, ...kept];
        });
      }).catch(() => setLibraryCreativeList((prev) => prev)).finally(() => setLibraryCreativeLoading(false));
      accountsApi.searchTikTokRegions({ ...p, language: 'zh_CN' }).then((r) => {
      const countries = (r.list ?? [])
        .filter((item) => item.level === 'COUNTRY')
        .map((item) => ({ value: item.location_id, label: `${item.name} (${item.region_code || ''})` }))
        .sort((a, b) => a.label.localeCompare(b.label));
      const codeMap = (r.list ?? [])
        .filter((item) => item.level === 'COUNTRY' && item.region_code && item.location_id)
        .reduce<Record<string, string>>((acc, item) => {
          acc[String(item.region_code).toUpperCase()] = String(item.location_id);
          return acc;
        }, {});
      setRegionOptions(countries);
      setRegionCodeToLocationID(codeMap);
      setCountries((prev) => {
        if (prev.length > 0 || isEditMode || !!createUnder) return prev;
        return countries.some((item) => item.value === TIKTOK_DEFAULT_COUNTRY) ? [TIKTOK_DEFAULT_COUNTRY] : prev;
      });
    }).catch(() => {
      setRegionOptions([]);
      setRegionCodeToLocationID({});
    });
      if (!selectedIdentity?.identity_id) {
        setSparkPostList([]);
        setSparkPostLoading(false);
        setSparkPostLoadingMore(false);
        setSparkPostCursor('');
        setSparkPostHasMore(false);
        return;
      }
    setSparkPostLoading(true);
    setSparkPostLoadingMore(false);
    setSparkPostCursor('');
    setSparkPostHasMore(false);
    loadTikTokPosts().catch(() => {
      setSparkPostList([]);
      setSparkPostCursor('');
      setSparkPostHasMore(false);
    }).finally(() => setSparkPostLoading(false));
  }, [brandId, advertiserIDForFetch, selectedIdentity, siteId]);

  useEffect(() => {
    if (!catalogID) return;
    const selectedCatalog = catalogList.find((item) => item.catalog_id === catalogID);
    const rawCatalog = selectedCatalog?.raw as Record<string, unknown> | undefined;
    const rawCatalogConf = rawCatalog?.catalog_conf as Record<string, unknown> | undefined;
    const countryCode = String(
      selectedCatalog?.country_code ||
      rawCatalogConf?.country ||
      selectedCatalog?.country ||
      rawCatalog?.country_code ||
      rawCatalog?.region_code ||
      rawCatalog?.market_code ||
      rawCatalog?.country ||
      rawCatalog?.market ||
      '',
    ).trim().toUpperCase();
    if (!countryCode) return;
    const locationID = regionCodeToLocationID[countryCode];
    if (!locationID) return;
    setCountries((prev) => (prev.length === 1 && prev[0] === locationID ? prev : [locationID]));
  }, [catalogID, catalogList, regionCodeToLocationID]);

  useEffect(() => {
    if (!showSparkPostModal || !selectedIdentity?.identity_id) return;
    const queryKey = `${selectedIdentity.identity_id}::${sparkPostKeyword.trim().toLowerCase()}`;
    if (sparkPostQueryKeyRef.current === queryKey && sparkPostList.length > 0) return;
    const timer = window.setTimeout(() => {
      setSparkPostLoading(true);
      setSparkPostCursor('');
      setSparkPostHasMore(false);
      loadTikTokPosts(undefined, sparkPostKeyword).catch(() => {
        setSparkPostList([]);
        setSparkPostCursor('');
        setSparkPostHasMore(false);
      }).finally(() => setSparkPostLoading(false));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [showSparkPostModal, selectedIdentity, sparkPostKeyword, sparkPostList.length]);

  useEffect(() => {
    if (!brandId || !advertiserIDForFetch) {
      setInterestSearchResults([]);
      setKnownTargetingItems([]);
      setInterestSearchLoading(false);
      return;
    }
    const keyword = interestSearchKeyword.trim();
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setInterestSearchLoading(true);
      accountsApi
        .listTikTokTargetingSearch({
          brand_id: brandId,
          site_id: siteId || undefined,
          advertiser_id: advertiserIDForFetch,
          language: 'zh',
          keyword: keyword || undefined,
        })
        .then((r) => {
          if (cancelled) return;
          const list = r.list ?? [];
          setInterestSearchResults(list);
          setKnownTargetingItems((prev) => {
            const map = new Map(prev.map((item) => [item.id, item]));
            list.forEach((item) => map.set(item.id, item));
            return Array.from(map.values());
          });
        })
        .catch(() => {
          if (!cancelled) {
            setInterestSearchResults([]);
          }
        })
        .finally(() => {
          if (!cancelled) setInterestSearchLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [advertiserIDForFetch, brandId, interestSearchKeyword, siteId]);

  useEffect(() => {
    if (!brandId || !advertiserIDForFetch || objectiveType !== 'SALES' || !catalogEnabled) {
      setCatalogList([]);
      setCatalogSetList([]);
      setCatalogLoading(false);
      setCatalogSetLoading(false);
      setCatalogError('');
      setCatalogSetError('');
      return;
    }
    if (!catalogBcID) {
      setCatalogList([]);
      setCatalogSetList([]);
      setCatalogLoading(false);
      setCatalogSetLoading(false);
      setCatalogError('当前账户缺少可用的 bc_id，暂时无法获取商品库。');
      setCatalogSetError('');
      return;
    }
    setCatalogLoading(true);
    setCatalogError('');
    accountsApi.listTikTokCatalogs({ brand_id: brandId, site_id: siteId || undefined, advertiser_id: advertiserIDForFetch, bc_id: catalogBcID })
      .then((r) => setCatalogList(r.list ?? []))
      .catch((e) => {
        setCatalogList([]);
        setCatalogError(e instanceof Error ? e.message : '商品库获取失败');
      })
      .finally(() => setCatalogLoading(false));
  }, [brandId, advertiserIDForFetch, objectiveType, catalogBcID, catalogEnabled, siteId]);

  useEffect(() => {
    if (!brandId || !advertiserIDForFetch || objectiveType !== 'SALES' || !catalogEnabled || !catalogID) {
      setCatalogSetList([]);
      setCatalogSetLoading(false);
      setCatalogSetError('');
      if (!catalogID) setCatalogSetID('');
      return;
    }
    setCatalogSetLoading(true);
    setCatalogSetError('');
    if (!catalogBcID) {
      setCatalogSetList([]);
      setCatalogSetLoading(false);
      setCatalogSetError('当前账户缺少可用的 bc_id，暂时无法获取商品系列。');
      return;
    }
    accountsApi.listTikTokCatalogSets({ brand_id: brandId, site_id: siteId || undefined, advertiser_id: advertiserIDForFetch, bc_id: catalogBcID, catalog_id: catalogID })
      .then((r) => {
        const list = r.list ?? [];
        setCatalogSetList(list);
        setCatalogSetID((current) => (
          list.some((item) => (item.product_set_id || item.store_id) === current) ? current : ''
        ));
      })
      .catch((e) => {
        setCatalogSetList([]);
        setCatalogSetError(e instanceof Error ? e.message : '商品系列获取失败');
      })
      .finally(() => setCatalogSetLoading(false));
  }, [brandId, advertiserIDForFetch, objectiveType, catalogEnabled, catalogID, catalogBcID, siteId]);

  useEffect(() => {
    if (!catalogEnabled) {
      setCatalogID('');
      setCatalogSetID('');
      setCatalogCreativeAssetTypes(['VIDEO', 'IMAGE']);
      setProductSelectionMode('ALL_PRODUCTS');
      setCatalogList([]);
      setCatalogSetList([]);
      setCatalogError('');
      setCatalogSetError('');
    }
  }, [catalogEnabled]);

  useEffect(() => {
    if (!isSalesObjective || !catalogEnabled) {
      if (videoSource === 'CATALOG_CREATIVE') {
        setVideoSource('LIBRARY');
      }
      setCatalogCreativeAssetTypes(['VIDEO', 'IMAGE']);
      return;
    }
    if (videoSource !== 'CATALOG_CREATIVE') {
      setCatalogCreativeAssetTypes(['VIDEO', 'IMAGE']);
    }
  }, [catalogEnabled, isSalesObjective, videoSource]);

  const syncTikTokOptimizationEvents = useCallback((nextPixelID: string, nextOptimizationEvent: string) => {
    const apiObjective = objectiveConfig.apiObjectiveType;
    if (!brandId || !advertiserIDForFetch || !apiObjective || !optimizationGoal) {
      setPixelEventOptions(PIXEL_EVENTS);
      return;
    }
    if (isSalesValueGoal) {
      setPixelEventOptions(SALES_VALUE_EVENT_OPTIONS);
      if (optimizationEvent !== 'SHOPPING') {
        setOptimizationEvent('SHOPPING');
      }
      return;
    }
    if (isSalesConvertGoal) {
      setPixelEventOptions(SALES_CONVERT_EVENT_OPTIONS);
      if (!SALES_CONVERT_EVENT_OPTIONS.some((item) => item.value === optimizationEvent)) {
        setOptimizationEvent('SHOPPING');
      }
      return;
    }
    if (isLeadConvertGoal) {
      setPixelEventOptions(LEAD_CONVERT_EVENT_OPTIONS);
      if (!LEAD_CONVERT_EVENT_OPTIONS.some((item) => item.value === optimizationEvent)) {
        setOptimizationEvent('ON_WEB_REGISTER');
      }
      return;
    }
    if (isTrafficObjective && optimizationGoal === 'TRAFFIC_LANDING_PAGE_VIEW') {
      setPixelEventOptions([{ value: 'LANDING_PAGE_VIEW', label: OPTIMIZATION_EVENT_LABELS.LANDING_PAGE_VIEW }]);
      return;
    }
    if (isTrafficObjective && optimizationGoal === 'ENGAGED_VIEW') {
      setPixelEventOptions([{ value: 'ENGAGED_VIEW', label: '深度互动会话' }]);
      return;
    }
    accountsApi.getTikTokOptimizationEvents({
      brand_id: brandId,
      site_id: siteId || undefined,
      advertiser_id: advertiserIDForFetch,
      objective_type: apiObjective,
      optimization_goal: optimizationGoal,
      pixel_id: nextPixelID || undefined,
    })
      .then((r) => {
        const items = r.list ?? [];
        if (items.length > 0) {
          setPixelEventOptions(items.map((item) => ({
            value: item.external_action,
            label: OPTIMIZATION_EVENT_LABELS[item.external_action] || item.external_action,
          })));
          // 如果当前选中值不在新列表中，自动选中第一个 READY 状态的事件
          const ready = items.find((item) => item.availability_status === 'READY');
          const currentInList = items.some((item) => item.external_action === nextOptimizationEvent);
          if (ready && !currentInList) {
            setOptimizationEvent(ready.external_action);
          }
        } else {
          setPixelEventOptions(PIXEL_EVENTS);
        }
      })
      .catch(() => setPixelEventOptions(PIXEL_EVENTS));
  }, [advertiserIDForFetch, brandId, isLeadConvertGoal, isSalesConvertGoal, isSalesValueGoal, isTrafficObjective, objectiveConfig.apiObjectiveType, optimizationGoal, siteId]);

  const applyPixelSelection = (nextPixelID: string) => {
    setPixelID(nextPixelID);
    setOptimizationEvent('');
    setFieldErrors((p) => ({ ...p, pixelID: '', optimizationEvent: '' }));
    syncTikTokOptimizationEvents(nextPixelID, '');
  };

  // 优化事件：根据 objective + optimization_goal + pixel 联动，调用 /app/optimization_event/
  useEffect(() => {
    syncTikTokOptimizationEvents(pixelID, optimizationEvent);
  }, [brandId, advertiserIDForFetch, isLeadConvertGoal, isSalesConvertGoal, isSalesValueGoal, isTrafficObjective, objectiveConfig.apiObjectiveType, optimizationEvent, optimizationGoal, pixelID, siteId]);

  const handleSparkAuthCode = async () => {
    const code = sparkAuthCode.trim();
    if (!code || !brandId || !advertiserIDForFetch) return false;
    setSparkPostModalError('');
    try {
      const info = await accountsApi.getTikTokSparkPostInfo({ brand_id: brandId, site_id: siteId || undefined, advertiser_id: advertiserIDForFetch, auth_code: code });
      upsertSparkPost({
        item_id: info.item_id,
        video_id: info.video_id,
        caption: info.caption,
        cover_url: info.cover_url,
        preview_url: info.preview_url,
        display_name: info.display_name,
        source: 'tiktok_page',
      });
      setVideoSource('TIKTOK_POST');
      setTikTokPostAdFormat('SPARK_ADS');
      setTikTokItemID((prev) => Array.from(new Set([...parseTikTokCreativeIDs(prev), info.item_id])).join(','));
      setSparkPostIdentityID(info.identity_id || '');
      setAppliedSparkAuthCode(code);
      setSparkAuthCode('');
      setFieldErrors((p) => ({ ...p, tikTokItemID: '' }));
      return true;
    } catch (e) {
      setSparkPostModalError(e instanceof Error ? e.message : '授权码解析失败');
      return false;
    }
  };

  // Derived flags for LEAD_GENERATION
  const needsAttribution =
    (isLeadGenObjective && optimizationGoal === 'CONVERT' && leadAttributionEnabled)
    || isSalesObjective
    || (isTrafficAdvancedGoal && !!pixelID.trim());
  const needsFrequency = isReachObjective || isVideoViewsObjective;
  const requiresPixelForAdGroup =
    isLeadConvertGoal
    || isSalesObjective
    || (isTrafficAdvancedGoal && !!pixelID.trim());
  const requiresOptimizationEvent =
    !!pixelID.trim() && (
      isLeadConvertGoal
      || isSalesObjective
      || isTrafficAdvancedGoal
    );
  const resolvedReachFrequency = useMemo(() => {
    if (!isReachObjective) {
      if (isVideoViewsObjective) {
        if (videoFrequencyMode === 'NONE') {
          return { count: undefined, days: undefined };
        }
        return {
          count: isPositive(videoFrequencyCount) ? Number(videoFrequencyCount) : undefined,
          days: isPositive(videoFrequencyDays) ? Number(videoFrequencyDays) : undefined,
        };
      }
      return {
        count: isPositive(frequency) ? Number(frequency) : undefined,
        days: isPositive(frequencySchedule) ? Number(frequencySchedule) : undefined,
      };
    }
    if (reachFrequencyMode === 'TARGETED') {
      const [days, count] = reachTargetedFrequency.split(':').map(Number);
      return { count, days };
    }
    if (reachCapMode === 'PRESET') {
      const [days, count] = reachCapPreset.split(':').map(Number);
      return { count, days };
    }
    return {
      count: isPositive(reachCapCustomCount) ? Number(reachCapCustomCount) : undefined,
      days: isPositive(reachCapCustomDays) ? Number(reachCapCustomDays) : undefined,
    };
  }, [
    frequency,
    frequencySchedule,
    isReachObjective,
    isVideoViewsObjective,
    reachCapCustomCount,
    reachCapCustomDays,
    reachCapMode,
    reachCapPreset,
    reachFrequencyMode,
    reachTargetedFrequency,
    videoFrequencyCount,
    videoFrequencyDays,
    videoFrequencyMode,
  ]);

  const setFieldRef = (key: string) => (node: HTMLElement | null) => {
    fieldRefs.current[key] = node;
  };

  const scrollFormToTop = () => formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const scrollToField = (key?: string) => key && fieldRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const handleSelectVideoFile = async (fileOrUrl: File | string) => {
    if (typeof fileOrUrl !== 'string') setVideoFile(fileOrUrl);
    const account = accounts.find((a) => a.id === selectedAccountID);
    const advID = account?.account_id ?? selectedAccountID;
    if (!advID) return;
    const fd = new FormData();
    fd.append('advertiser_id', advID);
    fd.append('material_type', 'video');
    if (typeof fileOrUrl === 'string') {
      fd.append('source_url', fileOrUrl);
    } else {
      fd.append('file', fileOrUrl);
    }
    setVideoUploading(true);
    setVideoUploadPct(0);
    try {
      const result = await accountsApi.uploadTikTokMaterial(brandId, fd, typeof fileOrUrl !== 'string' ? (pct) => setVideoUploadPct(pct) : undefined);
      const localPreviewURL = typeof fileOrUrl === 'string' ? fileOrUrl : URL.createObjectURL(fileOrUrl);
      const materialLabel = typeof fileOrUrl === 'string'
        ? (fileOrUrl.split('/').pop()?.split('?')[0] || `素材 ${result.tiktok_media_id}`)
        : (fileOrUrl.name || `素材 ${result.tiktok_media_id}`);
      upsertLibraryCreative({
        item_id: result.tiktok_media_id,
        video_id: result.tiktok_media_id,
        caption: materialLabel,
        preview_url: localPreviewURL,
        display_name: '创意素材库',
        source: 'creative_library',
      });
      setVideoSource('LIBRARY');
      setTikTokPostAdFormat('SINGLE_VIDEO');
      setVideoID((prev) => Array.from(new Set([...parseTikTokCreativeIDs(prev), result.tiktok_media_id])).join(','));
      setFieldErrors((p) => ({ ...p, videoID: '' }));
    } catch (e) {
      setError(e instanceof Error ? e.message : '视频上传失败，请手动填写 video_id。');
    } finally {
      setVideoUploading(false);
    }
  };

  const handleSelectMusicFile = async (file: File) => {
    const account = accounts.find((a) => a.id === selectedAccountID);
    const advID = account?.account_id ?? selectedAccountID;
    if (!advID) return;
    const fd = new FormData();
    fd.append('advertiser_id', advID);
    fd.append('material_type', 'music');
    fd.append('file', file);
    setMusicUploading(true);
    setMusicUploadPct(0);
    try {
      const result = await accountsApi.uploadTikTokMaterial(brandId, fd, (pct) => setMusicUploadPct(pct));
      setMusicID(result.tiktok_media_id);
      setFieldErrors((p) => ({ ...p, musicID: '' }));
    } catch (e) {
      setError(e instanceof Error ? e.message : '音乐上传失败，请手动填写 music_id。');
    } finally {
      setMusicUploading(false);
    }
  };

  const payload = useMemo<TikTokCreateAdFullRequest>(() => {
    const account = accounts.find((a) => a.id === selectedAccountID);
    const needsManualPlacement = isVideoViewsObjective || isReachObjective;
    const resolvedPlacementType = needsManualPlacement ? 'PLACEMENT_TYPE_NORMAL' : 'PLACEMENT_TYPE_AUTOMATIC';
    const resolvedPlacements = needsManualPlacement && !isSalesObjective ? ['PLACEMENT_TIKTOK'] : undefined;
    const resolvedSalesAdTexts = salesAdTexts.map((item) => item.trim()).filter(Boolean);
    const payloadVideoIDs = isSmartPlusMode && videoSource === 'LIBRARY' ? selectedVideoIDs : [];
    const payloadTikTokItemIDs = isSmartPlusMode && videoSource === 'TIKTOK_POST' ? selectedTikTokItemIDs : [];
    return {
      platform: 'tiktok',
      advertiser_id: account?.account_id ?? selectedAccountID,
      site_id: siteId || undefined,
      campaign_name: campaignName.trim(),
      objective_type: apiObjectiveType,
        campaign_budget_mode: useCampaignLifetime ? 'BUDGET_MODE_TOTAL' : 'BUDGET_MODE_DAY',
        campaign_budget_strategy: budgetStrategy || undefined,
        campaign_budget: showCampaignBudget && isPositive(campaignBudget) ? Number(campaignBudget) : undefined,
        budget_optimize_on: isSalesObjective
          ? (budgetStrategy === 'CAMPAIGN_BUDGET' ? true : budgetStrategy === 'ADGROUP_BUDGET' ? false : undefined)
          : undefined,
        campaign_status: campaignStatus,
        roas_bid: isSalesValueGoal && isPositive(targetRoas) ? Number(targetRoas) : undefined,
        campaign_product_source: isSalesObjective && catalogEnabled ? 'CATALOG' : undefined,
        sales_destination: undefined,
        catalog_enabled: isSalesObjective ? catalogEnabled : undefined,
        adgroup_name: adGroupName.trim(),
      audience_ids: audienceIDs.length ? audienceIDs : undefined,
      excluded_audience_ids: audienceExcludeText.trim() ? csvList(audienceExcludeText) : undefined,
      adgroup_budget_mode: showCampaignBudget ? undefined : adGroupBudgetMode,
      adgroup_budget: showCampaignBudget ? undefined : (isPositive(adGroupBudget) ? Number(adGroupBudget) : undefined),
      schedule_type: runContinuously ? 'SCHEDULE_FROM_NOW' : scheduleType,
      start_time: runContinuously ? startTime || undefined : scheduleType === 'SCHEDULE_START_END' ? startTime || undefined : undefined,
      end_time: runContinuously ? undefined : endTime || undefined,
      placement_type: resolvedPlacementType,
      placements: resolvedPlacements,
      optimization_goal: optimizationGoal,
      bid_type: resolvedBidType,
      bid_price: isPositive(bidPrice) ? Number(bidPrice) : undefined,
      billing_event: billingEvent,
      pixel_id: pixelID.trim() || undefined,
      optimization_event: pixelID.trim() ? optimizationEvent : undefined,
      search_result_enabled: undefined,
      search_keywords: undefined,
      location_ids: countries.length ? countries : undefined,
      age_groups: payloadAgeGroups.length ? payloadAgeGroups : undefined,
      gender,
      languages: languages.length ? languages : undefined,
      interest_category_ids: selectedInterestCategoryIDs.length ? selectedInterestCategoryIDs : undefined,
      video_action_category_ids: selectedVideoActionCategoryIDs.length ? selectedVideoActionCategoryIDs : undefined,
      interest_behavior_items: selectedInterestBehaviorItems.length ? selectedInterestBehaviorItems : undefined,
      operating_systems: undefined,
      network_types: undefined,
      identity_type: (videoSource === 'TIKTOK_POST' && tikTokPostAdFormat === 'SPARK_ADS' && appliedSparkAuthCode)
        ? 'AUTH_CODE'
        : selectedIdentity?.identity_type || undefined,
      identity_id: (videoSource === 'TIKTOK_POST' && tikTokPostAdFormat === 'SPARK_ADS' && sparkPostIdentityID)
        ? sparkPostIdentityID
        : selectedIdentity?.identity_id || undefined,
      identity_authorized_bc_id: (videoSource === 'TIKTOK_POST' && tikTokPostAdFormat === 'SPARK_ADS' && appliedSparkAuthCode)
        ? undefined
        : selectedIdentity?.identity_authorized_bc_id || undefined,
      promotion_type: apiPromotionType || undefined,
      promotion_target_type: apiPromotionTargetType,
      creative_material_mode: 'CUSTOM',
      app_id: needsApp ? appID.trim() || undefined : undefined,
      adgroup_status: adGroupStatus,
      ad_name: adName.trim(),
      ad_format: adFormat,
      ad_text: isSalesObjective ? (resolvedSalesAdTexts[0] ?? '') : adText.trim(),
      ad_texts: isSalesObjective
        ? resolvedSalesAdTexts
        : undefined,
      display_name: isSalesObjective
        ? selectedIdentity?.display_name || selectedIdentity?.username || selectedIdentity?.identity_id || undefined
        : undefined,
      auth_code: videoSource === 'TIKTOK_POST' && tikTokPostAdFormat === 'SPARK_ADS'
        ? appliedSparkAuthCode || undefined
        : undefined,
        call_to_action: (needsLandingPageURL && ctaMode === 'STANDARD') ? callToAction : undefined,
        landing_page_url: needsLandingPageURL ? landingPageURL.trim() || undefined : undefined,
      video_id: videoSource === 'LIBRARY' ? (payloadVideoIDs[0] || videoID.trim() || undefined) : undefined,
      video_ids: payloadVideoIDs.length ? payloadVideoIDs : undefined,
      tiktok_item_id: videoSource === 'TIKTOK_POST' ? (payloadTikTokItemIDs[0] || tikTokItemID.trim() || undefined) : undefined,
      tiktok_item_ids: payloadTikTokItemIDs.length ? payloadTikTokItemIDs : undefined,
      ad_status: adStatus,
      music_id: videoSource === 'CATALOG_CREATIVE' ? musicID.trim() || undefined : undefined,
      video_cover_url: videoSource === 'LIBRARY' ? selectedLibraryCreative?.cover_url || undefined : undefined,
      post_cover_url: videoSource === 'TIKTOK_POST' ? selectedSparkPost?.cover_url || undefined : undefined,
      post_caption: videoSource === 'TIKTOK_POST' ? selectedSparkPost?.caption || undefined : undefined,
      pacing: (isSalesObjective || isVideoViewsObjective) ? 'PACING_MODE_SMOOTH' : 'PACING_MODE_SMOOTH',
      frequency: needsFrequency ? resolvedReachFrequency.count : undefined,
      frequency_schedule: needsFrequency ? resolvedReachFrequency.days : undefined,
      click_attribution_window: needsAttribution ? clickAttributionWindow : undefined,
      view_attribution_window: needsAttribution ? viewAttributionWindow : undefined,
      statistic_type: needsAttribution ? statisticType : undefined,
        product_source: isSalesObjective && catalogEnabled ? 'CATALOG' : undefined,
        catalog_id: isSalesObjective && catalogEnabled ? catalogID.trim() || undefined : undefined,
        catalog_authorized_bc_id: isSalesObjective && catalogEnabled
          ? selectedIdentity?.identity_authorized_bc_id || undefined
          : undefined,
        store_id: isSalesObjective && catalogEnabled
          ? catalogSetID.trim() || undefined
          : undefined,
        store_authorized_bc_id: isSalesObjective && catalogEnabled
          ? selectedIdentity?.identity_authorized_bc_id || undefined
          : undefined,
        shopping_ads_type: isSalesObjective && catalogEnabled
          ? videoSource === 'CATALOG_CREATIVE'
            ? catalogCreativeAssetTypes
                .map((item) => (item === 'IMAGE' ? 'PRODUCT_SHOPPING_ADS' : 'VIDEO'))
                .join(',')
            : 'VIDEO'
          : undefined,
        product_specific_type: isSalesObjective && catalogEnabled
          ? (productSelectionMode === 'PRODUCT_SET' ? 'PRODUCT_SET' : 'ALL')
          : undefined,
        product_set_id: isSalesObjective && catalogEnabled && productSelectionMode === 'PRODUCT_SET'
          ? catalogSetID.trim() || undefined
          : undefined,
        // 广告标签
        tags: Object.keys(campaignTags).length > 0 ? campaignTags : undefined,
        adset_tags: Object.keys(adsetTags).length > 0 ? adsetTags : undefined,
        ad_tags: Object.keys(adTags).length > 0 ? adTags : undefined,
      };
    }, [
    accounts, selectedAccountID, campaignName, apiObjectiveType,
    useCampaignLifetime, showCampaignBudget, campaignBudget, campaignStatus,
    isSalesValueGoal, targetRoas, isSalesObjective, isVideoViewsObjective,
    adGroupName, audienceIDs, audienceExcludeText,
    adGroupBudgetMode, adGroupBudget, runContinuously, scheduleType, startTime, endTime,
    optimizationGoal, bidType, resolvedBidType, bidPrice, billingEvent,
    apiPromotionType, apiPromotionTargetType, needsApp, appID, pixelID, optimizationEvent,
    countries, ageGroups, payloadAgeGroups, gender, languages, minimumAge, selectedInterestCategoryIDs, selectedVideoActionCategoryIDs, selectedInterestBehaviorItems,
    selectedIdentity, adGroupStatus, tikTokPostAdFormat,
      adName, adFormat, adText, ctaMode, callToAction, landingPageURL,
      videoID, tikTokItemID, videoSource, adStatus,
    needsFrequency, frequency, frequencySchedule, resolvedReachFrequency,
    needsAttribution, clickAttributionWindow, viewAttributionWindow, statisticType,
      catalogEnabled, catalogCreativeAssetTypes, catalogID, productSelectionMode, catalogSetID, promotionType, salesAdTexts, appliedSparkAuthCode, sparkPostIdentityID, musicID,
      campaignTags, adsetTags, adTags,
    ]);

  const buildDraftPayload = useCallback((): TikTokDraftPayload => ({
    step,
    draftParentCampaignID: createUnder?.entity === 'campaign' ? createUnder.parentId : undefined,
    draftParentAdSetID: createUnder?.entity === 'adset' ? createUnder.parentId : undefined,
    selectedAccountID,
    selectedIdentityID,
    catalogEnabled,
    catalogID,
    productSelectionMode,
    catalogSetID,
    frequency,
    frequencySchedule,
    reachFrequencyMode,
    reachTargetedFrequency,
    reachCapMode,
    reachCapPreset,
    reachCapCustomCount,
    reachCapCustomDays,
    videoFrequencyMode,
    videoFrequencyCount,
    videoFrequencyDays,
    clickAttributionWindow,
    viewAttributionWindow,
    leadAttributionEnabled,
    statisticType,
    sparkAuthCode,
    campaignName,
    objectiveType,
    campaignSubtype,
    campaignBudget,
    useCampaignLifetime,
    budgetStrategy,
    campaignStatus,
    campaignTags,
    adsetTags,
    adTags,
    adGroupName,
    adGroupStatus,
    adGroupBudgetMode,
    adGroupBudget,
    scheduleType,
    startTime,
    endTime,
    optimizationGoal,
    bidType,
    bidPrice,
    billingEvent,
    pixelID,
    optimizationEvent,
    audienceExcludeText,
    audienceIDsText,
    countries,
    ageGroups,
    gender,
    languages,
    minimumAge,
    interestsBehaviorText,
    runContinuously,
    targetRoas,
    promotionType,
    appID,
    adName,
    adStatus,
    adText,
    salesAdTexts,
    ctaMode,
    callToAction,
    landingPageURL,
    videoID,
    tikTokItemID,
    appliedSparkAuthCode,
    sparkPostIdentityID,
    catalogCreativeAssetTypes,
    videoSource,
    tikTokPostAdFormat,
    musicID,
  }), [
    step, createUnder, selectedAccountID, selectedIdentityID, catalogEnabled, catalogID, productSelectionMode, catalogSetID,
    frequency, frequencySchedule, reachFrequencyMode, reachTargetedFrequency, reachCapMode, reachCapPreset,
    reachCapCustomCount, reachCapCustomDays, videoFrequencyMode, videoFrequencyCount, videoFrequencyDays,
    clickAttributionWindow, viewAttributionWindow, leadAttributionEnabled, statisticType, sparkAuthCode,
    campaignName, objectiveType, campaignSubtype, campaignBudget, useCampaignLifetime, budgetStrategy,
    campaignStatus, campaignTags, adsetTags, adTags, adGroupName, adGroupStatus, adGroupBudgetMode, adGroupBudget,
    scheduleType, startTime, endTime, optimizationGoal, bidType, bidPrice, billingEvent, pixelID, optimizationEvent,
    audienceExcludeText, audienceIDsText, countries, ageGroups, gender, languages, minimumAge, interestsBehaviorText,
    runContinuously, targetRoas, promotionType, appID, adName, adStatus, adText, salesAdTexts, ctaMode,
    callToAction, landingPageURL, videoID, tikTokItemID, appliedSparkAuthCode, sparkPostIdentityID,
    catalogCreativeAssetTypes, videoSource, tikTokPostAdFormat, musicID,
  ]);
  const draftPayloadSignature = useMemo(() => JSON.stringify(buildDraftPayload()), [buildDraftPayload]);

  const createDraftEntityType = useMemo<'campaign' | 'adset' | 'ad'>(() => {
    if (step === 1) return 'campaign';
    if (step === 2) return 'adset';
    return 'ad';
  }, [step]);

  const applyDraftPayload = useCallback((draft: TikTokDraftPayload) => {
    if (draft.step === 1 || draft.step === 2 || draft.step === 3) setStep(draft.step);
    if (typeof draft.selectedAccountID === 'string') setSelectedAccountID(draft.selectedAccountID);
    if (typeof draft.selectedIdentityID === 'string') setSelectedIdentityID(draft.selectedIdentityID);
    if (typeof draft.catalogEnabled === 'boolean') setCatalogEnabled(draft.catalogEnabled);
    if (typeof draft.catalogID === 'string') setCatalogID(draft.catalogID);
    if (draft.productSelectionMode === 'ALL_PRODUCTS' || draft.productSelectionMode === 'PRODUCT_SET') setProductSelectionMode(draft.productSelectionMode);
    if (typeof draft.catalogSetID === 'string') setCatalogSetID(draft.catalogSetID);
    if (typeof draft.frequency === 'string') setFrequency(draft.frequency);
    if (typeof draft.frequencySchedule === 'string') setFrequencySchedule(draft.frequencySchedule);
    if (draft.reachFrequencyMode === 'TARGETED' || draft.reachFrequencyMode === 'CAP') setReachFrequencyMode(draft.reachFrequencyMode);
    if (typeof draft.reachTargetedFrequency === 'string') setReachTargetedFrequency(draft.reachTargetedFrequency);
    if (draft.reachCapMode === 'PRESET' || draft.reachCapMode === 'CUSTOM') setReachCapMode(draft.reachCapMode);
    if (typeof draft.reachCapPreset === 'string') setReachCapPreset(draft.reachCapPreset);
    if (typeof draft.reachCapCustomCount === 'string') setReachCapCustomCount(draft.reachCapCustomCount);
    if (typeof draft.reachCapCustomDays === 'string') setReachCapCustomDays(draft.reachCapCustomDays);
    if (draft.videoFrequencyMode === 'NONE' || draft.videoFrequencyMode === 'CUSTOM') setVideoFrequencyMode(draft.videoFrequencyMode);
    if (typeof draft.videoFrequencyCount === 'string') setVideoFrequencyCount(draft.videoFrequencyCount);
    if (typeof draft.videoFrequencyDays === 'string') setVideoFrequencyDays(draft.videoFrequencyDays);
    if (typeof draft.clickAttributionWindow === 'string') setClickAttributionWindow(draft.clickAttributionWindow);
    if (typeof draft.viewAttributionWindow === 'string') setViewAttributionWindow(draft.viewAttributionWindow);
    if (typeof draft.leadAttributionEnabled === 'boolean') setLeadAttributionEnabled(draft.leadAttributionEnabled);
    if (typeof draft.statisticType === 'string') setStatisticType(draft.statisticType);
    if (typeof draft.sparkAuthCode === 'string') setSparkAuthCode(draft.sparkAuthCode);
    if (typeof draft.campaignName === 'string') setCampaignName(draft.campaignName);
    if (typeof draft.objectiveType === 'string') setObjectiveType(draft.objectiveType);
    if (typeof draft.campaignSubtype === 'string') setCampaignSubtype(draft.campaignSubtype);
    if (typeof draft.campaignBudget === 'string') setCampaignBudget(draft.campaignBudget);
    if (typeof draft.useCampaignLifetime === 'boolean') setUseCampaignLifetime(draft.useCampaignLifetime);
    if (typeof draft.budgetStrategy === 'string') setBudgetStrategy(draft.budgetStrategy);
    if (typeof draft.campaignStatus === 'string') setCampaignStatus(draft.campaignStatus);
    if (draft.campaignTags && typeof draft.campaignTags === 'object') setCampaignTags(draft.campaignTags as Record<string, string>);
    if (draft.adsetTags && typeof draft.adsetTags === 'object') setAdsetTags(draft.adsetTags as Record<string, string>);
    if (draft.adTags && typeof draft.adTags === 'object') setAdTags(draft.adTags as Record<string, string>);
    if (typeof draft.adGroupName === 'string') setAdGroupName(draft.adGroupName);
    if (typeof draft.adGroupStatus === 'string') setAdGroupStatus(draft.adGroupStatus);
    if (typeof draft.adGroupBudgetMode === 'string') setAdGroupBudgetMode(draft.adGroupBudgetMode);
    if (typeof draft.adGroupBudget === 'string') setAdGroupBudget(draft.adGroupBudget);
    if (typeof draft.scheduleType === 'string') setScheduleType(draft.scheduleType);
    if (typeof draft.startTime === 'string') setStartTime(draft.startTime);
    if (typeof draft.endTime === 'string') setEndTime(draft.endTime);
    if (typeof draft.optimizationGoal === 'string') setOptimizationGoal(draft.optimizationGoal);
    if (typeof draft.bidType === 'string') setBidType(draft.bidType);
    if (typeof draft.bidPrice === 'string') setBidPrice(draft.bidPrice);
    if (typeof draft.billingEvent === 'string') setBillingEvent(draft.billingEvent);
    if (typeof draft.pixelID === 'string') setPixelID(draft.pixelID);
    if (typeof draft.optimizationEvent === 'string') setOptimizationEvent(draft.optimizationEvent);
    if (typeof draft.audienceExcludeText === 'string') setAudienceExcludeText(draft.audienceExcludeText);
    if (typeof draft.audienceIDsText === 'string') setAudienceIDsText(draft.audienceIDsText);
    if (Array.isArray(draft.countries)) setCountries(draft.countries as string[]);
    if (Array.isArray(draft.ageGroups)) setAgeGroups(draft.ageGroups as string[]);
    if (typeof draft.gender === 'string') setGender(draft.gender);
    if (Array.isArray(draft.languages)) setLanguages(draft.languages as string[]);
    if (typeof draft.minimumAge === 'string') setMinimumAge(draft.minimumAge);
    if (typeof draft.interestsBehaviorText === 'string') setInterestsBehaviorText(draft.interestsBehaviorText);
    if (typeof draft.runContinuously === 'boolean') setRunContinuously(draft.runContinuously);
    if (typeof draft.targetRoas === 'string') setTargetRoas(draft.targetRoas);
    if (typeof draft.promotionType === 'string') setPromotionType(draft.promotionType);
    if (typeof draft.appID === 'string') setAppID(draft.appID);
    if (typeof draft.adName === 'string') setAdName(draft.adName);
    if (typeof draft.adStatus === 'string') setAdStatus(draft.adStatus);
    if (typeof draft.adText === 'string') setAdText(draft.adText);
    if (Array.isArray(draft.salesAdTexts)) setSalesAdTexts(draft.salesAdTexts as string[]);
    if (draft.ctaMode === 'DYNAMIC' || draft.ctaMode === 'STANDARD') setCtaMode(draft.ctaMode);
    if (typeof draft.callToAction === 'string') setCallToAction(draft.callToAction);
    if (typeof draft.landingPageURL === 'string') setLandingPageURL(draft.landingPageURL);
    if (typeof draft.videoID === 'string') setVideoID(draft.videoID);
    if (typeof draft.tikTokItemID === 'string') setTikTokItemID(draft.tikTokItemID);
    if (typeof draft.appliedSparkAuthCode === 'string') setAppliedSparkAuthCode(draft.appliedSparkAuthCode);
    if (typeof draft.sparkPostIdentityID === 'string') setSparkPostIdentityID(draft.sparkPostIdentityID);
    if (Array.isArray(draft.catalogCreativeAssetTypes)) setCatalogCreativeAssetTypes(draft.catalogCreativeAssetTypes as string[]);
    if (draft.videoSource === 'TIKTOK_POST' || draft.videoSource === 'LIBRARY' || draft.videoSource === 'CATALOG_CREATIVE') setVideoSource(draft.videoSource);
    if (draft.tikTokPostAdFormat === 'SPARK_ADS' || draft.tikTokPostAdFormat === 'SINGLE_VIDEO') setTikTokPostAdFormat(draft.tikTokPostAdFormat);
    if (typeof draft.musicID === 'string') setMusicID(draft.musicID);
  }, []);

  const buildDraftRequest = useCallback((draft: TikTokDraftPayload): DraftPayloadRequest => ({
    platform: 'tiktok',
    scene: draftScene,
    entity_type: isEditMode ? draftEntityType : createDraftEntityType,
    entity_id: draftEntityID,
    draft_key: draftKey,
    draft_name: draftName,
    site_id: siteId || undefined,
    payload: draft,
  }), [createDraftEntityType, draftEntityID, draftEntityType, draftKey, draftName, draftScene, isEditMode, siteId]);

  const saveManualDraftSilently = useCallback(async (payloadOverride?: TikTokDraftPayload) => {
    if (!draftFeatureEnabled || !brandId || !draftLoadedRef.current || isEditMode) return;
    const payload = payloadOverride ?? buildDraftPayload();
    try {
      await accountsApi.saveManualDraft(brandId, buildDraftRequest(payload));
      lastTempDraftSignatureRef.current = JSON.stringify(payload);
      return true;
    } catch {
      return null;
    }
  }, [brandId, buildDraftPayload, buildDraftRequest, draftFeatureEnabled, isEditMode]);

  const saveTempDraftSilently = useCallback(async (payloadOverride?: TikTokDraftPayload, signatureOverride?: string) => {
    if (!draftFeatureEnabled || !brandId || !draftLoadedRef.current || !isEditMode) return;
    const payload = payloadOverride ?? buildDraftPayload();
    const signature = signatureOverride ?? JSON.stringify(payload);
    try {
      const resp = await accountsApi.saveTempDraft(brandId, buildDraftRequest(payload));
      lastTempDraftSignatureRef.current = signature;
      setLastDraftSavedAt(resp.updated_at || new Date().toISOString());
      setHasTempDraft(true);
      return resp;
    } catch {
      // ignore autosave errors
      return null;
    }
  }, [brandId, buildDraftPayload, buildDraftRequest, draftFeatureEnabled, isEditMode]);

  const clearTempDraftSilently = useCallback(async () => {
    if (!draftFeatureEnabled || !brandId) return;
    try {
      await accountsApi.deleteTempDraft({
        brand_id: brandId,
        site_id: siteId || undefined,
        platform: 'tiktok',
        scene: draftScene,
        entity_type: draftEntityType,
        entity_id: draftEntityID,
        draft_key: draftKey,
      });
    } catch {
      // ignore cleanup errors
    }
  }, [brandId, draftEntityID, draftEntityType, draftFeatureEnabled, draftKey, draftScene, siteId]);

  const handleCancelWithDraft = useCallback(async () => {
    if (draftFeatureEnabled) {
      if (isEditMode) await saveTempDraftSilently();
      else await saveManualDraftSilently();
    }
    onCancel();
  }, [draftFeatureEnabled, isEditMode, onCancel, saveManualDraftSilently, saveTempDraftSilently]);

  const handleDiscardDraft = useCallback(async () => {
    if (!draftFeatureEnabled || !brandId || !initialDraftSnapshotRef.current) return;
    const ok = await confirmDialog({
      title: '放弃修改',
      message: '将删除当前临时草稿，并回退到上次已保存的草稿内容；如果没有已保存草稿，则回到进入页面时的内容。是否继续？',
      confirmText: '放弃修改',
      danger: true,
    });
    if (!ok) return;
    await clearTempDraftSilently();
    const rollbackPayload = savedDraftSnapshotRef.current || initialDraftSnapshotRef.current;
    applyDraftPayload(rollbackPayload);
    lastTempDraftSignatureRef.current = JSON.stringify(rollbackPayload);
    setLastDraftSavedAt(null);
    setHasTempDraft(false);
    showToast('已放弃临时草稿', 'success');
  }, [applyDraftPayload, brandId, clearTempDraftSilently, draftFeatureEnabled, showToast]);

  useEffect(() => {
    if (!draftFeatureEnabled || loadingAccounts || editLoading || createUnderLoading) return;
    draftLoadedRef.current = false;
    lastTempDraftSignatureRef.current = '';
    setDraftReady(false);
    setLastDraftSavedAt(null);
    setHasTempDraft(false);
    setDraftBootstrapVersion((prev) => prev + 1);
  }, [createUnderLoading, draftFeatureEnabled, draftKey, editLoading, loadingAccounts]);

  useEffect(() => {
    if (!draftFeatureEnabled || !brandId || loadingAccounts || editLoading || createUnderLoading) return;
    if (draftBootstrapVersion <= 0 || draftLoadedRef.current) return;
    let cancelled = false;

    (async () => {
      const basePayload = buildDraftPayload();
      initialDraftSnapshotRef.current = basePayload;
      savedDraftSnapshotRef.current = basePayload;

      if (!isEditMode) {
        lastTempDraftSignatureRef.current = JSON.stringify(basePayload);
        draftLoadedRef.current = true;
        setDraftReady(true);
        setLastDraftSavedAt(null);
        setHasTempDraft(false);
        return;
      }

      if (isEditMode) {
        try {
          const tempDraft = await accountsApi.getTempDraft({
            brand_id: brandId,
            site_id: siteId || undefined,
            platform: 'tiktok',
            scene: draftScene,
            entity_type: draftEntityType,
            entity_id: draftEntityID,
            draft_key: draftKey,
          });
          if (!cancelled && tempDraft?.payload) {
            const payload = tempDraft.payload as TikTokDraftPayload;
            applyDraftPayload(payload);
            lastTempDraftSignatureRef.current = JSON.stringify(payload);
            draftLoadedRef.current = true;
            setDraftReady(true);
            setLastDraftSavedAt(tempDraft.updated_at || null);
            setHasTempDraft(true);
            if (isEditMode) showToast('已恢复临时草稿', 'info');
            return;
          }
        } catch {
          // ignore missing temp draft
        }
      }

      try {
        const manualDraft = await accountsApi.getManualDraft({
          brand_id: brandId,
          site_id: siteId || undefined,
          platform: 'tiktok',
          scene: draftScene,
          entity_type: draftEntityType,
          entity_id: draftEntityID,
          draft_key: draftKey,
        });
        if (!cancelled && manualDraft?.payload) {
          const payload = manualDraft.payload as TikTokDraftPayload;
          savedDraftSnapshotRef.current = payload;
          applyDraftPayload(payload);
          lastTempDraftSignatureRef.current = JSON.stringify(payload);
          draftLoadedRef.current = true;
          setDraftReady(true);
          setLastDraftSavedAt(null);
          setHasTempDraft(false);
          if (isEditMode) showToast('已恢复保存草稿', 'info');
          return;
        }
      } catch {
        // ignore missing manual draft
      }

      if (!cancelled) {
        lastTempDraftSignatureRef.current = JSON.stringify(basePayload);
        draftLoadedRef.current = true;
        setDraftReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyDraftPayload, brandId, buildDraftPayload, createUnderLoading, draftBootstrapVersion, draftEntityID, draftEntityType, draftFeatureEnabled, draftKey, draftScene, editLoading, loadingAccounts, showToast, siteId]);

  useEffect(() => {
    if (!draftFeatureEnabled || !brandId || !draftReady || !isEditMode) return;
    const timer = window.setInterval(() => {
      void saveTempDraftSilently();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [brandId, draftFeatureEnabled, draftReady, isEditMode, saveTempDraftSilently]);

  useEffect(() => {
    if (!draftFeatureEnabled || !brandId || !draftReady || !isEditMode) return;
    if (draftPayloadSignature === lastTempDraftSignatureRef.current) return;
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      void saveTempDraftSilently(undefined, draftPayloadSignature);
    }, 1500);
    return () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    };
  }, [brandId, draftFeatureEnabled, draftPayloadSignature, draftReady, isEditMode, saveTempDraftSilently]);

  const validateStep = (s: WizardStep) => {
    const errs: Record<string, string> = {};
    if (!selectedAccountID) errs.account = '请选择 TikTok 广告账户。';

    if (s === 1) {
      if (!campaignName.trim()) errs.campaignName = '广告系列名称不能为空。';
      if (showCampaignBudget && (!isPositive(campaignBudget) || Number(campaignBudget) < 20)) errs.campaignBudget = '广告系列预算不能低于 $20.00。';
      if (showAdGroupBudget && adGroupBudgetMode !== 'BUDGET_MODE_INFINITE' && (!isPositive(adGroupBudget) || Number(adGroupBudget) < 20)) errs.adGroupBudget = '广告组预算不能低于 $20.00。';
    }

    if (s === 2) {
      if (!adGroupName.trim()) errs.adGroupName = '广告组名称不能为空。';
      if (!showCampaignBudget && (!isPositive(adGroupBudget) || Number(adGroupBudget) < 20)) errs.adGroupBudget = '广告组预算不能低于 $20.00。';
      if (countries.length === 0) errs.countries = '请至少选择一个投放地区。';
      if (canEdit('adset.start_time') && !runContinuously && !startTime) errs.startTime = '开始时间不能为空。';
      if (canEdit('adset.catalog_id') && isSalesObjective && catalogEnabled && !catalogID.trim()) errs.catalogID = '启用商品库推广后必须选择商品库。';
      if (canEdit('adset.pixel_id') && (isLeadConvertGoal || isSalesObjective) && !pixelID.trim()) {
        errs.pixelID = '当前路径需要填写 Pixel。';
      }
      if (canEdit('adset.optimization_event') && requiresOptimizationEvent && !optimizationEvent.trim()) {
        errs.optimizationEvent = '请选择优化事件。';
      }
      if (canEdit('adset.bid_price') && (resolvedBidType === 'BID_TYPE_CUSTOM' || resolvedBidType === 'BID_TYPE_TARGET_COST') && !isPositive(bidPrice)) {
        errs.bidPrice = '当前出价策略需要填写大于 0 的出价金额。';
      }
      if (isVideoViewsObjective && videoFrequencyMode === 'CUSTOM') {
        if (!isPositive(videoFrequencyCount)) errs.frequency = '视频播放的频次上限次数必须大于 0。';
        if (!isPositive(videoFrequencyDays)) errs.frequencySchedule = '视频播放的频次上限天数必须大于 0。';
      }
    }

    if (s === 3) {
      if (!adName.trim()) errs.adName = '广告名称不能为空。';
      if (isTikTokPostCreative) {
        // TikTok/Spark post creatives reuse the original post caption.
      } else if (isSalesObjective) {
        const validSalesTexts = salesAdTexts.map((item) => item.trim()).filter(Boolean);
        if (validSalesTexts.length === 0) {
          errs.adText = '请至少填写一条广告文案。';
        }
      } else if (!adText.trim()) {
        errs.adText = '广告文案不能为空。';
      }
      if (visibleModules.includes('identity') && identityOptions.length > 0 && !selectedIdentityID) {
        errs.identity = '请选择一个已授权的 TikTok Identity。';
      }
      if (videoSource === 'TIKTOK_POST') {
        if ((isSmartPlusMode ? selectedTikTokItemIDs.length === 0 : !tikTokItemID.trim())) errs.tikTokItemID = '请至少填写一个 TikTok 内容 ID。';
      } else if (videoSource === 'CATALOG_CREATIVE') {
        if (!isSalesObjective || !catalogEnabled || !catalogID.trim()) {
          errs.catalogID = '使用商品库创意素材前，必须先启用商品库并选择商品库。';
        }
        if (catalogCreativeAssetTypes.length === 0) errs.catalogCreativeAssetTypes = '请至少勾选一种商品库创意素材类型。';
      } else {
        if ((isSmartPlusMode ? selectedVideoIDs.length === 0 : !videoID.trim())) errs.videoID = '请至少填写一个视频 ID。';
      }
      if (needsLandingPageURL && (isTrafficObjective || isLeadGenObjective || isSalesObjective) && !landingPageURL.trim()) {
        errs.landingPageURL = '目标页面网站 URL 不能为空。';
      }
        if (needsLandingPageURL && landingPageURL.trim() && !isHttpUrl(landingPageURL.trim())) {
          errs.landingPageURL = '落地页链接必须使用 http 或 https。';
        }
        if (isSalesObjective && catalogEnabled && productSelectionMode === 'PRODUCT_SET' && !catalogSetID.trim()) {
          errs.catalogSetID = '选择商品系列时必须指定商品系列。';
        }
      }

    setFieldErrors(errs);
    const first = Object.keys(errs)[0];
    if (first) {
      setError(errs[first]);
      setTimeout(() => scrollToField(first), 0);
      return false;
    }

    setError(null);
    return true;
  };

  const handleNext = async () => {
    if (isEditMode) {
      setError(null);
      setFieldErrors({});
    } else if (!validateStep(step)) {
      return;
    }
    if (draftFeatureEnabled && !isEditMode) {
      await saveManualDraftSilently();
    }
    setStep((s) => Math.min(3, s + 1) as WizardStep);
    scrollFormToTop();
  };

  const handleBack = () => {
    setError(null);
    setFieldErrors({});
    setStep((s) => Math.max(createUnder ? createUnderMinStep : 1, s - 1) as WizardStep);
    scrollFormToTop();
  };

  const handleValidate = async () => {
    if (!validateStep(1)) {
      setStep(1);
      scrollFormToTop();
      return;
    }
    if (!validateStep(2)) {
      setStep(2);
      scrollFormToTop();
      return;
    }
    if (!validateStep(3)) {
      setStep(3);
      scrollFormToTop();
      return;
    }
    setValidating(true);
    setError(null);
    try {
      const r = await accountsApi.validateAdFull(brandId, payload as unknown as ValidateAdFullRequest);
      if (!r.valid) {
        if (r.campaign_error) {
          setStep(1);
          setError(`广告系列校验失败：${r.campaign_error}`);
          scrollFormToTop();
        } else if (r.adset_error) {
          setStep(2);
          setError(`广告组校验失败：${r.adset_error}`);
          scrollFormToTop();
        } else if (r.creative_error) {
          setStep(3);
          setError(`广告校验失败：${r.creative_error}`);
          scrollFormToTop();
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'TikTok 校验请求失败。');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (userLevel < 2) {
      showToast(isEditMode ? '只读角色无法保存修改' : '只读角色无法创建广告');
      return;
    }
    const permCode = (isEditMode && editMode) ? 'ad-mgmt:campaigns:edit' : 'ad-mgmt:campaigns:create';
    if (!hasPermission(permCode)) {
      showToast(isEditMode ? '暂无编辑广告权限' : '暂无创建广告权限');
      return;
    }
    if (!isEditMode && !validateStep(step)) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isEditMode && editMode) {
        await handleEditSave();
      } else if (createUnder) {
        const reqPayload = { ...payload, brand_id: brandId, site_id: siteId } as TikTokCreateAdFullRequest & { brand_id: string; site_id?: string };
        if (createUnder.entity === 'campaign') {
          await accountsApi.createTikTokAdGroupUnder(createUnder.parentId, reqPayload);
        } else {
          await accountsApi.createTikTokAdUnder(createUnder.parentId, reqPayload);
        }
      } else {
        await accountsApi.createAdFull(brandId, payload as unknown as CreateAdFullRequest);
      }
      if (draftFeatureEnabled && isEditMode) await clearTempDraftSilently();
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : isEditMode ? '保存修改失败。' : '创建 TikTok 广告失败。');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAction = useCallback(async () => {
    if (!isEditMode && step === 3) {
      const shouldSave = await confirmDialog({
        title: '保存草稿',
        message: '关闭前是否保存当前广告数据为草稿？保存草稿不会校验表单，也不会创建或同步到 TikTok。',
        confirmText: '保存并关闭',
      });
      if (shouldSave) {
        await saveManualDraftSilently();
      }
      onCancel();
      return;
    }
    await handleCancelWithDraft();
  }, [handleCancelWithDraft, isEditMode, onCancel, saveManualDraftSilently, step]);

  const handleEditSave = async () => {
    if (!editMode) return;
    const needsManualPlacement = isVideoViewsObjective || isReachObjective;
    const resolvedPlacementType = needsManualPlacement ? 'PLACEMENT_TYPE_NORMAL' : 'PLACEMENT_TYPE_AUTOMATIC';
    const resolvedPlacements = needsManualPlacement && !isSalesObjective ? ['PLACEMENT_TIKTOK'] : [];
    const resolvedCampaignBudgetMode = campaignBudget
      ? (useCampaignLifetime ? 'BUDGET_MODE_TOTAL' : 'BUDGET_MODE_DAY')
      : 'BUDGET_MODE_INFINITE';
    const resolvedCampaignBudget = showCampaignBudget ? (Number(campaignBudget) || 0) : 0;
    const resolvedSalesAdTexts = salesAdTexts.map((item) => item.trim()).filter(Boolean);
    const resolvedAdText = isSalesObjective ? (resolvedSalesAdTexts[0] ?? '') : adText.trim();
    const campaignPayload: TikTokEditCampaignPayload = {
      name: campaignName.trim(),
      objective_type: objectiveConfig.apiObjectiveType ?? objectiveType,
      budget_strategy: budgetStrategy,
      budget_mode: resolvedCampaignBudgetMode,
      budget: resolvedCampaignBudget,
      operation_status: campaignStatus,
      special_industries: [],
      roas_bid: isSalesValueGoal && isPositive(targetRoas) ? Number(targetRoas) : undefined,
      tags: Object.keys(campaignTags).length > 0 ? campaignTags : undefined,
    };
    const adsetPayload: TikTokEditAdSetPayload = {
      name: adGroupName.trim(),
      adgroup_status: adGroupStatus,
      budget_mode: adGroupBudgetMode,
      budget: Number(adGroupBudget) || 0,
      schedule_type: runContinuously ? 'SCHEDULE_FROM_NOW' : scheduleType,
      start_time: runContinuously ? (startTime || '') : (scheduleType === 'SCHEDULE_START_END' ? startTime : ''),
      end_time: runContinuously ? '' : endTime,
      placement_type: resolvedPlacementType,
      placements: resolvedPlacements,
      optimization_goal: optimizationGoal,
      bid_type: resolvedBidType,
      bid_price: Number(bidPrice) || 0,
      billing_event: billingEvent,
      pixel_id: pixelID,
      optimization_event: pixelID.trim() ? optimizationEvent : '',
      audience_ids: audienceIDs,
      excluded_audience_ids: csvList(audienceExcludeText),
      location_ids: countries,
      age_groups: payloadAgeGroups,
      gender,
      languages,
      interest_category_ids: selectedInterestCategoryIDs.map(String),
      video_action_category_ids: selectedVideoActionCategoryIDs,
      interest_behavior_items: selectedInterestBehaviorItems,
      operating_systems: [],
      network_types: [],
      identity_type: selectedIdentity?.identity_type || '',
      identity_id: selectedIdentity?.identity_id || '',
      creative_material_mode: 'CUSTOM',
      app_id: appID,
      promotion_type: apiPromotionType || '',
      tags: Object.keys(adsetTags).length > 0 ? adsetTags : undefined,
    };
    const imageIdsArr: string[] = [];
    const isSparkAdsEdit = videoSource === 'TIKTOK_POST' && tikTokPostAdFormat === 'SPARK_ADS';
    const editVideoIDs = isSmartPlusMode && videoSource === 'LIBRARY' ? selectedVideoIDs : [];
    const editTikTokItemIDs = isSmartPlusMode && videoSource === 'TIKTOK_POST' ? selectedTikTokItemIDs : [];
    const adPayload: TikTokEditAdPayload = {
      name: adName.trim(),
      ad_status: adStatus,
      ad_format: adFormat,
      ad_text: resolvedAdText,
      call_to_action: (needsLandingPageURL && ctaMode === 'STANDARD') ? callToAction : '',
      landing_page_url: landingPageURL.trim(),
      video_id: videoSource === 'LIBRARY' ? (editVideoIDs[0] || videoID.trim()) : '',
      video_ids_json: JSON.stringify(editVideoIDs),
      image_ids_json: JSON.stringify(imageIdsArr),
      display_name: isSalesObjective
        ? selectedIdentity?.display_name || selectedIdentity?.username || selectedIdentity?.identity_id || ''
        : '',
      identity_type: (isSparkAdsEdit && appliedSparkAuthCode) ? 'AUTH_CODE' : (selectedIdentity?.identity_type || ''),
      identity_id: (isSparkAdsEdit && sparkPostIdentityID) ? sparkPostIdentityID : (selectedIdentity?.identity_id || ''),
      identity_authorized_bc_id: (isSparkAdsEdit && appliedSparkAuthCode) ? '' : (selectedIdentity?.identity_authorized_bc_id || ''),
      tiktok_item_id: videoSource === 'TIKTOK_POST' ? (editTikTokItemIDs[0] || tikTokItemID.trim()) : '',
      tiktok_item_ids_json: JSON.stringify(editTikTokItemIDs),
      video_cover_url: videoSource === 'LIBRARY' ? selectedLibraryCreative?.cover_url || '' : '',
      post_cover_url: videoSource === 'TIKTOK_POST' ? selectedSparkPost?.cover_url || '' : '',
      post_caption: videoSource === 'TIKTOK_POST' ? selectedSparkPost?.caption || '' : '',
      tags: Object.keys(adTags).length > 0 ? adTags : undefined,
    };
    if (editMode.entity === 'campaign') {
      await accountsApi.updateTikTokCampaign(editMode.id, {
        brand_id: brandId,
        site_id: siteId,
        campaign: campaignPayload,
        adset: budgetStrategy === 'ADGROUP_BUDGET' ? adsetPayload : undefined,
      });
    } else if (editMode.entity === 'adset') {
      await accountsApi.updateTikTokAdSet(editMode.id, { brand_id: brandId, site_id: siteId, campaign: campaignPayload, adset: adsetPayload });
    } else {
      await accountsApi.updateTikTokAd(editMode.id, { brand_id: brandId, site_id: siteId, adset: adsetPayload, ad: adPayload });
    }
  };

  if (editLoading || createUnderLoading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{createUnder ? '正在加载父级数据...' : '正在加载编辑数据...'}</div>;
  }

  return (
    <div ref={formCardRef}>
      {createUnder && (
        <h2 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
          新增 TikTok {createUnder.entity === 'campaign' ? '广告组' : '广告'}
        </h2>
      )}
      <HorizontalStepBar step={step} minStep={createUnder ? createUnderMinStep : undefined} />

      {error && (
        <WizardErrorBanner
          title={isEditMode ? '保存失败' : 'TikTok 参数错误'}
          message={error}
          hint="请修复高亮字段或调整当前步骤配置后再继续。"
          bannerRef={errorRef}
        />
      )}

      {step === 1 && (
        <div>
          <SectionCard title="账号与名称">
            {loadingAccounts ? (
              <div style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 12 }}>正在加载账户列表...</div>
            ) : accounts.length === 0 ? (
              <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>该品牌下暂无可用的 TikTok 广告账户。</div>
            ) : (
              <FormGroup>
                <Label required>授权账户</Label>
                <div ref={setFieldRef('account')}>
                  <MultiSelect
                    single
                    options={accounts.map((a) => ({ value: a.id, label: a.account_name || a.account_id }))}
                    value={selectedAccountID ? [selectedAccountID] : []}
                    onChange={(vals) => setSelectedAccountID(vals[0] ?? '')}
                    placeholder="— 请选择 TikTok 授权账户 —"
                    style={{ width: '100%', ...(isEditMode ? { pointerEvents: 'none', opacity: 0.75 } : {}) }}
                  />
                </div>
              </FormGroup>
            )}

            <FormGroup>
              <Label required>广告系列名称</Label>
              <input
                ref={setFieldRef('campaignName') as React.Ref<HTMLInputElement>}
                className="form-input"
                value={campaignName}
                placeholder="例：TikTok_Glasses_US_Q2"
                disabled={!canEdit('campaign.name')}
                onChange={(e) => {
                  setCampaignName(e.target.value);
                  setFieldErrors((p) => ({ ...p, campaignName: '' }));
                }}
                style={{ borderColor: fieldErrors.campaignName ? 'var(--red)' : undefined }}
              />
              {fieldErrors.campaignName && <div style={errStyle}>{fieldErrors.campaignName}</div>}
            </FormGroup>
          </SectionCard>

          <SectionCard title="广告系列基础">
            <FormGroup>
              <Label required>营销目标</Label>
              <div style={{ display: 'grid', gap: 16 }}>
                {TIKTOK_OBJECTIVE_GROUPS.map((group) => (
                  <div key={group.key}>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>{group.label}</div>
                    <Cards
                      name={`tt-objective-${group.key}`}
                      value={objectiveType}
                      onChange={setObjectiveType}
                      disabled={!canEdit('campaign.objective_type')}
                      options={group.objectives.map((objective) => ({
                        value: objective,
                        label: TIKTOK_OBJECTIVE_CONFIG[objective].label,
                      }))}
                    />
                  </div>
                ))}
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--surface2)' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>{objectiveConfig.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{objectiveConfig.description}</div>
                  {objectiveConfig.notes && objectiveConfig.notes.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text3)' }}>
                      {objectiveConfig.notes.join(' ')}
                    </div>
                  )}
                </div>
              </div>
            </FormGroup>

            {subtypeOptions.length > 0 && (
              <FormGroup>
                <Label>{isSalesObjective ? '销售目标页面' : '推广系列设置'}</Label>
                <Cards
                  name="tt-campaign-subtype"
                  value={campaignSubtype}
                  onChange={setCampaignSubtype}
                  options={subtypeOptions}
                />
                <Hint>{subtypeConfig?.description}</Hint>
              </FormGroup>
            )}

            {isVideoViewsObjective && (
              <FormGroup>
                <Label>选择此推广目标的作用</Label>
                <div style={{ display: 'grid', gap: 8, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface2)' }}>
                  {VIDEO_VIEW_BENEFITS.map((item) => (
                    <div key={item} style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
                      {item}
                    </div>
                  ))}
                </div>
              </FormGroup>
            )}

          </SectionCard>

          {isSalesObjective && (
            <SectionCard title="销量目标设置">
              <FormGroup>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={catalogEnabled}
                    onChange={(e) => canEdit('campaign.catalog_enabled') && setCatalogEnabled(e.target.checked)}
                    disabled={!canEdit('campaign.catalog_enabled')}
                    style={{ accentColor: 'var(--primary)' }}
                  />
                  启用商品库推广
                </label>
                <Hint>启用后可在广告组层级选择商品库，系统将自动推广商品库中的商品。</Hint>
              </FormGroup>
            </SectionCard>
          )}

          <SectionCard title="广告系列预算与状态">

            <FormGroup>
              <Label>预算设置</Label>
              <div style={{ display: 'grid', gap: 10 }}>
                {!isSalesObjective && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: canEdit('campaign.budget_strategy') ? 'pointer' : 'not-allowed', opacity: canEdit('campaign.budget_strategy') ? 1 : 0.75 }}>
                    <input type="radio" name="tt-budget-strategy" checked={budgetStrategy === 'NONE'} disabled={!canEdit('campaign.budget_strategy')} onChange={() => setBudgetStrategy('NONE')} style={{ accentColor: 'var(--primary)' }} />
                    不设置预算
                  </label>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: canEdit('campaign.budget_strategy') ? 'pointer' : 'not-allowed', opacity: canEdit('campaign.budget_strategy') ? 1 : 0.75 }}>
                  <input type="radio" name="tt-budget-strategy" checked={budgetStrategy === 'CAMPAIGN_BUDGET'} disabled={!canEdit('campaign.budget_strategy')} onChange={() => setBudgetStrategy('CAMPAIGN_BUDGET')} style={{ accentColor: 'var(--primary)' }} />
                  设置推广系列预算
                </label>
                {!isSalesObjective && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: canEdit('campaign.budget_strategy') ? 'pointer' : 'not-allowed', opacity: canEdit('campaign.budget_strategy') ? 1 : 0.75 }}>
                    <input type="radio" name="tt-budget-strategy" checked={budgetStrategy === 'CAMPAIGN_BUDGET_OPTIMIZE'} disabled={!canEdit('campaign.budget_strategy')} onChange={() => setBudgetStrategy('CAMPAIGN_BUDGET_OPTIMIZE')} style={{ accentColor: 'var(--primary)' }} />
                    推广系列预算优化
                  </label>
                )}
                {isSalesObjective && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: canEdit('campaign.budget_strategy') ? 'pointer' : 'not-allowed', opacity: canEdit('campaign.budget_strategy') ? 1 : 0.75 }}>
                    <input type="radio" name="tt-budget-strategy" checked={budgetStrategy === 'ADGROUP_BUDGET'} disabled={!canEdit('campaign.budget_strategy')} onChange={() => setBudgetStrategy('ADGROUP_BUDGET')} style={{ accentColor: 'var(--primary)' }} />
                    设置广告组预算
                  </label>
                )}
              </div>
            </FormGroup>

            {showCampaignBudget && (
              <FormGroup>
                <Label>推广系列预算</Label>
                <div style={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr) 72px', gap: 8 }}>
                  <MultiSelect
                    single
                    options={[{ value: 'DAY', label: '日预算' }, { value: 'TOTAL', label: '总预算' }]}
                    value={useCampaignLifetime ? ['TOTAL'] : ['DAY']}
                    onChange={(vals) => setUseCampaignLifetime(vals[0] === 'TOTAL')}
                    style={!canEdit('campaign.budget_mode') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                  />
                  <input
                    ref={setFieldRef('campaignBudget') as React.Ref<HTMLInputElement>}
                    type="number"
                    min="20"
                    step="0.01"
                    className="form-input"
                    value={campaignBudget}
                    disabled={!canEdit('campaign.budget')}
                    onChange={(e) => {
                      setCampaignBudget(normalizeMoneyInput(e.target.value));
                      setFieldErrors((p) => ({ ...p, campaignBudget: '' }));
                    }}
                    placeholder="20.00 以上"
                    style={{ borderColor: fieldErrors.campaignBudget ? 'var(--red)' : undefined }}
                  />
                  <div className="form-input" style={{ display: 'grid', placeItems: 'center', color: 'var(--text2)', background: 'var(--surface2)' }}>USD</div>
                </div>
                {fieldErrors.campaignBudget && <div style={errStyle}>{fieldErrors.campaignBudget}</div>}
              </FormGroup>
            )}

            {showAdGroupBudget && (
              <FormGroup>
                <Label>广告组预算</Label>
                <div style={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr) 72px', gap: 8 }}>
                  <MultiSelect
                    single
                    options={[{ value: 'BUDGET_MODE_DAY', label: '日预算' }, { value: 'BUDGET_MODE_TOTAL', label: '总预算' }, { value: 'BUDGET_MODE_INFINITE', label: '不限' }]}
                    value={adGroupBudgetMode ? [adGroupBudgetMode] : []}
                    onChange={(vals) => setAdGroupBudgetMode(vals[0] ?? '')}
                    style={!canEdit('adset.budget_mode') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                  />
                  {adGroupBudgetMode !== 'BUDGET_MODE_INFINITE' && (
                    <input
                      ref={setFieldRef('adGroupBudget') as React.Ref<HTMLInputElement>}
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-input"
                      value={adGroupBudget}
                      disabled={!canEdit('adset.budget')}
                      onChange={(e) => {
                        setAdGroupBudget(normalizeMoneyInput(e.target.value));
                        setFieldErrors((p) => ({ ...p, adGroupBudget: '' }));
                      }}
                      placeholder="预算金额"
                      style={{ borderColor: fieldErrors.adGroupBudget ? 'var(--red)' : undefined }}
                    />
                  )}
                  {adGroupBudgetMode !== 'BUDGET_MODE_INFINITE' && (
                    <div className="form-input" style={{ display: 'grid', placeItems: 'center', color: 'var(--text2)', background: 'var(--surface2)' }}>USD</div>
                  )}
                </div>
                {fieldErrors.adGroupBudget && <div style={errStyle}>{fieldErrors.adGroupBudget}</div>}
              </FormGroup>
            )}

            <FormGroup>
              <Label>广告系列状态</Label>
              <MultiSelect
                single
                options={[{ value: 'DISABLE', label: '暂停' }, { value: 'ENABLE', label: '启用' }]}
                value={campaignStatus ? [campaignStatus] : []}
                onChange={(vals) => setCampaignStatus(vals[0] ?? '')}
                style={!canEdit('campaign.operation_status') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
              />
            </FormGroup>
          </SectionCard>

          {/* ── 动态广告标签（campaign 级）────────────────────────── */}
          {tagConfig.filter(t => t.level === 'campaign').length > 0 && (
            <SectionCard title="广告标签">
              <div className="tag-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 10px' }}>
                {tagConfig.filter(t => t.level === 'campaign').map(td => {
                  const flatOpts = td.options.flatMap(o =>
                    o.children && o.children.length > 0
                      ? o.children.map(c => ({ value: c.value, label: `${o.label} · ${c.label}` }))
                      : [{ value: o.value, label: o.label }]
                  );
                  return (
                    <div key={td.key}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{td.label}</div>
                      <MultiSelect single placeholder="请选择"
                        options={flatOpts}
                        value={campaignTags[td.key] ? [campaignTags[td.key]] : []}
                        onChange={(vals) => setCampaignTags(prev => ({ ...prev, [td.key]: vals[0] ?? '' }))}
                      />
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {step === 2 && (
        <div>
          <SectionCard title="广告组基础">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label required>广告组名称</Label>
                <input
                  ref={setFieldRef('adGroupName') as React.Ref<HTMLInputElement>}
                  className="form-input"
                  value={adGroupName}
                  disabled={!canEdit('adset.name')}
                  onChange={(e) => {
                    setAdGroupName(e.target.value);
                    setFieldErrors((p) => ({ ...p, adGroupName: '' }));
                  }}
                  style={{ borderColor: fieldErrors.adGroupName ? 'var(--red)' : undefined }}
                />
                {fieldErrors.adGroupName && <div style={errStyle}>{fieldErrors.adGroupName}</div>}
              </FormGroup>

              <FormGroup>
                <Label>状态</Label>
                <MultiSelect
                  single
                  options={[{ value: 'DISABLE', label: '暂停' }, { value: 'ENABLE', label: '启用' }]}
                  value={adGroupStatus ? [adGroupStatus] : []}
                  onChange={(vals) => setAdGroupStatus(vals[0] ?? '')}
                  style={!canEdit('adset.adgroup_status') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                />
              </FormGroup>
            </div>
          </SectionCard>

          {/* 商品库 — 仅销量目标且开启商品库推广时显示 */}
          {isSalesObjective && catalogEnabled && (
            <SectionCard title="商品库">
              <FormGroup>
                <Label required>商品库</Label>
                {catalogLoading ? (
                  <div className="form-input" style={{ color: 'var(--text3)', background: 'var(--surface2)' }}>
                    正在获取商品库...
                  </div>
                ) : catalogList.length > 0 ? (
                  <MultiSelect
                    single
                    options={catalogList.map((cat) => ({
                      value: cat.catalog_id,
                      label: `${cat.catalog_name} (${cat.catalog_id})${cat.country_code ? ` · ${cat.country_code}` : cat.country ? ` · ${cat.country}` : ''}`,
                    }))}
                    value={catalogID ? [catalogID] : []}
                    onChange={(vals) => {
                      if (!canEdit('adset.catalog_id')) return;
                      setCatalogID(vals[0] ?? '');
                      setCatalogSetID('');
                      setFieldErrors((p) => ({ ...p, catalogID: '' }));
                    }}
                    placeholder="-- 请选择商品库 --"
                    style={{ borderColor: fieldErrors.catalogID ? 'var(--red)' : undefined, ...(!canEdit('adset.catalog_id') ? { pointerEvents: 'none', opacity: 0.75 } : {}) }}
                  />
                ) : (
                  <div
                    ref={setFieldRef('catalogID') as React.Ref<HTMLDivElement>}
                    className="form-input"
                    style={{ color: fieldErrors.catalogID ? 'var(--red)' : 'var(--text3)', background: 'var(--surface2)' }}
                  >
                    {catalogError || '暂无可用商品库，请先确认当前 TikTok 广告账户下已创建并授权商品库。'}
                  </div>
                )}
                {fieldErrors.catalogID && <div style={errStyle}>{fieldErrors.catalogID}</div>}
                {!catalogLoading && catalogError && !fieldErrors.catalogID && <div style={errStyle}>{catalogError}</div>}
              </FormGroup>
            </SectionCard>
          )}


          {/* 受众定向 — 公共模块 */}
          <SectionCard title="受众定向">
            <div style={{ display: 'grid', gap: 16 }}>
              <FormGroup>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <Label required>投放地区</Label>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCountries([])}>清空</button>
                </div>
                <SearchableMultiSelect options={regionOptions} selected={countries} onChange={(v) => { setCountries(v); setFieldErrors((p) => ({ ...p, countries: '' })); }} placeholder="搜索国家/地区..." />
                {fieldErrors.countries && <div style={errStyle}>{fieldErrors.countries}</div>}
              </FormGroup>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <FormGroup>
                  <Label>最低年龄</Label>
                  <MultiSelect
                    single
                    options={MIN_AGE_OPTIONS}
                    value={minimumAge ? [minimumAge] : []}
                    onChange={(vals) => {
                      const next = vals[0] ?? '18';
                      setMinimumAge(next);
                      if (next === '18') {
                        setAgeGroups((prev) => prev.filter((value) => value !== 'AGE_13_17'));
                      }
                    }}
                    style={!canEdit('adset.age_groups') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>性别</Label>
                  <MultiSelect
                    single
                    options={[{ value: 'GENDER_UNLIMITED', label: '不限' }, { value: 'GENDER_MALE', label: '男性' }, { value: 'GENDER_FEMALE', label: '女性' }]}
                    value={gender ? [gender] : []}
                    onChange={(vals) => setGender(vals[0] ?? '')}
                    style={!canEdit('adset.gender') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>语言</Label>
                  <TikTokMultiSelect
                    options={LANGUAGE_OPTIONS}
                    selected={languages}
                    onChange={setLanguages}
                    placeholder="不限"
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <Label>年龄</Label>
                <Chips options={ageOptions} selected={ageGroups} onChange={handleAgeGroupsChange} />
              </FormGroup>

              <FormGroup>
                <Label>排除受众</Label>
                {audienceList.length > 0 ? (
                  <TikTokMultiSelect
                    options={audienceList.map((a) => ({ value: a.audience_id, label: `${a.audience_name} (${a.audience_id})` }))}
                    selected={audienceExcludeText ? audienceExcludeText.split(',').filter(Boolean) : []}
                    onChange={(ids) => setAudienceExcludeText(ids.join(','))}
                    placeholder="请选择排除受众"
                  />
                ) : (
                  <input
                    className="form-input"
                    value={audienceExcludeText}
                    onChange={(e) => setAudienceExcludeText(e.target.value)}
                    placeholder="多个 audience_id 用逗号分隔"
                  />
                )}
              </FormGroup>

              <FormGroup>
                <Label>包含受众</Label>
                {audienceList.length > 0 ? (
                  <TikTokMultiSelect
                    options={audienceList.map((a) => ({ value: a.audience_id, label: `${a.audience_name} (${a.audience_id})` }))}
                    selected={audienceIDs}
                    onChange={(ids) => setAudienceIDsText(ids.join(','))}
                    placeholder="请选择包含受众"
                  />
                ) : (
                  <input
                    className="form-input"
                    value={audienceIDsText}
                    onChange={(e) => setAudienceIDsText(e.target.value)}
                    placeholder="多个 audience_id 用逗号分隔"
                  />
                )}
              </FormGroup>

              <FormGroup>
                <Label>兴趣与行为</Label>
                <TikTokInterestBehaviorPicker
                  items={interestSearchResults}
                  lookupItems={knownTargetingItems}
                  loading={interestSearchLoading}
                  selected={interestsBehaviorText ? interestsBehaviorText.split(',').filter(Boolean) : []}
                  onChange={(ids) => setInterestsBehaviorText(ids.join(','))}
                  onSearchChange={setInterestSearchKeyword}
                />
              </FormGroup>
            </div>
          </SectionCard>

          {/* 广告位固定 TikTok，不渲染选择器 */}

          {/* 预算与排期 — 公共模块 */}
          <SectionCard title="预算与排期">
            {!showCampaignBudget && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <FormGroup>
                  <Label>预算类型</Label>
                  <MultiSelect
                    single
                    options={[{ value: 'BUDGET_MODE_DAY', label: '日预算' }, { value: 'BUDGET_MODE_TOTAL', label: '总预算' }]}
                    value={adGroupBudgetMode ? [adGroupBudgetMode] : []}
                    onChange={(vals) => setAdGroupBudgetMode(vals[0] ?? '')}
                    style={!canEdit('adset.budget_mode') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                  />
                </FormGroup>

                <FormGroup>
                  <Label required>预算金额（USD）</Label>
                  <input
                    ref={setFieldRef('adGroupBudget') as React.Ref<HTMLInputElement>}
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-input"
                    value={adGroupBudget}
                    disabled={!canEdit('adset.budget')}
                    onChange={(e) => {
                      setAdGroupBudget(normalizeMoneyInput(e.target.value));
                      setFieldErrors((p) => ({ ...p, adGroupBudget: '' }));
                    }}
                    style={{ borderColor: fieldErrors.adGroupBudget ? 'var(--red)' : undefined }}
                  />
                  {fieldErrors.adGroupBudget && <div style={errStyle}>{fieldErrors.adGroupBudget}</div>}
                </FormGroup>
              </div>
            )}
            {showCampaignBudget && (
              <FormGroup>
                <Label>预算</Label>
                <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.8 }}>
                  {campaignBudgetSummary}
                </div>
              </FormGroup>
            )}

            <div style={{ display: 'grid', gap: 12 }}>
              <FormGroup>
                <Label>排期</Label>
                <div style={{ display: 'grid', gap: 10 }}>
                  <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="radio"
                      checked={runContinuously}
                      onChange={() => canEdit('adset.schedule_type') && setRunContinuously(true)}
                      disabled={!canEdit('adset.schedule_type')}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    广告组持续投放
                  </label>
                  <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="radio"
                      checked={!runContinuously}
                      onChange={() => {
                        if (!canEdit('adset.schedule_type')) return;
                        setRunContinuously(false);
                        setScheduleType('SCHEDULE_START_END');
                      }}
                      disabled={!canEdit('adset.schedule_type')}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    设置开始和结束时间
                  </label>
                </div>
              </FormGroup>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <FormGroup>
                  <Label required>计划开始时间</Label>
                  <div ref={setFieldRef('startTime')}>
                    <DateTimePicker
                      value={startTime}
                      onChange={(next) => {
                        setStartTime(next);
                        setFieldErrors((p) => ({ ...p, startTime: '' }));
                      }}
                      placeholder="选择开始时间"
                      disabled={!canEdit('adset.start_time')}
                    />
                  </div>
                  {fieldErrors.startTime && <div style={errStyle}>{fieldErrors.startTime}</div>}
                </FormGroup>

                {!runContinuously && (
                  <FormGroup>
                  <Label>结束时间</Label>
                    <DateTimePicker value={endTime} onChange={setEndTime} placeholder="选择结束时间" disabled={!canEdit('adset.end_time')} />
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>时区</Label>
                  <input className="form-input" value="(UTC+08:00) China Standard Time (China)" readOnly />
                </FormGroup>
              </div>

            </div>
          </SectionCard>

          {/* ===== 差异模块：覆盖人数 ===== */}
          {isReachObjective && (
            <SectionCard title="出价与优化">
              <FormGroup>
                <Label>优化目标</Label>
                <input className="form-input" value="覆盖人群" readOnly />
              </FormGroup>
              <FormGroup>
                <Label>频率</Label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setReachFrequencyMode('TARGETED')}
                    style={{
                      borderColor: reachFrequencyMode === 'TARGETED' ? 'var(--primary)' : undefined,
                      color: reachFrequencyMode === 'TARGETED' ? 'var(--primary)' : undefined,
                      background: reachFrequencyMode === 'TARGETED' ? 'var(--primary-bg)' : undefined,
                    }}
                  >
                    定向频率
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setReachFrequencyMode('CAP')}
                    style={{
                      borderColor: reachFrequencyMode === 'CAP' ? 'var(--primary)' : undefined,
                      color: reachFrequencyMode === 'CAP' ? 'var(--primary)' : undefined,
                      background: reachFrequencyMode === 'CAP' ? 'var(--primary-bg)' : undefined,
                    }}
                  >
                    频次上限
                  </button>
                </div>
              </FormGroup>

              {reachFrequencyMode === 'TARGETED' ? (
                <FormGroup>
                  <Label>定向频率</Label>
                  <MultiSelect
                    single
                    options={REACH_TARGETED_FREQUENCY_OPTIONS}
                    value={reachTargetedFrequency ? [reachTargetedFrequency] : []}
                    onChange={(vals) => setReachTargetedFrequency(vals[0] ?? '')}
                  />
                  <Hint>我们将针对你选择的频率目标进行优化。然而，实际频率可能会有所变化，无法完全保证投放频率。</Hint>
                </FormGroup>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  <FormGroup>
                    <Label>频次上限</Label>
                    <div style={{ display: 'grid', gap: 10 }}>
                      {REACH_FREQUENCY_CAP_PRESETS.map((item) => (
                        <label
                          key={item.value}
                          className="form-label"
                          style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                        >
                          <input
                            type="radio"
                            checked={reachCapMode === 'PRESET' && reachCapPreset === item.value}
                            onChange={() => {
                              setReachCapMode('PRESET');
                              setReachCapPreset(item.value);
                            }}
                            style={{ accentColor: 'var(--primary)' }}
                          />
                          {item.label}
                        </label>
                      ))}
                      <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="radio"
                          checked={reachCapMode === 'CUSTOM'}
                          onChange={() => setReachCapMode('CUSTOM')}
                          style={{ accentColor: 'var(--primary)' }}
                        />
                        自定义频次上限
                      </label>
                    </div>
                  </FormGroup>

                  {reachCapMode === 'CUSTOM' && (
                    <FormGroup>
                      <Label>频次上限</Label>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr 1fr' : '120px auto 120px auto',
                          gap: 12,
                          alignItems: 'center',
                          padding: 16,
                          border: '1px solid var(--border)',
                          borderRadius: 10,
                          background: 'var(--surface2)',
                        }}
                      >
                        <input
                          type="number"
                          min="1"
                          className="form-input"
                          value={reachCapCustomCount}
                          onChange={(e) => setReachCapCustomCount(e.target.value)}
                        />
                        <span style={{ fontSize: 14, color: 'var(--text2)' }}>次展示 /</span>
                        <input
                          type="number"
                          min="1"
                          className="form-input"
                          value={reachCapCustomDays}
                          onChange={(e) => setReachCapCustomDays(e.target.value)}
                        />
                        <span style={{ fontSize: 14, color: 'var(--text2)' }}>天</span>
                      </div>
                    </FormGroup>
                  )}
                </div>
              )}

              <FormGroup>
                <Label>出价控制（USD / 千次展示）</Label>
                <input
                  ref={setFieldRef('bidPrice') as React.Ref<HTMLInputElement>}
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-input"
                  value={bidPrice}
                  disabled={!canEdit('adset.bid_price')}
                  onChange={(e) => {
                    setBidPrice(normalizeMoneyInput(e.target.value));
                    setFieldErrors((p) => ({ ...p, bidPrice: '' }));
                  }}
                  style={{ borderColor: fieldErrors.bidPrice ? 'var(--red)' : undefined }}
                  placeholder="留空则自动出价"
                />
                {fieldErrors.bidPrice && <div style={errStyle}>{fieldErrors.bidPrice}</div>}
              </FormGroup>
              <FormGroup>
                <Label>计费事件</Label>
                <input className="form-input" value="CPM" readOnly />
              </FormGroup>
            </SectionCard>
          )}

          {/* ===== 差异模块：访问量 ===== */}
          {isTrafficObjective && (
            <>
              <SectionCard title="优化位置">
                <FormGroup>
                  <Label>访问量优化位置</Label>
                  <Cards
                    name="tt-traffic-destination"
                    value={promotionType}
                    onChange={setPromotionType}
                    disabled={!canEdit('adset.promotion_type')}
                    options={[
                      { value: 'WEBSITE', label: '落地页' },
                      { value: 'APP_ANDROID', label: '应用' },
                    ]}
                  />
                </FormGroup>
                {isTrafficAppPath && (
                  <FormGroup>
                    <Label>应用 ID</Label>
                    <div className="form-input" style={{ color: 'var(--text2)', background: 'var(--surface2)' }}>
                      使用系统配置的 TikTok 推广 App ID
                    </div>
                  </FormGroup>
                )}
              </SectionCard>

              <SectionCard title="出价与优化">
                {isTrafficAppPath ? (
                  <>
                    <FormGroup>
                      <Label>优化目标</Label>
                      <input className="form-input" value="点击" readOnly />
                    </FormGroup>
                    <FormGroup>
                      <Label required>目标 CPA（USD / 点击）</Label>
                      <input
                        ref={setFieldRef('bidPrice') as React.Ref<HTMLInputElement>}
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-input"
                        value={bidPrice}
                        disabled={!canEdit('adset.bid_price')}
                        onChange={(e) => {
                          setBidPrice(normalizeMoneyInput(e.target.value));
                          setFieldErrors((p) => ({ ...p, bidPrice: '' }));
                        }}
                        style={{ borderColor: fieldErrors.bidPrice ? 'var(--red)' : undefined }}
                      />
                      {fieldErrors.bidPrice && <div style={errStyle}>{fieldErrors.bidPrice}</div>}
                    </FormGroup>
                    <FormGroup>
                      <Label>计费事件</Label>
                      <input className="form-input" value="CPC" readOnly />
                    </FormGroup>
                  </>
                ) : (
                  <>
                    <FormGroup>
                      <Label>优化目标</Label>
                      <Cards
                        name="tt-traffic-goal"
                        value={optimizationGoal}
                        onChange={setOptimizationGoal}
                        disabled={!canEdit('adset.optimization_goal')}
                        options={[
                          { value: 'CLICK', label: '点击' },
                          { value: 'TRAFFIC_LANDING_PAGE_VIEW', label: '落地页浏览量' },
                          { value: 'ENGAGED_VIEW', label: '深度互动会话' },
                        ]}
                      />
                    </FormGroup>

                    {optimizationGoal === 'CLICK' ? (
                      <>
                        <FormGroup>
                          <Label required>目标 CPA（USD / 点击）</Label>
                          <input
                            ref={setFieldRef('bidPrice') as React.Ref<HTMLInputElement>}
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-input"
                            value={bidPrice}
                            disabled={!canEdit('adset.bid_price')}
                            onChange={(e) => {
                              setBidPrice(normalizeMoneyInput(e.target.value));
                              setFieldErrors((p) => ({ ...p, bidPrice: '' }));
                            }}
                            style={{ borderColor: fieldErrors.bidPrice ? 'var(--red)' : undefined }}
                          />
                          {fieldErrors.bidPrice && <div style={errStyle}>{fieldErrors.bidPrice}</div>}
                        </FormGroup>
                        <FormGroup>
                          <Label>计费事件</Label>
                          <input className="form-input" value="CPC" readOnly />
                        </FormGroup>
                      </>
                    ) : (
                      <>
                        <FormGroup>
                          <Label required={requiresPixelForAdGroup}>Pixel（数据连接）</Label>
                            <MultiSelect
                              single
                              options={pixelList.map((px) => ({ value: px.pixel_id, label: `${px.pixel_name} (${px.pixel_id})` }))}
                              value={pixelID ? [pixelID] : []}
                              onChange={(vals) => {
                                applyPixelSelection(vals[0] ?? '');
                              }}
                              disabled={!canEdit('adset.pixel_id')}
                              placeholder="-- 请选择 Pixel --"
                              style={{ borderColor: fieldErrors.pixelID ? 'var(--red)' : undefined }}
                            />
                          {fieldErrors.pixelID && <div style={errStyle}>{fieldErrors.pixelID}</div>}
                        </FormGroup>
                        <FormGroup>
                          <Label required={requiresOptimizationEvent}>优化事件</Label>
                          {isTrafficObjective && (optimizationGoal === 'TRAFFIC_LANDING_PAGE_VIEW' || optimizationGoal === 'ENGAGED_VIEW') ? (
                            <input
                              ref={setFieldRef('optimizationEvent') as React.Ref<HTMLInputElement>}
                              className="form-input"
                              readOnly
                              value={optimizationGoal === 'ENGAGED_VIEW' ? '深度互动会话' : '落地页浏览量'}
                              style={{ borderColor: fieldErrors.optimizationEvent ? 'var(--red)' : undefined, background: 'var(--surface2)' }}
                            />
                          ) : (
                            <MultiSelect
                              single
                              options={pixelEventOptions}
                              value={optimizationEvent ? [optimizationEvent] : []}
                              onChange={(vals) => {
                                setOptimizationEvent(vals[0] ?? '');
                                setFieldErrors((p) => ({ ...p, optimizationEvent: '' }));
                              }}
                              disabled={!canEdit('adset.optimization_event')}
                              style={{ borderColor: fieldErrors.optimizationEvent ? 'var(--red)' : undefined }}
                            />
                          )}
                          {fieldErrors.optimizationEvent && <div style={errStyle}>{fieldErrors.optimizationEvent}</div>}
                        </FormGroup>
                      <FormGroup>
                        <Label>归因窗口设置</Label>
                        <TikTokAttributionWindowPicker
                          clickValue={clickAttributionWindow}
                          viewValue={viewAttributionWindow}
                          onClickChange={setClickAttributionWindow}
                          onViewChange={setViewAttributionWindow}
                          disabled={!canEdit('adset.click_attribution_window') || !canEdit('adset.view_attribution_window')}
                        />
                      </FormGroup>
                        <FormGroup>
                          <Label>目标 CPA（USD / 转化）</Label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-input"
                            value={bidPrice}
                            disabled={!canEdit('adset.bid_price')}
                            onChange={(e) => {
                              setBidPrice(normalizeMoneyInput(e.target.value));
                              setFieldErrors((p) => ({ ...p, bidPrice: '' }));
                            }}
                            placeholder="可选，留空则自动出价"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>计费事件</Label>
                          <input className="form-input" value="OCPM" readOnly />
                        </FormGroup>
                      </>
                    )}
                  </>
                )}
              </SectionCard>
            </>
          )}

          {/* ===== 差异模块：视频播放 ===== */}
          {isVideoViewsObjective && (
            <SectionCard title="出价与优化">
              <FormGroup>
                <Label>优化目标</Label>
                <MultiSelect
                  single
                  options={VIDEO_VIEW_GOAL_OPTIONS}
                  value={optimizationGoal ? [optimizationGoal] : []}
                  onChange={(vals) => setOptimizationGoal(vals[0] ?? '')}
                  disabled={!canEdit('adset.optimization_goal')}
                />
                <Hint>
                  {VIDEO_VIEW_GOAL_OPTIONS.find((option) => option.value === optimizationGoal)?.hint
                    ?? VIDEO_VIEW_GOAL_OPTIONS[0].hint}
                </Hint>
              </FormGroup>
              <FormGroup>
                <Label>频次上限</Label>
                <div style={{ display: 'grid', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="tt-video-frequency-mode"
                      checked={videoFrequencyMode === 'NONE'}
                      onChange={() => setVideoFrequencyMode('NONE')}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <span>继续但不设置自定义频次上限</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="tt-video-frequency-mode"
                      checked={videoFrequencyMode === 'CUSTOM'}
                      onChange={() => setVideoFrequencyMode('CUSTOM')}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <span>自定义频次上限</span>
                  </label>
                </div>
              </FormGroup>
              {videoFrequencyMode === 'CUSTOM' && (
                <FormGroup>
                  <Label>频次上限</Label>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      flexWrap: 'wrap',
                      padding: 16,
                      borderRadius: 10,
                      background: 'var(--surface2)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <input
                      ref={setFieldRef('frequency') as React.Ref<HTMLInputElement>}
                      type="number"
                      min="1"
                      className="form-input"
                      value={videoFrequencyCount}
                      onChange={(e) => {
                        setVideoFrequencyCount(e.target.value);
                        setFieldErrors((p) => ({ ...p, frequency: '' }));
                      }}
                      style={{ width: 120, borderColor: fieldErrors.frequency ? 'var(--red)' : undefined }}
                    />
                    <span>次展示</span>
                    <span>/</span>
                    <input
                      ref={setFieldRef('frequencySchedule') as React.Ref<HTMLInputElement>}
                      type="number"
                      min="1"
                      className="form-input"
                      value={videoFrequencyDays}
                      onChange={(e) => {
                        setVideoFrequencyDays(e.target.value);
                        setFieldErrors((p) => ({ ...p, frequencySchedule: '' }));
                      }}
                      style={{ width: 120, borderColor: fieldErrors.frequencySchedule ? 'var(--red)' : undefined }}
                    />
                    <span>天</span>
                  </div>
                  {fieldErrors.frequency && <div style={errStyle}>{fieldErrors.frequency}</div>}
                  {fieldErrors.frequencySchedule && <div style={errStyle}>{fieldErrors.frequencySchedule}</div>}
                </FormGroup>
              )}
              <FormGroup>
                <Label>出价控制（可选）</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <input
                    ref={setFieldRef('bidPrice') as React.Ref<HTMLInputElement>}
                    type="number"
                    min="0"
                    step="0.001"
                    className="form-input"
                    value={bidPrice}
                    disabled={!canEdit('adset.bid_price')}
                    onChange={(e) => {
                      setBidPrice(normalizeMoneyInput(e.target.value));
                      setFieldErrors((p) => ({ ...p, bidPrice: '' }));
                    }}
                    style={{ width: 220, borderColor: fieldErrors.bidPrice ? 'var(--red)' : undefined }}
                    placeholder="留空则自动出价"
                  />
                  <span>
                    USD/观看（{optimizationGoal === 'ENGAGED_VIEW_FIFTEEN' ? '专注观看 15 秒' : '专注观看 6 秒'}）
                  </span>
                </div>
                <Hint>若要使用“最大投放量”出价策略，请填写出价控制字段。</Hint>
                {fieldErrors.bidPrice && <div style={errStyle}>{fieldErrors.bidPrice}</div>}
              </FormGroup>
              <FormGroup>
                <Label>计费事件</Label>
                <input className="form-input" value="CPV" readOnly />
              </FormGroup>
              <FormGroup>
                <Label>投放速度</Label>
                <input className="form-input" value="标准" readOnly />
              </FormGroup>
            </SectionCard>
          )}

          {/* ===== 差异模块：线索收集 ===== */}
          {isLeadGenObjective && (
            <SectionCard title="优化与出价">
              <FormGroup>
                <Label>渠道</Label>
                <input className="form-input" value="落地页" readOnly />
              </FormGroup>
              <FormGroup>
                <Label>目标</Label>
                <Cards
                  name="tt-lead-goal"
                  value={optimizationGoal}
                  onChange={setOptimizationGoal}
                  disabled={!canEdit('adset.optimization_goal')}
                  options={[
                    { value: 'CONVERT', label: '转化' },
                    { value: 'CLICK', label: '点击' },
                  ]}
                />
              </FormGroup>

              {isLeadConvertGoal ? (
                <>
                  <FormGroup>
                    <Label required>Pixel（数据连接）</Label>
                      <MultiSelect
                        single
                        options={pixelList.map((px) => ({ value: px.pixel_id, label: `${px.pixel_name} (${px.pixel_id})` }))}
                              value={pixelID ? [pixelID] : []}
                              onChange={(vals) => {
                                applyPixelSelection(vals[0] ?? '');
                              }}
                              disabled={!canEdit('adset.pixel_id')}
                              placeholder="-- 请选择 Pixel --"
                        style={{ borderColor: fieldErrors.pixelID ? 'var(--red)' : undefined }}
                      />
                    {fieldErrors.pixelID && <div style={errStyle}>{fieldErrors.pixelID}</div>}
                  </FormGroup>
                  <FormGroup>
                    <Label required={requiresOptimizationEvent}>优化事件</Label>
                    <MultiSelect
                      single
                      options={pixelEventOptions}
                      value={optimizationEvent ? [optimizationEvent] : []}
                              onChange={(vals) => {
                                setOptimizationEvent(vals[0] ?? '');
                                setFieldErrors((p) => ({ ...p, optimizationEvent: '' }));
                              }}
                              disabled={!canEdit('adset.optimization_event')}
                              style={{ borderColor: fieldErrors.optimizationEvent ? 'var(--red)' : undefined }}
                    />
                    {fieldErrors.optimizationEvent && <div style={errStyle}>{fieldErrors.optimizationEvent}</div>}
                  </FormGroup>
                  <FormGroup>
                    <Label>归因设置</Label>
                    <TikTokOptionalAttributionSettings
                      enabled={leadAttributionEnabled}
                      onToggle={setLeadAttributionEnabled}
                      clickValue={clickAttributionWindow}
                      viewValue={viewAttributionWindow}
                      onClickChange={setClickAttributionWindow}
                      onViewChange={setViewAttributionWindow}
                      statisticType={statisticType}
                      onStatisticTypeChange={setStatisticType}
                      disabled={!canEdit('adset.click_attribution_window') || !canEdit('adset.view_attribution_window')}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>目标 CPA（USD / 转化）</Label>
                    <input
                      ref={setFieldRef('bidPrice') as React.Ref<HTMLInputElement>}
                      type="number"
                      min="0"
                      step="0.001"
                      className="form-input"
                      value={bidPrice}
                      disabled={!canEdit('adset.bid_price')}
                      onChange={(e) => {
                        setBidPrice(normalizeMoneyInput(e.target.value));
                        setFieldErrors((p) => ({ ...p, bidPrice: '' }));
                      }}
                      style={{ borderColor: fieldErrors.bidPrice ? 'var(--red)' : undefined }}
                    />
                    {fieldErrors.bidPrice && <div style={errStyle}>{fieldErrors.bidPrice}</div>}
                  </FormGroup>
                  <FormGroup>
                    <Label>计费事件</Label>
                    <input className="form-input" value="OCPM" readOnly />
                  </FormGroup>
                </>
              ) : (
                <>
                  <FormGroup>
                    <Label required>目标 CPA（USD / 点击）</Label>
                    <input
                      ref={setFieldRef('bidPrice') as React.Ref<HTMLInputElement>}
                      type="number"
                      min="0"
                      step="0.001"
                      className="form-input"
                      value={bidPrice}
                      disabled={!canEdit('adset.bid_price')}
                      onChange={(e) => {
                        setBidPrice(normalizeMoneyInput(e.target.value));
                        setFieldErrors((p) => ({ ...p, bidPrice: '' }));
                      }}
                      style={{ borderColor: fieldErrors.bidPrice ? 'var(--red)' : undefined }}
                    />
                    {fieldErrors.bidPrice && <div style={errStyle}>{fieldErrors.bidPrice}</div>}
                  </FormGroup>
                  <FormGroup>
                    <Label>计费事件</Label>
                    <input className="form-input" value="CPC" readOnly />
                  </FormGroup>
                </>
              )}
            </SectionCard>
          )}

          {/* ===== 差异模块：销量 ===== */}
          {isSalesObjective && (
            <SectionCard title="优化与出价">
              <FormGroup>
                <Label>目标</Label>
                <Cards
                  name="tt-sales-goal"
                  value={optimizationGoal}
                  onChange={setOptimizationGoal}
                  disabled={!canEdit('adset.optimization_goal')}
                  options={[
                    { value: 'CONVERT', label: '转化' },
                    { value: 'VALUE', label: '价值' },
                  ]}
                />
              </FormGroup>

              <FormGroup>
                <Label required>Pixel（数据连接）</Label>
                  <MultiSelect
                    single
                    options={pixelList.map((px) => ({ value: px.pixel_id, label: `${px.pixel_name} (${px.pixel_id})` }))}
                        value={pixelID ? [pixelID] : []}
                        onChange={(vals) => {
                          applyPixelSelection(vals[0] ?? '');
                        }}
                        disabled={!canEdit('adset.pixel_id')}
                        placeholder="-- 请选择 Pixel --"
                    style={{ borderColor: fieldErrors.pixelID ? 'var(--red)' : undefined }}
                  />
                {fieldErrors.pixelID && <div style={errStyle}>{fieldErrors.pixelID}</div>}
              </FormGroup>

              {isSalesConvertGoal ? (
                <>
                  <FormGroup>
                    <Label required={requiresOptimizationEvent}>优化事件</Label>
                    <MultiSelect
                      single
                      options={pixelEventOptions}
                      value={optimizationEvent ? [optimizationEvent] : []}
                      onChange={(vals) => {
                        setOptimizationEvent(vals[0] ?? '');
                        setFieldErrors((p) => ({ ...p, optimizationEvent: '' }));
                      }}
                      disabled={!canEdit('adset.optimization_event')}
                      style={{ borderColor: fieldErrors.optimizationEvent ? 'var(--red)' : undefined }}
                    />
                    {fieldErrors.optimizationEvent && <div style={errStyle}>{fieldErrors.optimizationEvent}</div>}
                  </FormGroup>
                  <FormGroup>
                    <Label>目标 CPA（USD / 转化）</Label>
                    <input
                      ref={setFieldRef('bidPrice') as React.Ref<HTMLInputElement>}
                      type="number"
                      min="0"
                      step="0.001"
                      className="form-input"
                      value={bidPrice}
                      disabled={!canEdit('adset.bid_price')}
                      onChange={(e) => {
                        setBidPrice(normalizeMoneyInput(e.target.value));
                        setFieldErrors((p) => ({ ...p, bidPrice: '' }));
                      }}
                      style={{ borderColor: fieldErrors.bidPrice ? 'var(--red)' : undefined }}
                    />
                    {fieldErrors.bidPrice && <div style={errStyle}>{fieldErrors.bidPrice}</div>}
                  </FormGroup>
                  <FormGroup>
                    <Label>归因窗口设置</Label>
                    <TikTokAttributionWindowPicker
                      clickValue={clickAttributionWindow}
                          viewValue={viewAttributionWindow}
                          onClickChange={setClickAttributionWindow}
                          onViewChange={setViewAttributionWindow}
                          disabled={!canEdit('adset.click_attribution_window') || !canEdit('adset.view_attribution_window')}
                        />
                  </FormGroup>
                  <FormGroup>
                    <Label>事件统计方式</Label>
                    <Cards
                      name="tt-sales-stat"
                      value={statisticType}
                      onChange={setStatisticType}
                      disabled={!canEdit('adset.click_attribution_window') || !canEdit('adset.view_attribution_window')}
                      options={[
                        { value: 'EVERYTIME', label: '每一次' },
                        { value: 'NONE', label: '仅一次' },
                      ]}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>计费事件</Label>
                    <input className="form-input" value="OCPM" readOnly />
                  </FormGroup>
                  <FormGroup>
                    <Label>投放速度</Label>
                    <input className="form-input" value="标准" readOnly />
                  </FormGroup>
                </>
              ) : (
                <>
                  <FormGroup>
                    <Label>目标 ROAS</Label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={targetRoas}
                      disabled={!canEdit('campaign.roas_bid')}
                      onChange={(e) => setTargetRoas(normalizeMoneyInput(e.target.value))}
                      placeholder="请输入目标 ROAS"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>归因窗口设置</Label>
                    <TikTokAttributionWindowPicker
                      clickValue={clickAttributionWindow}
                      viewValue={viewAttributionWindow}
                      onClickChange={setClickAttributionWindow}
                      onViewChange={setViewAttributionWindow}
                      disabled={!canEdit('adset.click_attribution_window') || !canEdit('adset.view_attribution_window')}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>计费事件</Label>
                    <input className="form-input" value="OCPM" readOnly />
                  </FormGroup>
                  <FormGroup>
                    <Label>投放速度</Label>
                    <input className="form-input" value="标准" readOnly />
                  </FormGroup>
                </>
              )}
            </SectionCard>
          )}

          {/* ── 动态广告标签（adset 级）──────────────────────────── */}
          {tagConfig.filter(t => t.level === 'adset').length > 0 && (
            <SectionCard title="广告标签">
              <div className="tag-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 10px' }}>
                {tagConfig.filter(t => t.level === 'adset').map(td => {
                  const flatOpts = td.options.flatMap(o =>
                    o.children && o.children.length > 0
                      ? o.children.map(c => ({ value: c.value, label: `${o.label} · ${c.label}` }))
                      : [{ value: o.value, label: o.label }]
                  );
                  return (
                    <div key={td.key}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{td.label}</div>
                      <MultiSelect single placeholder="请选择"
                        options={flatOpts}
                        value={adsetTags[td.key] ? [adsetTags[td.key]] : []}
                        onChange={(vals) => setAdsetTags(prev => ({ ...prev, [td.key]: vals[0] ?? '' }))}
                      />
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}

        </div>
      )}

        {step === 3 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              gap: 20,
              alignItems: 'start',
              width: '100%',
            }}
          >
            <div style={{ minWidth: 0, width: '100%' }}>
          <SectionCard title="广告基础">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <FormGroup>
                <Label required>广告名称</Label>
                <input
                  ref={setFieldRef('adName') as React.Ref<HTMLInputElement>}
                  className="form-input"
                  value={adName}
                  disabled={!canEdit('ad.name')}
                  onChange={(e) => {
                    setAdName(e.target.value);
                    setFieldErrors((p) => ({ ...p, adName: '' }));
                  }}
                  style={{ borderColor: fieldErrors.adName ? 'var(--red)' : undefined }}
                />
                {fieldErrors.adName && <div style={errStyle}>{fieldErrors.adName}</div>}
              </FormGroup>

              <FormGroup>
                <Label>广告状态</Label>
                <MultiSelect
                  single
                  options={[{ value: 'DISABLE', label: '暂停' }, { value: 'ENABLE', label: '启用' }]}
                  value={adStatus ? [adStatus] : []}
                  onChange={(vals) => setAdStatus(vals[0] ?? '')}
                  style={!canEdit('ad.ad_status') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                />
              </FormGroup>
            </div>

          </SectionCard>

          {visibleModules.includes('identity') && (
            <SectionCard title="身份">
              {loadingIdentities ? (
                <div style={{ color: 'var(--text3)', fontSize: 13 }}>正在加载已授权的 TikTok 身份...</div>
              ) : identityOptions.length === 0 ? (
                <div style={{ borderRadius: 10, background: 'rgba(20,184,166,.10)', border: '1px solid rgba(20,184,166,.18)', padding: '12px 14px', fontSize: 13, lineHeight: 1.6 }}>
                  当前账户下暂未拉到可选身份。提交时后端仍会尝试自动解析，但建议先在 TikTok 广告后台完成账号授权。
                </div>
              ) : (
                <FormGroup>
                  <Label required>授权身份</Label>
                  <div ref={setFieldRef('identity')}>
                    <MultiSelect
                      single
                      options={identityOptions.map((item) => {
                        const lbl = item.display_name || item.username || item.identity_id;
                        const suffix = [item.identity_type, item.identity_authorized_bc_id].filter(Boolean).join(' / ');
                        return { value: item.identity_id, label: suffix ? `${lbl} (${suffix})` : lbl };
                      })}
                      value={selectedIdentityID ? [selectedIdentityID] : []}
                      onChange={(vals) => {
                        setSelectedIdentityID(vals[0] ?? '');
                        setFieldErrors((p) => ({ ...p, identity: '' }));
                      }}
                      placeholder="— 请选择身份 —"
                      style={{ width: '100%', borderColor: fieldErrors.identity ? 'var(--red)' : undefined, ...((!canEdit('ad.identity_id')) ? { pointerEvents: 'none', opacity: 0.75 } : {}) }}
                    />
                  </div>
                  {fieldErrors.identity && <div style={errStyle}>{fieldErrors.identity}</div>}
                  <Hint>
                    Custom identity 已不再作为主路线，这里优先选择已授权的 TikTok 账号身份。
                    {selectedIdentity && (
                      <>
                        {' '}当前选择：
                        <strong>{selectedIdentity.display_name || selectedIdentity.username || selectedIdentity.identity_id}</strong>
                        {' '}({selectedIdentity.identity_type})
                      </>
                    )}
                  </Hint>
                </FormGroup>
              )}
            </SectionCard>
          )}

          <SectionCard title="选择视频">
            <FormGroup>
              <Label required>视频来源</Label>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="radio" name="tt-video-source" checked={videoSource === 'TIKTOK_POST'} onChange={() => setVideoSource('TIKTOK_POST')} style={{ accentColor: 'var(--primary)' }} />
                  TikTok 帖子
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="radio" name="tt-video-source" checked={videoSource === 'LIBRARY'} onChange={() => setVideoSource('LIBRARY')} style={{ accentColor: 'var(--primary)' }} />
                  素材库
                </label>
                {isSalesObjective && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                    <input
                      type="radio"
                      name="tt-video-source"
                      checked={videoSource === 'CATALOG_CREATIVE'}
                      onChange={() => setVideoSource('CATALOG_CREATIVE')}
                      disabled={!catalogEnabled}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    商品库创意素材
                  </label>
                )}
              </div>
              {isSalesObjective && !catalogEnabled && (
                <Hint>启用商品库推广后可使用“商品库创意素材”。</Hint>
              )}
            </FormGroup>

            {videoSource === 'TIKTOK_POST' && (
              <>
                <Hint style={{ marginBottom: 12 }}>使用弹窗选择主页帖子、已授权帖子，或通过创作者授权码添加 Spark 帖子。</Hint>

                <FormGroup>
                  <Label>帖子选择</Label>
                  <div
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 16,
                      background: 'var(--surface2)',
                      padding: 16,
                      display: 'grid',
                      gap: 14,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>TikTok 帖子</div>
                        <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                          可查看帖子封面、内容摘要、所属账号，并按帖子标题或帖子 ID 搜索。
                        </div>
                      </div>
                      <button type="button" className="btn btn-primary" onClick={() => { setSparkPostModalError(''); setShowSparkPostModal(true); }}>
                        添加帖子
                      </button>
                    </div>

                      {isSmartPlusMode && videoSource === 'TIKTOK_POST' && selectedSparkPosts.length > 0 ? (
                      <div
                        style={{
                          display: 'grid',
                          gap: 14,
                          padding: 12,
                          borderRadius: 12,
                          background: 'var(--surface)',
                          border: '1px solid rgba(20,184,166,.22)',
                        }}
                      >
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text1)' }}>
                          已选择 {selectedSparkPosts.length} 个帖子
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                          {selectedSparkPosts.map((item) => (
                            <div key={item.item_id} style={{ display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr) auto', gap: 10, padding: 10, borderRadius: 10, background: 'var(--surface2)', alignItems: 'start' }}>
                              <div style={{ borderRadius: 8, overflow: 'hidden', background: '#E8EAF0', aspectRatio: '9 / 16' }}>
                                {item.cover_url ? (
                                  <img src={item.cover_url} alt={item.caption || item.item_id} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                ) : null}
                              </div>
                              <div style={{ minWidth: 0, display: 'grid', gap: 4 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{item.caption || `帖子 ${item.item_id}`}</div>
                                <div style={{ fontSize: 12, color: 'var(--text3)' }}>帖子 ID：{item.item_id}</div>
                              </div>
                              <button type="button" className="btn btn-outline" style={{ padding: '6px 10px' }} onClick={() => removeTikTokItemID(item.item_id)}>
                                删除
                              </button>
                            </div>
                          ))}
                        </div>
                        <div style={{ minWidth: 0, display: 'grid', gap: 8 }}>
                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <button type="button" className="btn btn-outline" onClick={() => { setSparkPostModalError(''); setShowSparkPostModal(true); }}>
                              继续添加
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={() => {
                                setTikTokItemID('');
                                setAppliedSparkAuthCode('');
                                setFieldErrors((p) => ({ ...p, tikTokItemID: '' }));
                              }}
                            >
                              清除
                            </button>
                          </div>
                        </div>
                      </div>
                      ) : tikTokItemID ? (
                      <div
                        style={{
                          padding: '10px 12px',
                          borderRadius: 10,
                          background: 'rgba(20,184,166,.05)',
                          border: '1px solid rgba(20,184,166,.16)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 12,
                          flexWrap: 'wrap',
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>帖子 ID：{tikTokItemID}</div>
                          <div style={{ fontSize: 12, color: 'var(--text3)' }}>帖子详情暂未加载，可重新打开弹窗查看。</div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-outline" onClick={() => { setSparkPostModalError(''); setShowSparkPostModal(true); }}>
                            查看或更换
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => {
                              setTikTokItemID('');
                              setAppliedSparkAuthCode('');
                              setFieldErrors((p) => ({ ...p, tikTokItemID: '' }));
                            }}
                          >
                            清除
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label required>TikTok 内容 ID</Label>
                  <input
                    ref={setFieldRef('tikTokItemID') as React.Ref<HTMLInputElement>}
                    className="form-input"
                    value={tikTokItemID}
                    onChange={(e) => {
                      setTikTokItemID(e.target.value);
                      setFieldErrors((p) => ({ ...p, tikTokItemID: '' }));
                    }}
                    style={{ borderColor: fieldErrors.tikTokItemID ? 'var(--red)' : undefined }}
                    placeholder="从列表选择或通过授权码解析后自动填入"
                  />
                  <Hint>支持多个内容 ID，使用逗号、空格或换行分隔。</Hint>
                  {fieldErrors.tikTokItemID && <div style={errStyle}>{fieldErrors.tikTokItemID}</div>}
                </FormGroup>
              </>
            )}

            {videoSource === 'LIBRARY' && (
              <>
                <FormGroup>
                  <Label>上传视频</Label>
                  <UploadZone
                    onFile={handleSelectVideoFile}
                    accept="video/*"
                    hint="支持多次上传并累加多个视频 ID，提交时会一并回写和保存。"
                    fileName={selectedLibraryCreative?.caption || videoFile?.name}
                    uploading={videoUploading}
                    uploadPct={videoUploadPct}
                    previewURL={selectedLibraryCreative?.preview_url}
                    actionSlot={selectedVideoIDs.length > 0 ? (
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => {
                          setVideoID('');
                          setVideoFile(null);
                          setFieldErrors((p) => ({ ...p, videoID: '' }));
                        }}
                      >
                        清除
                      </button>
                    ) : undefined}
                  />
                </FormGroup>

                <FormGroup>
                  <Label required>视频 ID</Label>
                  <input
                    ref={setFieldRef('videoID') as React.Ref<HTMLInputElement>}
                    className="form-input"
                    value={videoID}
                    placeholder="上传后自动填入，也可手动填写"
                    onChange={(e) => {
                      setVideoID(e.target.value);
                      setFieldErrors((p) => ({ ...p, videoID: '' }));
                    }}
                    style={{ borderColor: fieldErrors.videoID ? 'var(--red)' : undefined }}
                  />
                  <Hint>支持多个视频 ID，使用逗号、空格或换行分隔。</Hint>
                  {fieldErrors.videoID && <div style={errStyle}>{fieldErrors.videoID}</div>}
                </FormGroup>
              </>
            )}

            {videoSource === 'CATALOG_CREATIVE' && (
              <>
                <Hint style={{ marginBottom: 12 }}>商品库创意素材只需要勾选视频和图片，不再展示全部素材内容。</Hint>

                <FormGroup>
                  <Label>商品库创意素材</Label>
                  <div
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 16,
                      background: 'var(--surface2)',
                      padding: 16,
                      display: 'grid',
                      gap: 14,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>商品库创意素材类型</div>
                      <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                        基于当前已选商品库和商品范围，勾选需要启用的素材类型。
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={catalogCreativeAssetTypes.includes('VIDEO')}
                          onChange={() => {
                            setCatalogCreativeAssetTypes((current) =>
                              current.includes('VIDEO') ? current.filter((item) => item !== 'VIDEO') : [...current, 'VIDEO'],
                            );
                            setFieldErrors((p) => ({ ...p, catalogCreativeAssetTypes: '' }));
                          }}
                          style={{ accentColor: 'var(--primary)' }}
                        />
                        视频
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={catalogCreativeAssetTypes.includes('IMAGE')}
                          onChange={() => {
                            setCatalogCreativeAssetTypes((current) =>
                              current.includes('IMAGE') ? current.filter((item) => item !== 'IMAGE') : [...current, 'IMAGE'],
                            );
                            setFieldErrors((p) => ({ ...p, catalogCreativeAssetTypes: '' }));
                          }}
                          style={{ accentColor: 'var(--primary)' }}
                        />
                        图片
                      </label>
                    </div>

                    <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                      当前商品库创意会结合已选商品库、商品范围，以及这里勾选的素材类型生成。
                    </div>
                  </div>
                  {fieldErrors.catalogCreativeAssetTypes && <div style={errStyle}>{fieldErrors.catalogCreativeAssetTypes}</div>}
                </FormGroup>

                <FormGroup>
                  <Label>背景音乐 <span style={{ color: 'var(--red)' }}>*</span></Label>
                  <div
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 16,
                      background: 'var(--surface2)',
                      padding: 16,
                      display: 'grid',
                      gap: 12,
                    }}
                  >
                    <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                      商品库轮播广告需要提供背景音乐。可以上传音乐文件或直接输入 music_id。
                    </div>

                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 16px',
                          borderRadius: 8,
                          background: 'var(--primary)',
                          color: '#fff',
                          cursor: musicUploading ? 'not-allowed' : 'pointer',
                          fontSize: 14,
                          opacity: musicUploading ? 0.6 : 1,
                        }}
                      >
                        <input
                          type="file"
                          accept=".mp3,.wav,.aac,.ogg,.flac"
                          style={{ display: 'none' }}
                          disabled={musicUploading}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleSelectMusicFile(f);
                            e.target.value = '';
                          }}
                        />
                        {musicUploading ? '上传中...' : '上传音乐文件'}
                      </label>
                      {musicUploading && (
                        <div style={{ flex: 1, minWidth: 120 }}>
                          <div
                            style={{
                              height: 6,
                              borderRadius: 3,
                              background: 'var(--border)',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${musicUploadPct}%`,
                                background: 'var(--primary)',
                                borderRadius: 3,
                                transition: 'width 0.3s',
                              }}
                            />
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{musicUploadPct}%</div>
                        </div>
                      )}
                    </div>

                    <input
                      type="text"
                      className="form-input"
                      placeholder="music_id（上传后自动填入，也可手动输入）"
                      value={musicID}
                      onChange={(e) => {
                        setMusicID(e.target.value);
                        setFieldErrors((p) => ({ ...p, musicID: '' }));
                      }}
                      style={{ borderColor: fieldErrors.musicID ? 'var(--red)' : undefined }}
                    />
                    {fieldErrors.musicID && <div style={errStyle}>{fieldErrors.musicID}</div>}
                  </div>
                </FormGroup>
              </>
            )}
            </SectionCard>

            {isSalesObjective && catalogEnabled && catalogID && (
              <SectionCard title="添加商品">
                <FormGroup>
                  <Label>商品范围</Label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                      <input
                        type="radio"
                        name="tt-product-selection"
                        checked={productSelectionMode === 'ALL_PRODUCTS'}
                        onChange={() => {
                          setProductSelectionMode('ALL_PRODUCTS');
                          setCatalogSetID('');
                          setFieldErrors((p) => ({ ...p, catalogSetID: '' }));
                        }}
                        style={{ accentColor: 'var(--primary)' }}
                      />
                      所有商品
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                      <input
                        type="radio"
                        name="tt-product-selection"
                        checked={productSelectionMode === 'PRODUCT_SET'}
                        onChange={() => setProductSelectionMode('PRODUCT_SET')}
                        style={{ accentColor: 'var(--primary)' }}
                      />
                      商品系列
                    </label>
                  </div>
                  <Hint>启用商品库推广后，可在广告层选择投放全部商品或指定商品系列。</Hint>
                </FormGroup>

                {productSelectionMode === 'PRODUCT_SET' && (
                  <FormGroup>
                    <Label required>商品系列</Label>
                    {catalogSetLoading ? (
                      <div className="form-input" style={{ color: 'var(--text3)', background: 'var(--surface2)' }}>
                        正在获取商品系列...
                      </div>
                    ) : catalogSetList.length > 0 ? (
                      <MultiSelect
                        single
                        options={catalogSetList.map((item) => ({
                          value: item.product_set_id || item.store_id,
                          label: `${item.product_set_name || item.store_name || item.product_set_id || item.store_id}${item.product_num ? ` (${item.product_num})` : ''}`,
                        }))}
                        value={catalogSetID ? [catalogSetID] : []}
                        onChange={(vals) => {
                          setCatalogSetID(vals[0] ?? '');
                          setFieldErrors((p) => ({ ...p, catalogSetID: '' }));
                        }}
                        placeholder="-- 请选择商品系列 --"
                        style={{ borderColor: fieldErrors.catalogSetID ? 'var(--red)' : undefined }}
                      />
                    ) : (
                      <div
                        ref={setFieldRef('catalogSetID') as React.Ref<HTMLDivElement>}
                        className="form-input"
                        style={{ color: fieldErrors.catalogSetID ? 'var(--red)' : 'var(--text3)', background: 'var(--surface2)' }}
                      >
                        {catalogSetError || '当前商品库下暂无可用商品系列。'}
                      </div>
                    )}
                    {fieldErrors.catalogSetID && <div style={errStyle}>{fieldErrors.catalogSetID}</div>}
                    {!catalogSetLoading && catalogSetError && !fieldErrors.catalogSetID && <div style={errStyle}>{catalogSetError}</div>}
                  </FormGroup>
                )}
              </SectionCard>
            )}


            <SectionCard title="文案与落地页">
            {!isTikTokPostCreative && (
            <FormGroup>
              <Label required>广告文案</Label>
              {isSalesObjective ? (
                <div style={{ display: 'grid', gap: 10 }}>
                  {salesAdTexts.map((item, index) => (
                    <div key={`sales-ad-text-${index}`} style={{ display: 'grid', gap: 6 }}>
                      <textarea
                        ref={index === 0 ? (setFieldRef('adText') as React.Ref<HTMLTextAreaElement>) : undefined}
                        className="form-input"
                        value={item}
                        onChange={(e) => {
                          const next = [...salesAdTexts];
                          next[index] = e.target.value;
                          setSalesAdTexts(next);
                          setFieldErrors((p) => ({ ...p, adText: '' }));
                        }}
                        style={{ minHeight: 88, resize: 'vertical', borderColor: fieldErrors.adText ? 'var(--red)' : undefined }}
                        placeholder="请输入广告输入文字"
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{item.length}/100</div>
                        {salesAdTexts.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => {
                              setSalesAdTexts((current) => current.filter((_, currentIndex) => currentIndex !== index));
                            }}
                          >
                            删除
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setSalesAdTexts((current) => [...current, ''])}
                    style={{ justifySelf: 'start' }}
                  >
                    + 添加文案
                  </button>
                </div>
              ) : (
                <>
                  <textarea
                    ref={setFieldRef('adText') as React.Ref<HTMLTextAreaElement>}
                    className="form-input"
                    value={adText}
                    onChange={(e) => {
                      setAdText(e.target.value);
                      setFieldErrors((p) => ({ ...p, adText: '' }));
                    }}
                    style={{ minHeight: 88, resize: 'vertical', borderColor: fieldErrors.adText ? 'var(--red)' : undefined }}
                  />
                  <Hint>当前字数：{adText.length}</Hint>
                </>
              )}
              {fieldErrors.adText && <div style={errStyle}>{fieldErrors.adText}</div>}
            </FormGroup>
            )}
            {isTikTokPostCreative && (
              <Hint>使用 TikTok 红人帖子 / Spark 帖子时，默认复用帖子本身文案，无需额外填写广告文案。</Hint>
            )}

              <FormGroup>
                <Label>行动号召</Label>
                <div style={{ display: 'grid', gap: 10 }}>
                  {CTA_MODES.map((mode) => {
                    const active = ctaMode === mode.value;
                    return (
                      <label
                        key={mode.value}
                        style={{
                          display: 'grid',
                          gap: 4,
                          padding: '10px 12px',
                          borderRadius: 12,
                          border: active ? '1px solid rgba(20,184,166,.45)' : '1px solid var(--border)',
                          background: active ? 'rgba(20,184,166,.08)' : 'var(--surface)',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 600 }}>
                          <input
                            type="radio"
                            name="tt-cta-mode"
                            checked={active}
                            onChange={() => setCtaMode(mode.value)}
                            style={{ accentColor: 'var(--primary)' }}
                          />
                          {mode.label}
                        </div>
                        {mode.hint && (
                          <div style={{ paddingLeft: 26, fontSize: 12, lineHeight: 1.6, color: 'var(--text3)' }}>
                            {mode.hint}
                          </div>
                        )}
                      </label>
                    );
                  })}
                  {ctaMode === 'STANDARD' ? (
                    <MultiSelect
                      single
                      options={CTAS}
                      value={callToAction ? [callToAction] : []}
                      onChange={(vals) => setCallToAction(vals[0] ?? '')}
                    />
                  ) : (
                    <Hint>动态优选模式下，将由 TikTok 自动选择更合适的行动号召。</Hint>
                  )}
                </div>
              </FormGroup>

            {needsLandingPageURL && (
              <FormGroup>
                <Label required={isTrafficObjective || isLeadGenObjective || landingPageRequired}>目标页面 URL</Label>
                <input
                  ref={setFieldRef('landingPageURL') as React.Ref<HTMLInputElement>}
                  type="url"
                  className="form-input"
                  value={landingPageURL}
                  disabled={!canEdit('ad.landing_page_url')}
                  onChange={(e) => {
                    setLandingPageURL(e.target.value);
                    setFieldErrors((p) => ({ ...p, landingPageURL: '' }));
                  }}
                  style={{ borderColor: fieldErrors.landingPageURL ? 'var(--red)' : undefined }}
                  placeholder={`https://example.com/landing${(isTrafficObjective || isLeadGenObjective || landingPageRequired) ? '' : '（可选）'}`}
                />
                {fieldErrors.landingPageURL && <div style={errStyle}>{fieldErrors.landingPageURL}</div>}
              </FormGroup>
            )}
          </SectionCard>

          {/* ── 动态广告标签（ad 级）─────────────────────────────── */}
          {tagConfig.filter(t => t.level === 'ad').length > 0 && (
            <SectionCard title="广告标签">
              <div className="tag-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 10px' }}>
                {tagConfig.filter(t => t.level === 'ad').map(td => {
                  const flatOpts = td.options.flatMap(o =>
                    o.children && o.children.length > 0
                      ? o.children.map(c => ({ value: c.value, label: `${o.label} · ${c.label}` }))
                      : [{ value: o.value, label: o.label }]
                  );
                  return (
                    <div key={td.key}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{td.label}</div>
                      <MultiSelect single placeholder="请选择"
                        options={flatOpts}
                        value={adTags[td.key] ? [adTags[td.key]] : []}
                        onChange={(vals) => setAdTags(prev => ({ ...prev, [td.key]: vals[0] ?? '' }))}
                      />
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}

          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginTop: 24,
          paddingTop: 20,
          borderTop: '1px solid var(--border)',
        }}
      >
        <button
          className="btn btn-ghost"
          style={{ visibility: step > (createUnder ? createUnderMinStep : 1) && !(isEditMode && step <= 1) ? 'visible' : 'hidden' }}
          onClick={handleBack}
          disabled={submitting || validating}
        >
          ← 上一步
        </button>
        {draftFeatureEnabled && isEditMode && hasTempDraft ? (
          <div style={{ display: 'grid', justifyItems: 'center', gap: 4, flex: '1 1 240px', minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)' }}>已启用自动保存内容</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>最近保存时间：{formatDraftTimestamp(lastDraftSavedAt)}</div>
          </div>
        ) : <div style={{ flex: '1 1 240px' }} />}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={() => { void handleCloseAction(); }} disabled={submitting || validating}>关闭</button>
          {draftFeatureEnabled && isEditMode && (
            <button
              className="btn btn-outline"
              onClick={() => { void handleDiscardDraft(); }}
              disabled={validating || submitting}
            >
              放弃修改
            </button>
          )}
          {step === editMaxStep && !isEditMode && !createUnder && (
            <button
              className="btn btn-outline"
              onClick={handleValidate}
              disabled={validating || submitting}
              style={{ color: 'var(--primary)', borderColor: 'rgba(18,16,184,.35)' }}
            >
              {validating ? '校验中...' : '校验'}
            </button>
          )}

          {step < editMaxStep ? (
            <button className="btn btn-primary" onClick={handleNext} disabled={submitting || validating}>
              下一步
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || validating}>
              {submitting
                ? (isEditMode ? '保存中...' : '创建中...')
                : (isEditMode ? '保存修改' : createUnder ? (createUnder.entity === 'campaign' ? '创建广告组' : '创建广告') : '创建 TikTok 广告')}
            </button>
          )}
        </div>
      </div>

        <TikTokSparkPostPickerModal
          open={showSparkPostModal}
          posts={mergedCreativeList}
          sourceMode={videoSource}
          multiSelect={isSmartPlusMode}
          identities={identityOptions}
          selectedIdentityID={selectedIdentityID}
          loadingPosts={sparkPostLoading}
          loadingLibrary={libraryCreativeLoading}
          loadingMorePosts={sparkPostLoadingMore}
          hasMorePosts={sparkPostHasMore}
          selectedItemIDs={videoSource === 'LIBRARY' ? selectedVideoIDs : selectedTikTokItemIDs}
          selectedIdentityLabel={selectedIdentity?.display_name || selectedIdentity?.username || selectedIdentity?.identity_id || ''}
        search={sparkPostKeyword}
        errorMessage={sparkPostModalError}
        authCode={sparkAuthCode}
        onSearchChange={setSparkPostKeyword}
        onAuthCodeChange={setSparkAuthCode}
        onClose={() => { setSparkPostModalError(''); setShowSparkPostModal(false); }}
          onApplySelection={(items) => {
            const appliedItems = isSmartPlusMode ? items : items.slice(-1);
            if (appliedItems.some((item) => item.source === 'creative_library')) {
              setVideoSource('LIBRARY');
              setTikTokPostAdFormat('SINGLE_VIDEO');
              setVideoID(Array.from(new Set(appliedItems.map((item) => item.video_id || item.item_id).filter(Boolean))).join(','));
              setFieldErrors((p) => ({ ...p, videoID: '', tikTokItemID: '' }));
            } else {
              setVideoSource('TIKTOK_POST');
              setTikTokPostAdFormat('SINGLE_VIDEO');
              setTikTokItemID(Array.from(new Set(appliedItems.map((item) => item.item_id).filter(Boolean))).join(','));
              setFieldErrors((p) => ({ ...p, tikTokItemID: '' }));
            }
            setShowSparkPostModal(false);
          }}
        onIdentityChange={(identityID) => setSelectedIdentityID(identityID)}
        onLoadMore={() => {
          if (!sparkPostHasMore || !sparkPostCursor || sparkPostLoading || sparkPostLoadingMore) return;
          setSparkPostLoadingMore(true);
          void loadTikTokPosts(sparkPostCursor).finally(() => setSparkPostLoadingMore(false));
        }}
        onApplyAuthCode={() => {
          void handleSparkAuthCode().then((ok) => {
            if (ok) setShowSparkPostModal(false);
          });
        }}
      />

    </div>
  );
}

export default CreateTikTokAdWizard;
